/**
 * textos.ts — el diccionario del producto.
 *
 * Se PORTÓ una vez desde design/mockups/app.js, que es la Fase 0 aprobada.
 * De acá en adelante lo dueño es este archivo: los mockups fueron el origen,
 * no la fuente. Un generador que nadie vuelve a correr solo sirve para que
 * alguien pierda una tarde averiguando por qué su clave desapareció.
 *
 * El producto es bilingüe de raíz, no traducido después: cada cadena que ve una
 * persona vive acá y en ninguna otra parte.
 *
 * ── La parte que no es decorativa ─────────────────────────────────────────
 *
 * `EN` está declarado como `Record<Clave, string>`. No es adorno de tipos: si
 * alguien agrega una clave en español y olvida el inglés, **el proyecto no
 * compila**.
 *
 * Sin eso el fallo sería invisible. Un `t()` que cae al español cuando falta
 * la traducción deja una frase en español dentro de una pantalla en inglés, y
 * nadie que pruebe en español la va a ver nunca. Es la misma familia de fallo
 * silencioso que el latido: sale bien y está mal.
 *
 * Los énfasis van entre *asteriscos*, no en HTML: así ningún texto necesita
 * `dangerouslySetInnerHTML`, que es una puerta que no conviene abrir en un
 * producto que muestra datos de varios inquilinos.
 */

export const ES = {
  'app.tagline': "Cuadra. Declara. Crece.",
  // ▶ Decía «Dashboard» en los DOS idiomas, y era la única entrada del menú
  // sin traducir en un producto que se revisó dos veces palabra por palabra.
  // No se le escapó a la revisión bilingüe por descuido: se le escapó porque
  // una palabra inglesa que también se usa en español pasa las dos lecturas.
  // El código la llamó siempre `tablero`, y la pantalla se titula así.
  'nav.dashboard': "Tablero",
  'nav.entries': "Asientos",
  'nav.invoices': "Facturas",
  'nav.reports': "Reportes",
  'nav.integrations': "Integraciones",
  'nav.users': "Usuarios",
  'nav.settings': "Configuración",
  'nav.group.accounting': "Contabilidad",
  'nav.group.system': "Sistema",
  // ▶ La última superviviente de `wip.*`. Las demás se fueron con
  // `EnConstruccion` el 11 de setiembre, cuando `/configuracion` dejó el menú
  // sin ninguna entrada por construir. Ésta la usa `/integraciones` para una
  // integración que está aprobada y no tiene etapa asignada.
  'wip.noDate': "Fuera del MVP · sin fecha",
  // ── La configuración de la empresa (0069) ──────────────────────────────
  'cfg.title': "Configuración",
  'cfg.sub': "Lo que esta empresa puede cambiar de sí misma. El catálogo, los periodos y la gente tienen sus propias pantallas.",
  'cfg.readOnly': "Se necesita ser administrador de la empresa para cambiar algo de acá. Lo puede ver todo.",
  'cfg.saved': "Guardado.",
  'cfg.index': "Secciones de esta página",
  'cfg.company': "La empresa",
  'cfg.policy': "Política contable",
  'cfg.targets': "Metas de los índices",
  'cfg.targetsWhy': "La FÓRMULA de cada índice es del modelo y es la misma para todos —qué cuentas suman arriba y abajo—; contra qué se compara el resultado es decisión de quien dirige esta empresa. ▶ Vacía significa que no se ha fijado, y se puede guardar así: «todavía no» es una decisión. Mientras nadie las mire, el tablero las enseña marcadas como metas de la casa.",
  'cfg.targetModel': "modelo: {meta}",
  'cfg.targetNone': "sin fijar",
  'cfg.targetSave': "Guardar las metas",
  'cfg.targetSaved': "Guardadas",
  'cfg.targetUnconfirmed': "{cuantas} meta(s) siguen siendo las del modelo y nadie las ha confirmado.",
  'cfg.taxRate': "Tarifa del impuesto sobre la renta (%)",
  'cfg.taxRateNone': "sin declarar",
  'cfg.taxRateWhy': "El cierre la usa para provisionar la renta sobre la utilidad antes de impuesto de los libros. El producto no trae ninguna: depende del tramo de ingresos brutos de cada empresa, y una inventada produce un número con cara de oficial. Dejarla vacía es decir que nadie la declaró — no es lo mismo que cero por ciento, que es una tarifa de verdad. ▶ Y esta provisión NO es el impuesto de la declaración: la renta imponible lleva diferencias que este producto no conoce.",
  'cfg.matrix': "Matriz de deterioro de cartera (%)",
  'cfg.matrixUnconfirmed': "Estos porcentajes son todavía la propuesta del producto y nadie los ha confirmado. El cierre no da por cumplido el paso del deterioro hasta que se guarden.",
  'cfg.matrixWhy': "NIIF 9 admite estimar la pérdida esperada con una matriz por antigüedad, y los porcentajes los estima cada entidad con su propia historia de cobros. Los tramos son los mismos de la antigüedad de cartera. ▶ «Sin vencimiento» arranca en cero a propósito: un documento sin fecha no está vencido — lo que le falta es la fecha.",
  'cfg.matrixSave': "Guardar la matriz",
  'cfg.matrixSaved': "Guardada",
  'cfg.deferred': "Diferencias temporarias · NIC 12",
  'cfg.deferredWhy': "El valor CONTABLE de cada partida está en el libro; la base FISCAL no la conoce nadie más que quien hace la declaración —depende de la depreciación admitida, de qué gastos son deducibles y de criterios que cambian con la ley—. ▶ Así que se declara. Sin diferencias declaradas no hay impuesto diferido, y el paso 11 del cierre dice «no aplica» en vez de bloquear.",
  'cfg.deferredSign': "El signo sale de la NATURALEZA de la partida y no de cuál base es mayor: en un ACTIVO el contable mayor produce un PASIVO diferido, y en un PASIVO produce un ACTIVO. Las dos de en medio son las que se voltean solas.",
  'cfg.deferredConcept': "Concepto",
  'cfg.deferredKind': "La partida es",
  'cfg.deferredAsset': "un activo",
  'cfg.deferredLiability': "un pasivo",
  'cfg.deferredBook': "Base contable",
  'cfg.deferredTax': "Base fiscal",
  'cfg.deferredAdd': "Declarar la diferencia",
  'cfg.deferredDrop': "Quitar",
  'cfg.deferredEffect': "Efecto",
  'cfg.deferredNoRate': "Falta la tasa de renta de arriba: sin ella se ven las diferencias y no su efecto.",
  'cfg.deferredNone': "Ninguna declarada para {anio}.",
  'cfg.deferredTarget': "Objetivo del año",
  'cfg.deferredLiabilityNet': "pasivo diferido",
  'cfg.deferredAssetNet': "activo diferido",
  'cfg.tramo.corriente': "Al día",
  'cfg.tramo.tramo_1_30': "1 a 30 días",
  'cfg.tramo.tramo_31_60': "31 a 60 días",
  'cfg.tramo.tramo_61_90': "61 a 90 días",
  'cfg.tramo.tramo_mas_90': "Más de 90 días",
  'cfg.tramo.sin_vencimiento': "Sin vencimiento",
  'cfg.name': "Razón social",
  'cfg.nameEmpty': "La razón social encabeza los estados financieros: no puede quedar vacía.",
  'cfg.code': "Código",
  'cfg.codeWhy': "El código nombra el esquema donde viven estos libros, así que no se puede cambiar nunca — ni desde acá ni desde ningún otro sitio.",
  'cfg.taxId': "Identificación",
  'cfg.taxIdProvisional': "provisional",
  'cfg.taxIdType': "Tipo de identificación",
  'cfg.taxIdCanonical': "Forma canónica",
  'cfg.taxIdNone': "sin forma canónica",
  'cfg.taxIdForeign': "Una identificación extranjera no tiene forma canónica: no se puede comparar con otra sin saber de qué país es. Eso no la hace menos real.",
  'cfg.taxIdUnmatched': "Ese número no cuadra con el tipo elegido, así que no se pudo canonizar y la empresa queda marcada como provisional. Revise el tipo o el número.",
  'cfg.taxIdWhy': "Se guarda como usted la escribe, y la forma canónica la calcula la base. Los ceros de relleno son parte de la forma: 1-234-567 y 1-0234-0567 son la misma cédula.",
  'cfg.taxIdSave': "Guardar identificación",
  'cfg.currency': "La moneda de los libros",
  'cfg.currencyCode': "Código ISO",
  'cfg.currencyOpen': "Todavía no hay ningún asiento, así que la moneda se puede cambiar. Después del primero ya no: elíjala ahora.",
  'cfg.currencyLocked': "No se puede cambiar: hay {asientos} asientos escritos en {moneda}. Los importes están *en* esa moneda sin llevarla escrita, así que cambiarla no los convertiría — los reinterpretaría, sin que nada fallara.",
  'cfg.currencyLockedOne': "No se puede cambiar: ya hay un asiento escrito en {moneda}. Los importes están *en* esa moneda sin llevarla escrita, así que cambiarla no los convertiría — los reinterpretaría, sin que nada fallara.",
  'cfg.currencyForm': "Son tres letras: CRC, USD, EUR.",
  'cfg.language': "El idioma",
  'cfg.languageDefault': "Idioma por omisión",
  'cfg.spanish': "Español",
  'cfg.english': "Inglés",
  'cfg.languageWhy': "Es con el que abre alguien que todavía no ha elegido. No cambia el suyo: el conmutador ES/EN de arriba guarda su preferencia en este navegador y manda sobre ésta.",
  'cfg.save': "Guardar",
  'cfg.saveNote': "Lo que cambie queda anotado en la bitácora, con el valor anterior.",
  'cfg.nothingChanged': "No hay nada que guardar.",
  'cfg.opening': "Los saldos de apertura",
  'cfg.openingLoaded': "Ya están cargados.",
  'cfg.openingNone': "Está declarado que esta empresa no tiene saldos anteriores: sus libros empiezan el {desde}.",
  'cfg.openingPending': "No se han cargado, y nadie ha declarado que no haya.",
  'cfg.openingGo': "Ir a Saldos de apertura",
  'cfg.elsewhere': "Lo que no se cambia acá",
  'cfg.elsewhereWhy': "Cada una de estas cosas tiene su pantalla, y repetir sus botones acá sería dos sitios para el mismo acto.",
  'cfg.elsewhereChart': "las cuentas, sus nombres y sus auxiliares",
  'cfg.elsewherePeriods': "abrir, cerrar y reabrir un mes",
  'cfg.elsewherePeople': "quién entra y con qué rol",
  // El rótulo de la barra para quien la oye, no la ve. Estaba puesto
  // `nav.dashboard`, o sea que un lector de pantalla anunciaba el menú entero
  // como «Tablero» — el nombre de UNA de sus entradas.
  'nav.main': "Navegación principal",
  // ── La frontera de error ───────────────────────────────────────────────
  // Un render que lanza desmonta el árbol entero. Sin esto la página queda EN
  // BLANCO: ni mensaje, ni código, ni nada que reintentar.
  'err.screen': "Esta pantalla falló y no se pudo dibujar.",
  'err.screenHelp': "El resto de Books sigue funcionando: elija otra pantalla en el menú. Si vuelve a pasar en la misma, es un defecto nuestro y nos sirve saberlo.",
  'err.detail': "Ver el detalle técnico",
  // ▶ Se llamaba `jr.loading` y decía «Trayendo el diario…», que es cierto en
  // UNA de las nueve pantallas que lo pintan: el catálogo, el cierre, la
  // consola, los índices, los reportes, el tablero, los usuarios y la
  // bitácora anunciaban que estaban trayendo el diario. Una clave se queda
  // con el nombre de la primera pantalla que la usó, y el texto se queda con
  // lo que esa pantalla hacía. Ahora no nombra ninguna.
  'common.loading': "Cargando…",
  'common.debit': "Debe",
  'common.credit': "Haber",
  'common.account': "Cuenta",
  'common.date': "Fecha",
  'common.description': "Descripción",
  'common.amount': "Monto",
  'common.currency': "Moneda",
  'common.rate': "Tipo de cambio",
  'common.total': "Total",
  'common.source': "Origen",
  'common.status': "Estado",
  'common.folio': "Folio",
  'common.reference': "Referencia",
  'common.export': "Exportar",
  'common.export.csv': "CSV",
  'common.export.excel': "Excel",
  'common.export.print': "Imprimir",
  'common.cancel': "Cancelar",
  'common.dismiss': "Cerrar",
  'common.save': "Guardar",
  'common.search': "Buscar",
  'common.manual': "Manual",
  'common.posted': "Contabilizado",
  'common.draft': "Borrador",
  'common.all': "Todos",
  'common.month': "Mes",
  'common.quarter': "Trimestre",
  'common.year': "Año",
  'common.asset': "Activo",
  'common.liability': "Pasivo",
  'common.equity': "Patrimonio",
  'common.income': "Ingreso",
  'common.expense': "Gasto",
  // ▶ La única de los seis grupos que no tenía rótulo: nadie la nombraba. Las
  // otras cinco ya existían —tres del balance, dos de la leyenda del tablero—
  // y se REUSAN en vez de escribir otras: significan lo mismo, la naturaleza
  // de la cuenta. Los seis son los que admite `tipo_conocido` (migración
  // 0002), así que la lista del filtro sale de la base y no de la pantalla.
  'common.mixed': "Mixta",
  'common.functional': "Moneda funcional",
  'common.original': "Moneda original",
  'common.equivalent': "Equivalente en colones",
  'common.admin': "Admin",
  'common.accountant': "Contador",
  'common.readonly': "Solo lectura",
  'common.unknownRole': "Rol desconocido",
  /* ── Los valores que la BASE enumera ──────────────────────────────────
     Las claves se llaman como el valor de la base a propósito: la pantalla
     escribe t(rol.<valor>) sin una tabla de conversión en medio. Una
     tabla sería un tercer sitio con la misma lista, y el día que la base
     acepte un valor nuevo, la pantalla enseñaría el que le quede más cerca.
     `pruebas/idioma.test.ts` lee las restricciones de las migraciones y exige
     que cada valor tenga su texto. */
  'rol.admin': "Admin",
  'rol.contador': "Contador",
  'rol.solo_lectura': "Solo lectura",
  'estado.activa': "Activa",
  'estado.suspendida': "Suspendida",
  'estado.cerrada': "Cerrada",
  // ▶ Los cinco de un comprobante electrónico. Comparten el prefijo `estado.`
  // con los de una empresa porque la clave sale del NOMBRE de la columna, y las
  // dos se llaman `estado`. No chocan —los valores son distintos— pero conviene
  // saberlo antes de agregar un tercer `estado` a alguna tabla.
  //
  // Cada uno dice el HECHO y no el mecanismo del proveedor: `facturaencr` tiene
  // siete estados y cinco son su implementación.
  'estado.por_emitir': "Sin enviar",
  'estado.esperando_a_hacienda': "Esperando a Hacienda",
  'estado.aceptado': "Aceptado",
  'estado.rechazado': "Rechazado",
  'estado.fallido': "Falló el envío",
  // El trámite del emisor. Son cuatro y no un botón de encendido: crear un
  // emisor pasa por el portal del proveedor, con una persona adentro.
  // ▶ Eran los cuatro pasos de un trámite en el portal de un proveedor que se
  // descartó el 10 de setiembre. Lo que queda es el hecho que decidían debajo:
  // quién asigna el consecutivo. Ver la `0076`.
  'numera.atv': "La numera Hacienda",
  'numera.books': "La numera Books",
  'numera.apagado': "Apagado",
  'origen.manual': "Manual",
  'origen.integracion': "Integración",
  'origen.cierre': "Cierre",
  'origen.apertura': "Apertura",
  'origen.depreciacion': "Depreciación",
  'origen.devengo': "Devengo",
  'origen.provision': "Provisión de renta",
  'origen.deterioro': "Deterioro de cartera",
  'origen.avance': "Avance de proyectos",
  'origen.revaluacion': "Revaluación",
  'origen.diferencial': "Diferencial realizado",
  'origen.diferido': "Impuesto diferido",
  'origen.banco': "Nota del banco",
  'origen.cierre_anual': "Cierre del ejercicio",
  'fuente.vigente_declarado': "Declarado vigente",
  'fuente.copiado_del_anterior': "Copiado del día anterior",
  'dash.sub': "Cierre en *{dias} días* · {asientos} asientos",
  'ver.hay': "Hay una versión nueva de Skoped Books.",
  'ver.actualizar': "Actualizar",
  'ver.despues': "Después",
  'ver.instalar': "Instalar",
  'ver.instalarPor': "Abrirla como aplicación, sin la barra del navegador.",
  'ver.puedeInstalar': "Books se puede instalar como aplicación — se abre en su propia ventana, sin la barra del navegador.",
  'ver.ahora': "Instalar ahora",
  'ver.luego': "Más tarde",
  'dash.period.open': "Periodo abierto: {periodo}",
  'dash.newEntry': "Nuevo asiento",
  'dash.kpi.income': "Ingresos del periodo",
  'dash.kpi.expense': "Gastos del periodo",
  'dash.kpi.profit': "Utilidad antes de impuesto",
  'dash.kpi.tax': "Impuesto estimado (D‑100)",
  'dash.kpi.taxNote': "Provisión al 30 %",
  'dash.kpi.taxChip': "Declara antes del 15 set",
  'dash.margin': "Margen",
  'dash.vsPrev': "vs. {mes}",
  'dash.chart.title': "Ingresos vs. gastos",
  'dash.equation': "Ecuación contable",
  'dash.equationSub': "Verificación en vivo del balance de comprobación.",
  'dash.difference': "Difererencia",
  'dash.balancedTitle': "Libros cuadrados.",
  'dash.balancedBody': "Activo = Pasivo + Patrimonio al cierre del periodo, con todo lo registrado hasta esa fecha.",
  'dash.fxTitle': "Exposición en dólares",
  'dash.fxSub': "Saldos en USD revaluados al tipo de cambio de cierre.",
  'dash.fxAccounts': "Cuentas en USD",
  'dash.fxGain': "Diferencial cambiario del mes",
  'dash.fxNote': "Un colón de variación en el tipo de cambio del {moneda} mueve la utilidad en {monto}.",
  'dash.integrations': "Integraciones activas",
  'dash.manage': "Administrar integraciones",
  'dash.inactive': "inactiva",
  'dash.entries': "Asientos recientes",
  'dash.searchPh': "Buscar descripción…",
  'dash.integration': "Integración",
  'dash.ofEntries': "de 128 asientos",
  'dash.viewJournal': "Ver libro diario completo →",
  'dash.viewIn': "Ver en",
  'form.title': "Registrar asiento contable",
  'form.sub': "Partida doble. Los libros se llevan en *colones*: un asiento en dólares se convierte y debe cuadrar en colones.",
  'form.post': "Contabilizar",
  'form.saveDraft': "Guardar borrador",
  'form.header': "Encabezado",
  'form.docType': "Tipo de comprobante",
  'doc.journal': "Diario",
  'doc.receipt': "Ingreso",
  'doc.payment': "Egreso",
  'doc.adjust': "Ajuste",
  'doc.closing': "Cierre",
  'form.descPh': "Ej.: Venta de curso · Programación Básica",
  'form.descHint': "Aparece en el libro diario y en el detalle del libro mayor.",
  'form.refPh': "FE‑00042 / recibo",
  'form.templates': "Plantillas rápidas",
  'form.tmpl.sale': "Venta de contado",
  'form.tmpl.expense': "Gasto por banco",
  'form.tmpl.payroll': "Planilla",
  'form.tmpl.credit': "Venta a crédito + IVA",
  'form.tmpl.usd': "Venta en dólares",
  'form.tmpl.fx': "Diferencial cambiario",
  'form.lines': "Líneas del asiento",
  'form.hint': "Enter para bajar · Ctrl+↵ contabiliza",
  'form.addLine': "Agregar línea",
  'form.lineDetail': "Detalle de línea",
  'form.optional': "Opcional",
  'form.rateLabel': "Tipo de cambio del día",
  'form.rateSource': "Referencia BCCR · venta",
  'form.rateHint': "Se aplica a las líneas en dólares, convertidas *una por una*. Lo declara el BCCR para esta fecha y no se teclea encima: registrar un tipo pactado distinto necesita su motivo, y eso llega en una etapa posterior.",
  'form.balanced': "Cuadra — Debe = Haber = {monto}",
  'form.unbalanced': "Descuadrado — diferencia ",
  'form.overDebit': " de más al debe",
  'form.overCredit': " de más al haber",
  'form.noAmounts': "Sin montos — capture al menos una línea al debe y una al haber",
  'form.postedAs': "Contabilizado como AS‑0129 · ",
  'form.effect': "Efecto en el balance",
  'form.effectEmpty': "Agregue líneas para ver el efecto por tipo de cuenta.",
  'form.trace': "Trazabilidad",
  'form.user': "Usuario",
  'form.period': "Periodo",
  'form.open': "Abierto",
  'form.traceNote': "Todo asiento contabilizado queda en event_logs y no se elimina: se reversa con un asiento de ajuste.",
  'form.goldenRule': "Regla de oro",
  'form.goldenBody': "Todo lo que entra por un lado sale por el otro. Si el asiento no cuadra, *Skoped Books no lo contabiliza* — lo guarda como borrador hasta que usted lo cierre.",
  'form.fxNote': "Las cifras en dólares son informativas. *El cuadre se valida en colones*, que es la moneda de los libros.",
  'rep.title': "Reportes financieros y fiscales",
  'rep.sub': "Todos los reportes se calculan del libro diario — da igual si el asiento entró a mano o por integración.",
  'rep.sendTax': "Enviar a Hacienda",
  'rep.tab.balance': "Balance General",
  'rep.tab.income': "Estado de Resultados",
  'rep.tab.equity': "Cambios en el patrimonio",
  'rep.equityTitle': "Estado de cambios en el patrimonio",
  'rep.equityMeta': "Del inicio del ejercicio a {periodo}",
  'rep.equityComponent': "Componente",
  'rep.equityOpening': "Saldo inicial",
  'rep.equityMovement': "Movimientos del periodo",
  'rep.equityClosing': "Saldo final",
  'rep.equityTotal': "Total patrimonio",
  'rep.equityEmpty': "Esta empresa todavía no tiene patrimonio registrado.",
  'rep.equityNote': "El saldo inicial es lo que cada componente traía al empezar el "
    + "ejercicio: la apertura no es un aporte del primer día. Los movimientos NO están "
    + "clasificados por naturaleza —aporte, dividendo, traslado a reserva—: eso exige "
    + "mirar la contrapartida de cada línea, y se dice en vez de adivinarse. El total "
    + "de la última columna es el patrimonio del balance.",
  'rep.tab.cash': "Flujos de efectivo",
  'rep.cashTitle': "Estado de flujos de efectivo",
  'rep.cashMeta': "Del inicio del ejercicio a {periodo} · método indirecto",
  'rep.cashConcept': "Concepto",
  'rep.cashAmount': "Importe",
  'rep.cashOperating': "Actividades de operación",
  'rep.cashInvesting': "Actividades de inversión",
  'rep.cashFinancing': "Actividades de financiación",
  'rep.cashFx': "Efecto de la variación del tipo de cambio",
  'rep.cashUnclassified': "Cuentas sin actividad declarada",
  'rep.cashOpTotal': "Flujo neto de operación",
  'rep.cashInvTotal': "Flujo neto de inversión",
  'rep.cashFinTotal': "Flujo neto de financiación",
  'rep.cashChange': "Variación neta del efectivo",
  'rep.cashTies': "El efectivo al cierre sale de las cuentas de efectivo, y la "
    + "variación sale de todas las demás. Los dos caminos dan lo mismo.",
  'rep.cashMismatch': "El efectivo al cierre no coincide con la variación: hay "
    + "movimientos que el estado no está leyendo.",
  'rep.cashUnclassifiedWarn': "Hay cuentas de balance sin actividad declarada. El "
    + "total sigue cuadrando — lo que está incompleto es el reparto entre las "
    + "tres actividades.",
  'rep.cashEmpty': "Este periodo no tiene movimientos de efectivo.",
  'rep.cashNote': "Método indirecto: se parte del resultado del periodo y se "
    + "ajusta por lo que no movió efectivo. El estado NO excluye las "
    + "transacciones que no mueven efectivo —reconocer un arrendamiento sale "
    + "como inversión y financiación por el mismo importe—: eso exige mirar la "
    + "contrapartida de cada asiento, y se dice en vez de adivinarse. Los "
    + "intereses y el impuesto de renta quedan en operación.",
  'rep.tab.trial': "Balance de Comprobación",
  'rep.tab.d100': "D‑100 · Hacienda CR",
  'rep.tab.iva': "IVA · mensual",
  'rep.ivaTitle': "Conciliación del IVA",
  'rep.ivaMeta': "Impuesto al valor agregado · {periodo} · Dirección General de Tributación",
  // ── La antigüedad (0067) ───────────────────────────────────────────────
  'rep.tab.aging': "Antigüedad",
  'rep.tab.balances': "Saldos de bancos",
  'rep.tab.health': "Salud",
  'rep.group.statements': "Estados financieros",
  'rep.group.review': "Revisión",
  'rep.group.fiscal': "Fiscales",
  'rep.group.management': "Gestión",
  'rep.report': "Informe",
  'rep.printAsOf': "Al cierre del {fecha}",
  'rep.printCurrency': "Cifras expresadas en {moneda}",
  'rep.printPreparedBy': "Preparado por",
  'rep.printReviewedBy': "Revisado por",
  'rep.printIssued': "Emitido el {fecha}",
  'rep.printTitle.balance': "Balance de situación",
  'rep.printTitle.income': "Estado de resultados",
  'rep.printTitle.equity': "Estado de cambios en el patrimonio",
  'rep.printTitle.cash': "Estado de flujos de efectivo",
  'rep.printTitle.trial': "Balance de comprobación",
  'rep.printTitle.d100': "Declaración D-101 (impuesto sobre la renta)",
  'rep.printTitle.iva': "Conciliación del IVA",
  'rep.printTitle.aging': "Antigüedad de saldos",
  'rep.printTitle.health': "Panel de salud financiera",
  'rep.health.title': "Panel de salud financiera",
  'rep.health.sub': "Ingresos y costos por línea, OpEx por departamento y EBITDA en una página.",
  'rep.health.meta': "Al cierre de {periodo}",
  'rep.health.month': "{periodo}",
  'rep.health.ytd': "YTD",
  'rep.health.pctNR': "% NR",
  'rep.health.grossRevenue': "Ingresos brutos",
  'rep.health.cogs': "Costo de ventas",
  'rep.health.netRevenue': "Ingreso neto (NR)",
  'rep.health.grossMargin': "Margen bruto",
  'rep.health.opex': "Gastos operativos",
  'rep.health.ebit': "Resultado operativo (EBIT)",
  'rep.health.depreciation': "+ Depreciación y amortización",
  'rep.health.ebitda': "EBITDA",
  'rep.health.ebitdaMargin': "Margen EBITDA",
  'rep.health.nonOperating': "Resultado no operativo",
  'rep.health.netIncome': "Resultado del período",
  'rep.health.ratios': "Ratios",
  'rep.health.ratioValue': "Valor",
  'rep.health.ratioTarget': "Meta",
  'rep.health.noData': "Sin movimientos de operación en el período. Al registrar ingresos y gastos el panel se llena solo.",
  'rep.health.undept': "Sin departamento",
  'rep.agingTitle': "Antigüedad de saldos",
  'rep.agingMeta': "Documentos abiertos · {periodo} · los días se cuentan hasta el cierre del periodo",
  'rep.agingSide': "De quién es la antigüedad",
  'rep.agingReceivable': "Por cobrar",
  'rep.agingPayable': "Por pagar",
  // ▶ Los dos avisos NO son decoración: son lo que hace legible el informe.
  'rep.agingFifo': "Lo que alguien dijo que se pagó se descuenta primero; el resto se aplica al documento más viejo. La columna «se adivina» dice cuánto de cada fila lo colocó esa regla porque nadie lo repartió: en cero, ese corte es un hecho. El criterio existe porque hay que elegir alguno, y de todos los posibles es el único con el que la suma de los tramos da exactamente el saldo de la cuenta.",
  'rep.agingGuessed': "Se adivina",
  'rep.agingStated': "lo dijo alguien",
  'rep.agingNoDueDate': "Los tramos cuentan días desde el *vencimiento* del documento. Lo que no ha vencido va en «Corriente». Lo que no tiene vencimiento —lo asentado a mano, los saldos de apertura, o un papel que no lo decía— va en su propia columna: derivarlo del plazo de crédito de la ficha haría que «31–60 vencido» significara dos cosas distintas, una leída del papel y otra calculada.",
  // ▶ Decía que el catálogo no pide proveedor. Desde la 0074 sí lo pide, así
  // que la clave pasó a ser el rótulo del HUECO: los saldos escritos antes de
  // esa migración —y los de la apertura— no lo traen.
  'rep.agingNoSupplier': "Sin proveedor",
  'rep.agingCustomer': "Cliente",
  'rep.agingSupplier': "Proveedor",
  'rep.agingCurrent': "Corriente",
  'rep.aging1': "1–30 días",
  'rep.agingNoDue': "Sin vencimiento",
  'rep.aging31': "31–60",
  'rep.aging61': "61–90",
  'rep.aging90': "Más de 90",
  'rep.agingTotal': "Total",
  'rep.agingCredit': "A favor",
  'rep.agingOpen': "Documentos abiertos",
  'rep.agingLessCredit': "menos notas a favor",
  'rep.agingLedger': "Saldo de la cuenta",
  'rep.agingMismatch': "No cuadra contra la cuenta:",
  'rep.agingNoDueAll': "Toda la cartera está sin vencimiento, así que los tramos "
    + "no dicen nada: un documento sin fecha de vencimiento no se puede envejecer. "
    + "Se arregla al registrarlo, no acá.",
  'rep.agingAll': "Todos",
  'rep.agingDocs': "{n} documentos",
  'rep.agingDocsOne': "1 documento",
  'rep.agingDays': "{n} días",
  'rep.agingOf': "de {total}",
  'rep.agingNoCustomer': "Sin cliente · viene del asiento de apertura",
  'rep.agingEmpty': "No hay nada abierto en este corte.",
  // ▶ Dice que NO es el formulario, y por qué. Un informe que se deja leer como
  // la declaración oficial se descubre el día de presentar, que es el peor.
  'rep.ivaWarn': "*Calculado del libro diario, y no es el formulario D-104.* Son los números para teclear en ATV, no un archivo que se presente. Revise cada línea contra sus comprobantes: el IVA es *mensual*, así que este mes es este mes y no lo que va del año.",
  'rep.ivaHow': "El débito fiscal sale de las cuentas de IVA por pagar y el crédito de las de IVA por acreditar, con sus auxiliares. Abra cada línea para ver de qué cuenta salió.",
  'rep.presented': "Presentado en",
  'rep.provisionalId': "cédula provisional",
  'rep.noId': "sin identificación",
  'rep.balanceTitle': "Balance General",
  'rep.balanceMeta': "{empresa} · {identificacion} · al {fecha}",
  'rep.equationChip': "Activo = Pasivo + Patrimonio",
  'rep.incomeTitle': "Estado de Resultados",
  'rep.incomeMeta': "Del 1 al {fecha}",
  'rep.marginChip': "Margen antes de impuesto 38,8 %",
  'rep.trialTitle': "Balance de Comprobación",
  'rep.trialMeta': "Sumas y saldos · {periodo} · {cuentas} cuentas con movimiento",
  'rep.sumsChip': "Sumas iguales",
  'rep.d100Title': "Declaración D‑101",
  'rep.d100Meta': "Impuesto sobre la Renta · periodo fiscal {anio} · Dirección General de Tributación",
  'rep.d100Due': "Vence",
  // ▶ Decía «Skoped Books genera el archivo», y NO lo genera: la declaración
  // se teclea en ATV. Era una promesa que sostenía el botón de XML de al lado,
  // y cuando el botón se retiró la frase se quedó sola diciéndola. Ahora dice
  // lo que sí hace —calcular el borrador y enseñar de dónde sale cada casilla—
  // y quién presenta.
  'rep.d100Warn': "*Borrador calculado del libro diario.* Revise cada casilla contra sus comprobantes —puede abrirlas para ver qué cuentas la forman— y tecléela en ATV. Skoped Books calcula; la presentación y la responsabilidad fiscal son suyas.",
  'rep.d100Currency': "*El D‑100 se presenta en colones.* Las operaciones en dólares ya están convertidas al tipo de cambio de cada transacción, no al de cierre.",
  'rep.box': "Casilla",
  'rep.concept': "Concepto",
  'rep.generated': "Generado el {fecha}",
  'rep.d100Breakdown': "Descargar el desglose",
  // ▶ Dice CUÁNTAS cuentas hay antes de abrir. Sin el número, abrir una casilla
  // es una apuesta: puede traer dos líneas o cuarenta, y en una pantalla que se
  // está copiando a mano eso decide si conviene abrirla ahora o bajar el CSV.
  'rep.d100Open': "— ver las {cuentas} cuentas",
  'rep.d100OpenOne': "— ver la cuenta",
  'rep.d100Hide': "— ocultar las {cuentas} cuentas",
  'rep.d100HideOne': "— ocultar la cuenta",
  'rep.d100NoAccounts': "Este periodo no tiene movimiento en ninguna casilla, así que no hay desglose que bajar.",
  'rep.exported': " generado.",
  'rep.exportedBody': " listo para descargar.",
  'rep.fileOf': "Archivo de ",
  'int.title': "Integraciones",
  'int.sub': "Opcionales. El sistema funciona completo sin ninguna activada — esto solo le ahorra digitar.",
  'int.docs': "Ver documentación API",
  'int.connect': "Conectar sistema propio",
  'int.plan': "Books funciona completo sin ninguna integración: todo se puede registrar a mano. Lo que sigue es lo que *podrá* recibir, y a qué cuentas iría cada evento.",
  'int.active': "activas",
  'int.activeOne': "activa",
  'int.endpoint': "Punto de entrada",
  'int.apiKey': "Llave de API",
  'int.keyNote': "Se almacena cifrada. Rótela si sospecha exposición.",
  'int.mapping': "Mapeo de cuentas",
  'int.copy': "Copiar",
  'int.copied': "Copiado",
  'int.show': "Mostrar",
  'int.hide': "Ocultar",
  'int.currencyLabel': "Moneda de los eventos",
  'int.currencyHint': "Los eventos en dólares se convierten al tipo de cambio de la fecha del evento.",
  'int.entries': "Asientos",
  'int.lastEvent': "Últ. evento",
  'int.errors': "Errores 30 d",
  'int.never': "nunca",
  'int.log': "Registro de eventos",
  'int.processed': "Procesados",
  'int.withError': "Con error",
  'int.eventCount': "{eventos} eventos",
  'int.eventOne': "evento",
  'int.retention': "Toda entrada queda en bitácora con su origen, su carga y su asiento.",
  'login.sub': "Sus libros, cuadrados y listos para Hacienda.",
  'login.emailHint': "usted@empresa.cr",
  'login.email': "Correo",
  'login.password': "Contraseña",
  'login.enter': "Entrar",
  'login.or': "O BIEN",
  'login.ms': "Iniciar sesión con Microsoft",
  'login.noAccount': "¿Sin cuenta?",
  'login.createCompany': "Cree su empresa gratis",
  'login.forgot': "Olvidé mi contraseña",
  'login.forgotSub': "Le mandamos un enlace para ponerle una nueva.",
  'login.forgotSend': "Mandar el enlace",
  'login.forgotSent': "Si esa dirección está en Books, va a llegar un correo con el enlace. Vence en una hora.",
  'login.backToSignIn': "Volver a entrar",
  // -- La pantalla de poner contraseña: invitación y recuperación ---------
  'clave.checking': "Comprobando el enlace…",
  'clave.subInvite': "Bienvenido. Ponga la contraseña con la que va a entrar.",
  'clave.subRecovery': "Ponga su contraseña nueva.",
  'clave.new': "Contraseña nueva",
  'clave.repeat': "Repítala",
  'clave.save': "Guardar la contraseña",
  'clave.rule': "Ocho caracteres o más.",
  'clave.done': "Contraseña guardada",
  'clave.doneBody': "Ya puede entrar a Books con ella.",
  'clave.enter': "Entrar",
  'clave.back': "Ir a la pantalla de acceso",
  'clave.linkTitle': "Este enlace no sirve",
  'clave.sin_enlace': "Esta dirección se abre desde el enlace de un correo de Books. Si llegó escribiéndola a mano, no hay nada que hacer acá.",
  'clave.enlace_vencido': "El enlace venció. Duran una hora, y solo sirven una vez. Pida otro desde «Olvidé mi contraseña».",
  'clave.enlace_usado': "Ese enlace ya se usó. Cada uno sirve una sola vez; pida otro si hace falta.",
  'clave.enlace_denegado': "El enlace fue rechazado. Pida uno nuevo desde «Olvidé mi contraseña».",
  'clave.enlace_desconocido': "El enlace no se pudo leer. Pida uno nuevo desde «Olvidé mi contraseña».",
  'clave.sin_configurar': "Falta configurar el proveedor de identidad. Esto es cosa nuestra, no suya.",
  'clave.sin_conexion': "No se pudo hablar con el proveedor de identidad. Pruebe otra vez.",
  'clave.err.vacia': "Escriba una contraseña.",
  'clave.err.corta': "Muy corta: ocho caracteres o más.",
  'clave.err.no_se_pudo': "No se pudo guardar. El enlace pudo vencer mientras llenaba el formulario: pida otro.",
  'clave.err.no_coincide': "Las dos no son iguales.",
  'clave.err.sin_conexion': "No hubo conexión. La contraseña NO se guardó.",
  'login.badEmail': "Revise el formato del correo.",
  'login.badPass': "La contraseña debe tener al menos 6 caracteres.",
  'login.signedIn': "Sesión iniciada",
  'login.whichCompany': "¿En cuál empresa trabaja hoy?",
  'login.rolesSub': "Su usuario tiene acceso a {n} empresas, con rol propio en cada una.",
  'login.switchUser': "← Cambiar de usuario",
  'login.addCompany': "Agregar empresa",
  'login.opening': "Abriendo ",
  'login.role': "Rol: ",
  'login.readonlyNote': " — no podrá crear ni editar asientos",
  'login.toDashboard': "Ir al dashboard →",
  'login.byLabs': "Un producto de Skoped Labs",
  'login.entries': "asientos",
  'lab.title': "Laboratorio de color",
  'lab.sub': "Cinco candidatos a color de producto, aplicados a pantallas reales. Elija uno arriba y toda la interfaz se retiñe.",
  'lab.pick': "Candidatos",
  'lab.why': "Por qué",
  'lab.risk': "Riesgo",
  'lab.contrast': "Contraste sobre blanco",
  'lab.family': "Tablero de familia",
  'lab.familySub': "El candidato junto a los nueve productos existentes. Si cuesta distinguirlo, no sirve.",
  'lab.candidate': "Candidato",
  'lab.applied': "Aplicado a la interfaz",
  'lab.semantics': "Convivencia con la semántica contable",
  'lab.semanticsSub': "Un contable ya gasta verde en ingreso y rojo en gasto. El acento tiene que sobrevivir al lado de ambos sin confundirse.",
  'lab.text': "Texto de acento",
  'lab.button': "Botón primario",
  'lab.console': "Consola de acceso",

  /* — agregadas después del porte — */
  'dash.period.closed': "Periodo cerrado: {periodo}",
  'fx.declared': "Tipo de cambio declarado vigente por el BCCR para esta fecha",
  'fx.copied': "ATENCIÓN: copiado del día anterior. No respalda un comprobante fiscal",
  'dash.kpi.cash': "Efectivo disponible",
  'dash.unbalancedTitle': "Los libros no cuadran.",
  'dash.unbalancedBody': " Activos no es igual a pasivos más patrimonio. No cierre el periodo hasta resolverlo.",
  'form.pickAccount': "Elegir cuenta…",
  'form.removeLine': "Quitar línea",
  'form.unreadable': "Hay un monto que no se puede leer. Revíselo: no se toma como cero.",
  'form.off': "No cuadra — diferencia de {monto}",
  'form.authority': "Este cuadre es un anticipo. Quien decide es la base: un asiento descuadrado se guarda como borrador, nunca contabilizado.",
  'form.noRate': "No hay tipo de cambio para {moneda} en esta fecha. Esa línea no se convierte ni se suma: el asiento no puede contabilizarse hasta resolverlo.",

  /* — reportes y NIIF 18 — */
  'er.g4': "Ingresos operativos",
  'er.g4.total': "Total de ingresos operativos",
  'er.g5': "Costo de ventas",
  'er.g5.total': "Total del costo de ventas",
  'er.g6': "Gastos operativos",
  'er.g6.total': "Total de gastos operativos",
  'er.g7': "Categoría de inversión",
  'er.g7.total': "Total de la categoría de inversión",
  'er.g8': "Categoría de financiación",
  'er.g8.total': "Total de la categoría de financiación",
  'er.g9': "Impuesto sobre la renta",
  'er.g9.total': "Total del impuesto sobre la renta",
  'er.ingresoNeto': "Ingreso neto",
  'er.utilidadOperativa': "Utilidad operativa",
  'er.antesDeFinanciacion': "Utilidad antes de financiación e impuestos",
  'er.antesDeImpuestos': "Utilidad antes de impuestos",
  'er.resultadoDelPeriodo': "Resultado del periodo",
  'er.niif18': "Subtotal obligatorio NIIF 18",
  'er.mpm': "MPM",
  'er.mpmTitle': "Medida definida por la gerencia",
  'er.mpmNote': "El *ingreso neto* no es un subtotal de las NIIF: es una medida que define la gerencia, y sobre ella se calculan los índices operativos. NIIF 18 obliga a revelarla como tal y conciliada contra el subtotal más cercano, que es la utilidad operativa.",
  'er.niif18Note': "Presentación conforme a NIIF 18, vigente para ejercicios que comienzan el 1 de enero de 2027. Los dos subtotales marcados son obligatorios por la norma.",
  'rep.readingAid': "Ayuda de lectura",
  'rep.readingAidNote': "Los libros se llevan en colones. Mostrar las cifras en dólares *no es una conversión contable*: no genera asientos, no cambia ningún saldo y no es la moneda de presentación de los estados. Es solo una forma de leerlos.",
  'rep.noRate': "No hay tipo de cambio del último día de este periodo: sin él no se puede leer en dólares, y no se inventa uno.",
  'rep.d100NoView': "La declaración se presenta en colones y no admite esta vista: es un documento con consecuencia fiscal, no una lectura gerencial.",
  'rep.prevMonth': "{periodo}",
  'rep.thisMonth': "{periodo}",
  'rep.ytd': "Acumulado {anio}",
  'rep.pctRevenue': "% de ingresos",
  'rep.code': "Código",
  'rep.totalAsset': "Total activo",
  'rep.reclassified': "reclasificada",
  'rep.reclassifiedWhy': "El saldo de esta línea NO es el del mayor: lleva una reclasificación de presentación. Dos cosas se presentan donde van sin asentarse —el pasivo de contrato que vence a más de doce meses del corte, y el activo de contrato que quedó en negativo, que es un pasivo— porque las dos dependen de la fecha del corte y un asiento las fijaría a un instante.",
  'rep.totalLiability': "Total pasivo",
  'rep.totalEquity': "Total patrimonio",
  'rep.liabPlusEquity': "Pasivo + Patrimonio",
  'rep.balanced': "Cuadrado",
  'rep.notBalanced': "NO CUADRA",
  'rep.revalued': "Revaluado al tipo de cierre",

  /* — integraciones — */
  'int.subReal': "Opcionales, y ninguna hace falta. Books funciona completo registrando a mano; una integración solo ahorra digitación.",
  'int.none': "Ninguna conectada",
  'int.available': "Lista para conectar",
  'int.planned': "En una etapa posterior",
  'int.stage': "Etapa",
  'int.mapsTo': "El evento se registraría en",
  'int.noMapping': "No genera asientos: alimenta datos, no el libro.",
  'int.eventCurrency': "Moneda de los eventos",
  'int.keyPending': "La llave se genera al conectar",
  'int.keyWhy': "No hay ninguna llave todavía, y por eso no se muestra ninguna. Una cadena con forma de credencial en una pantalla de demostración termina copiada a algún lado.",
  'int.logEmpty': "Todavía no ha entrado ningún evento",
  'int.logEmptyBody': "Cuando una integración esté conectada, cada evento aparecerá acá con su origen, lo que trajo y el asiento que produjo — o el error que lo detuvo.",
  'int.manualNote': "Un asiento hecho a mano y uno que llegó por integración se registran igual y valen igual. La diferencia queda en el campo de origen, que es trazabilidad, no jerarquía.",
  'int.retentionPending': "El plazo de conservación de la bitácora está pendiente de confirmar con el contador. No se fija acá una cifra sin respaldo.",
  'int.mech': "Cómo entra",
  'int.mech.api': "El sistema de origen envía los eventos. Se genera una llave al conectar.",
  'int.mech.archivo': "Usted sube el archivo. No lleva llave ni credencial.",
  'int.mech.servicio': "Books consulta al servicio con su propio token. Nada que configurar acá.",
  'form.copiedRate': "El tipo de cambio de esta fecha fue copiado del día anterior, no declarado vigente por el BCCR. El asiento se puede guardar como borrador, pero no contabilizar.",

  /* — acceso — */
  'acc.checking': "Comprobando su sesión…",
  'acc.notConfigured': "Falta configurar el acceso",
  'acc.notConfiguredBody': "No están puestas VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY. Se dice acá en vez de mostrar un formulario que no puede funcionar: tecleando una contraseña buena y viendo que falla, cualquiera concluiría que la contraseña está mal.",
  'acc.byInvitation': "Se entra a Books por invitación. Si su empresa ya usa Books, pídale a su administrador que lo agregue.",
  'acc.errCredentials': "Correo o contraseña incorrectos.",
  'acc.errOffline': "No se pudo contactar el servicio. No es su contraseña: vuelva a intentar en un momento.",
  'acc.errNoAccess': "Su acceso a esa empresa ya no está vigente. Elija otra.",
  'acc.noCompanies': "Su usuario no está en ninguna empresa",
  'acc.operatorOnly': "Entró como administrador de Skoped Labs",
  'acc.operatorOnlyBody': "Esta cuenta administra clientes y no lleva libros de ninguna empresa: es así a propósito. Lo que puede hacer está en la consola.",
  'acc.noCompaniesBody': "Puede ser que la invitación no se completó, o que lo retiraron. Su administrador puede agregarlo de nuevo.",
  'acc.listFailed': "No se pudieron consultar sus empresas",
  'acc.listFailedBody': "Es un fallo de conexión, no una falta de permisos. Vuelva a intentar.",
  'acc.retry': "Reintentar",
  'acc.signOut': "Salir",
  'acc.opening': "Abriendo sus libros…",
  'acc.floor': "Solo lectura y descargas",
  'acc.floorWhy': "Esta empresa está suspendida. Puede leer y exportar sus periodos cerrados, sus estados y su bitácora — eso no se le retiene nunca. No se pueden registrar movimientos.",
  'acc.readOnly': "Su rol es de solo lectura en esta empresa.",
  'acc.switchCompany': "Cambiar de empresa",
  /* --- E1 · lo que la base rechaza ---------------------------------------
     Una clave por CÓDIGO de la base. La API contesta el código y nunca el
     mensaje: el texto de una migración está escrito en español, y esta
     pantalla puede estar en inglés. El único sitio donde vive lo que lee una
     persona es este archivo. */
  'sb.SB020': "Un asiento necesita al menos dos líneas.",
  'sb.SB021': "El asiento no cuadra: el debe y el haber tienen que ser iguales.",
  'sb.SB022': "Esa cuenta no recibe movimientos: es de mayor o está desactivada.",
  'sb.SB023': "Falta la clasificación que esa cuenta exige.",
  'sb.SB025': "Esta empresa está suspendida: puede leer y exportar, no registrar.",
  'sb.SB026': "El importe llegó como número y tiene que viajar como texto.",
  'sb.SB027': "Esa fecha no cae en ningún periodo del ejercicio.",
  'sb.SB030': "El código tiene que colgar de su cuenta padre.",
  'sb.SB031': "Esa cuenta no existe.",
  'sb.SB032': "La cuenta ya tiene movimientos y no puede volverse de mayor.",
  'sb.SB033': "Eso no se puede cambiar: de ello dependen los estados y los índices.",
  'sb.SB034': "Una cuenta de la plantilla no se borra; se desactiva.",
  'sb.SB035': "El árbol de cuentas quedaría a medias.",
  'sb.SB036': "La cuenta necesita nombre en los dos idiomas.",
  'sb.unknown': "No se pudo completar la operación.",
  'sb.conexion': "No hubo conexión. Nada se guardó — vuelva a intentar.",

  /* --- E1 · el diario --------------------------------------------------- */
  'jr.recordedAt': "Registrado el — cuándo se tecleó, que no es la fecha contable",
  'jr.count': "{asientos} asientos",
  'jr.countOne': "1 asiento",
  'jr.title': "Diario",
  // ▶ Decía «TODOS los asientos de la empresa». Era cierto —la pantalla se
  // los traía todos— y por eso había que cambiarlo el mismo día que se puso
  // el filtro: una frase que era verdad y deja de serlo no avisa de nada.
  'jr.sub': "Los asientos del periodo que elija, del más reciente al más viejo. Un asiento registrado no se edita: se corrige con otro.",
  'jr.period': "Periodo",
  'jr.wholeYear': "Todo {anio}",
  // El tope, dicho. Un recorte callado convierte el conteo de la esquina en un
  // error con pinta de dato.
  'jr.trimmed': "cortado en {limite}",
  'jr.ledgerTrimmed': "Se muestran los primeros {limite} movimientos del periodo. El saldo corrido de las filas visibles es correcto, pero *no es el saldo final de la cuenta*: faltan los movimientos posteriores. Acote el periodo para verlo completo.",
  'jr.empty': "Todavía no hay asientos en este periodo.",
  'jr.editDate': "Fecha",
  'jr.editDateHint': "corregir la fecha de este asiento manual",
  'jr.newDate': "Fecha nueva",
  'jr.editReason': "Motivo",
  'jr.editReasonHint': "por qué se está corrigiendo; queda en la auditoría",
  'jr.editSave': "Guardar",
  'jr.editDone': "Fecha del asiento #{numero} corregida.",
  'jr.notHere': "El asiento #{asiento} no está en este periodo. Puede estar en otro mes, o haberse anulado.",
  'jr.number': "Asiento",
  'jr.date': "Fecha",
  'jr.memo': "Glosa",
  'jr.total': "Total",
  'jr.origin': "Origen",
  'jr.ledger': "Mayor",
  'jr.account': "Cuenta",
  'jr.balance': "Saldo",
  'jr.pickAccount': "Elija una cuenta para ver su mayor",
  'jr.saved': "Asiento registrado",

  /* --- E1 · clasificación analítica ------------------------------------- */
  'cls.customer': "Cliente",
  'cls.project': "Proyecto",
  'cls.department': "Área",
  'cls.modalidad': "Modalidad",
  'cls.supplier': "Proveedor",
  /* ▶ Vocabulario cerrado por dimensión. Las claves siguen el patrón
     `voc.{dimension}.{valor}` y las lee `CampoDeDimension` para enseñar el
     nombre humano en el desplegable, en lugar del código `support`/`rnd`/… que
     la base conoce. Lo que viaja en el asiento sigue siendo el código. */
  'voc.department.ps': "Serv. profesionales",
  'voc.department.support': "Soporte / Mantenimiento",
  'voc.department.cloud': "Nube / Infraestructura",
  'voc.department.rnd': "I+D",
  'voc.department.sales': "Ventas",
  'voc.department.marketing': "Mercadeo",
  'voc.department.ga': "Administración",
  'voc.stream.maintenance': "Mantenimiento",
  'voc.stream.subscription': "Suscripción",
  'voc.stream.hosting': "Hosting",
  'voc.stream.transactional': "Transaccional",
  'voc.stream.ps': "Serv. profesionales",
  'voc.stream.license': "Licencias",
  'voc.stream.hardware': "Hardware",
  'voc.stream.other': "Otros",
  'voc.modalidad.saas': "SaaS",
  'voc.modalidad.on_premise': "On-premise",
  // ▶ Decía «Esta cuenta la exige», y desde la 0071 no siempre es la cuenta:
  // el salario del área de soporte pide `modalidad` por el ÁREA, no por ser la
  // 6110. La frase vieja mandaba a mirar la cuenta a quien buscara por qué.
  'cls.required': "Hacen falta para registrar",

  /* --- E1 · catálogo ---------------------------------------------------- */
  'cat.title': "Catálogo de cuentas",
  // El conteo llevaba SOLO el número: «123», suelto en la esquina de la
  // tarjeta, sin decir de qué. En una pantalla de cuentas contables un número
  // sin rótulo se lee como un código o como un saldo.
  'cat.count': "{cuentas} cuentas",
  'cat.countOne': "1 cuenta",
  'cat.sub': "Puede renombrar cualquier cuenta, colgar auxiliares y desactivar las que no use. Lo que no cambia es su estructura: de ella dependen los estados y los índices, no del nombre.",
  'cat.rename': "Renombrar",
  'cat.addChild': "Colgar auxiliar",
  'cat.deactivate': "Desactivar",
  'cat.activate': "Activar",
  'cat.nameEs': "Nombre (español)",
  'cat.nameEn': "Nombre (inglés)",
  'cat.bothLangs': "Los dos idiomas van juntos: renombrar solo uno deja dos catálogos que no coinciden.",
  'cat.newCode': "Código de la cuenta nueva",
  'cat.inheritNote': "La cuenta nueva hereda los atributos de su padre. Por eso no puede descuadrar un estado.",
  'cat.ledgerAccount': "De mayor",
  'cat.inactive': "Desactivada",
  'cat.save': "Guardar",
  'cat.cancel': "Cancelar",
  'cat.search': "Buscar",
  // El rótulo DICE la regla: sin esto, una fila que aparece porque coincidió
  // su nombre en inglés no tiene explicación a la vista.
  'cat.searchHint': "código o nombre, en los dos idiomas",
  'cat.group': "Grupo",
  'cat.allGroups': "Todos los grupos",
  'cat.onlyPostable': "Solo las que reciben movimiento",
  'cat.hideInactive': "Esconder las desactivadas",
  'cat.clearFilters': "Quitar filtros",
  // Dos números, y no uno: «2 cuentas» a secas se lee como «esta empresa tiene
  // dos». Y los padres que se muestran para no romper el árbol NO se cuentan.
  'cat.countFiltered': "{cuantas} de {total} cuentas",
  'cat.noMatch': "Ninguna cuenta coincide con lo que buscó. El catálogo sigue completo: lo que cambió es lo que se está mostrando.",
  'fx.none': "No hay tipo de cambio publicado para esta fecha.",
  'fx.asking': "Buscando el tipo de cambio…",
  'rep.period': "Periodo",
  'idx.title': "Índices operativos",
  'idx.fromLedger': "Salen del libro. Nadie los teclea.",
  'idx.target': "meta",
  'idx.noTarget': "sin meta fijada",
  'idx.motivo.sin_fte': "Este índice mide contra la capacidad del equipo (FTE), un dato que el producto ya no captura. Queda sin base contra la que medir.",
  'idx.motivo.sin_jornada': "Este índice necesita las horas laborables del mes, un dato que el producto ya no captura.",
  'idx.motivo.sin_disponible': "El disponible del mes salió en cero o menos: las horas de vacaciones y feriados se comieron la jornada declarada. Es un dato raro y conviene verlo, no taparlo con un porcentaje.",
  'idx.targetProposed': "meta de la casa",
  'idx.targetProposedWhy': "Esta meta viene del modelo contable y nadie de esta empresa la ha confirmado todavía. Se revisa en Configuración.",
  'idx.provisional': "provisional",
  'idx.provisionalWhy': "El mes no ha cerrado: se calcula sobre el acumulado y no es definitivo.",
  'idx.noData': "No se puede calcular con lo que hay.",
  'op.title': "Consola de operador",
  'op.sub': "Dar de alta clientes y repartir accesos. Desde acá no se ven los libros de nadie.",
  'op.created': "Cliente creado: {razon} · {codigo}. Todavía no tiene a nadie con acceso — «Accesos», en su fila.",
  'op.notOperator': "Su usuario no es operador.",
  'op.notOperatorWhy': "La consola es de quien opera Skoped, no de quien lleva los libros de una empresa. Si llegó por un enlace guardado, sus libros siguen donde estaban.",
  'op.backToBooks': "Volver a Books",
  'op.failed': "No se pudo completar la operación.",
  'op.newClient': "Nuevo cliente",
  'op.clientCount': "{empresas} empresas",
  'op.clientCountOne': "1 empresa",
  'op.clientCountFiltered': "{cuantas} de {total} empresas",
  'op.billing': "Facturación electrónica",
  'op.billingWhy': "La factura electrónica se emite en ATV y *el consecutivo lo asigna Hacienda*. Esta lista dice quién numera a cada cliente, porque dos contadores sobre la misma secuencia legal dan saltos o repetidos — y un consecutivo no se mueve para atrás.",
  'op.noIssuerYet': "Sin decidir",
  'op.startIssuer': "Dar de alta",
  'op.toBooks': "Que numere Books",
  'op.toAtv': "Que numere Hacienda",
  'op.turnOff': "Apagar",
  'op.billingNone': "Ninguna empresa tiene facturación todavía. Las demos no salen acá: no facturan.",
  // ▶ Lo dice para que quien pulsa sepa que está AFIRMANDO algo, no marcando
  // una casilla: la base no puede ver el almacén de secretos de Cloudflare.
  'op.booksWarn': "Decir que numere Books abre la puerta a que este despliegue tome números de una secuencia legal que hoy lleva Hacienda. Si las dos numeran, salen saltos o repetidos — y un consecutivo no se mueve para atrás. Queda anotado en la bitácora quién lo dijo.",
  'op.clients': "Clientes",
  'op.search': "Buscar",
  // La cédula entra en la búsqueda: es por donde se busca a un cliente cuando
  // llega un pago y lo único que trae es el número.
  'op.searchHint': "código, razón social o cédula",
  'op.allStates': "Todos los estados",
  'op.demoFilter': "Demostración",
  'op.demoAll': "Clientes y demos",
  'op.demoWithout': "Solo clientes de verdad",
  'op.demoOnly': "Solo las demos",
  'op.noMatch': "Ninguna empresa coincide con el filtro. Están todas: lo que cambió es lo que se muestra.",
  'op.noClients': "Todavía no hay ninguna empresa dada de alta.",
  'op.code': "Código",
  'op.codeHint': "Minúsculas, sin espacios. Es el nombre del esquema y NO se puede cambiar nunca.",
  'op.legalName': "Razón social",
  'op.taxId': "Identificación",
  'op.state': "Estado",
  'op.members': "Accesos",
  'op.access': "Accesos",
  'op.create': "Crear cliente",
  'op.grant': "Dar acceso",
  'op.revoke': "Quitar",
  'op.email': "Correo",
  'op.emailHint': "El MISMO con el que se le invita en el proveedor de identidad. Uno distinto crea un usuario que no es nadie.",
  'op.personName': "Nombre",
  'op.role': "Rol",
  'op.hasEntered': "ya entró",
  'op.neverEntered': "no ha entrado",
  'op.noMembers': "Nadie tiene acceso todavía.",
  'op.provisional': "La identificación es un relleno",
  'op.provisionalWhy': "Para una empresa cuyo trámite no ha terminado. Queda marcada hasta corregirla, y quien emita un documento fiscal tiene que negarse mientras lo esté.",
  'op.provisionalChip': "provisional",
  'op.kind': "¿Cliente o empresa de prueba?",
  'op.kindReal': "Cliente de verdad",
  'op.kindDemo': "Empresa de prueba",
  'op.kindWhy': "Hay que elegir: no viene marcado. Una prueba que entre como cliente se factura y se cuenta en las métricas; un cliente que entre como prueba deja de reportarse y nadie se entera.",
  'op.demoChip': "prueba",
  'op.invited': "Acceso dado, y se le mandó la invitación por correo.",
  'op.alreadyInAuth': "Acceso dado. Esa persona ya tenía cuenta, así que no se mandó otra invitación: ya puede entrar.",
  'op.inviteByHand': "Acceso dado. La invitación NO se mandó —falta la llave de administración— así que hay que invitarla a mano en el proveedor de identidad.",
  'op.inviteFailed': "Acceso dado. La invitación no se pudo mandar: hay que invitarla a mano en el proveedor de identidad.",
  'fac.title': "Facturas electrónicas",
  'fac.sub': "Los comprobantes que esta empresa emite ante Hacienda, y en qué estado quedó cada uno.",
  'fac.issuer': "El emisor",
  'fac.documents': "Comprobantes",
  'fac.branch': "sucursal {sucursal} · terminal {terminal}",
  'fac.number': "Consecutivo",
  'fac.kind': "Tipo",
  'fac.state': "Estado",
  // ▶ «Asiento» y no «asiento contable»: la palabra ya significa eso en este
  // producto, y el «—» de una fila sin asiento NO es un hueco — un comprobante
  // puede nacer antes que su asiento.
  'fac.entry': "Asiento",
  'fac.count': "{n} comprobantes",
  'fac.countOne': "1 comprobante",
  // ▶ «No hay emisor» y «no ha facturado» se ven iguales en una tabla vacía y
  // no lo son: la primera dice que NO SE PUEDE, la segunda que todavía no.
  'fac.noIssuer': "Las facturas de esta empresa se emiten en ATV, fuera de Books.",
  'fac.noIssuerWhy': "Es una decisión, no un pendiente: *hoy se factura directamente en el sistema de Hacienda y Books registra el asiento*. La emisión desde acá llega cuando la facturación viva en el hub; hasta entonces esta pantalla se queda vacía a propósito.",
  'fac.empty': "Todavía no se ha emitido ningún comprobante.",
  'fac.emptyNoIssuer': "No hay comprobantes acá porque no se emiten desde acá. La venta sí está en los libros: se registra como asiento, en el diario.",
  'fac.trimmed': "Hay más de {limite} comprobantes y se muestran *los más recientes*. Lo que falta es lo más viejo.",
  // Los cuatro pasos del trámite, dichos como lo que falta y no como un estado.
  'fac.step.atv': "Sus facturas se emiten en ATV, y *el consecutivo lo asigna Hacienda*. Books anota acá el número que salió y el asiento que le corresponde — no toma números: dos contadores sobre la misma secuencia dan saltos o repetidos, y un consecutivo no se mueve para atrás.",
  'fac.step.apagado': "Este emisor está apagado y no emite. Los comprobantes que ya salieron siguen siendo válidos: apagar no anula nada.",
  'fac.kind.factura': "Factura",
  'fac.kind.nota_credito': "Nota de crédito",
  'fac.kind.nota_debito': "Nota de débito",
  'fac.kind.tiquete': "Tiquete",
  'fac.kind.factura_compra': "Factura de compra",
  'fac.kind.exportacion': "Exportación",
  'fac.kind.recibo_pago': "Recibo de pago",
  'fac.why': "Un comprobante aceptado no se borra ni se corrige: se anula con otro documento, que lleva su propio consecutivo. Por eso «aceptado» y «rechazado» son finales.",
  'bit.title': "Bitácora",
  'bit.sub': "Quién hizo qué en los libros de esta empresa, y cuándo.",
  'bit.why': "Es una entrada del piso: una empresa suspendida la lee entera. Ahí está la orden que la suspendió.",
  'bit.floor': "Esta empresa está suspendida, y esta pantalla sigue siendo suya: la bitácora es de lo que el piso garantiza.",
  'bit.from': "Desde",
  'bit.to': "Hasta",
  'bit.action': "Qué pasó",
  'bit.who': "Quién",
  'bit.detail': "Detalle",
  'bit.trimmed': "Hay más de {limite} anotaciones y se muestran *las más recientes*. Lo que falta es lo más viejo, que suele ser lo que se viene a buscar: acote con Desde y Hasta para verlo.",
  // ▶ Estos dos se llamaban `bit.*` cuando solo los leía la bitácora. Los
  // pide también el diario —quién escribió cada asiento— y no son un concepto
  // de la bitácora: son de QUIEN ACTÚA. Duplicarlos habría dejado dos frases
  // que hay que acordarse de cambiar juntas.
  'common.system': "el sistema",
  'common.removedUser': "usuario retirado",
  'jr.who': "Quién lo escribió",
  'bit.empty': "Todavía no hay nada anotado.",
  'bit.empresa.creada': "Se creó la empresa",
  'bit.empresa.provisionada': "Se sembró el catálogo y los periodos",
  'bit.membresia.dada': "Se dio un acceso",
  'bit.membresia.quitada': "Se quitó un acceso",
  'bit.miembro.invitado': "Persona invitada a la empresa",
  'bit.miembro.rol_cambiado': "Rol cambiado",
  'bit.miembro.retirado': "Persona retirada de la empresa",
  'bit.mapeo.guardado': "Correspondencias de cuentas guardadas",
  'bit.apertura.cargada': "Se cargaron los saldos de apertura",
  'bit.periodo.revaluado': "Se corrió la revaluación del periodo",
  'bit.diferencial.realizado': "Se asentó la diferencia de cambio realizada al liquidar",
  'bit.contraparte.alta': "Se dio de alta una contraparte",
  'bit.contraparte.editada': "Se editó una contraparte",
  'bit.contraparte.activada': "Se activó una contraparte",
  'bit.contraparte.desactivada': "Se desactivó una contraparte",
  'bit.documento.registrado': "Se registró una factura con su asiento",
  'bit.pago.registrado': "Se registró un pago con su asiento",
  'bit.cobro.en_moneda': "Se cobró una factura en moneda extranjera con un depósito en colones",
  'bit.aplicacion.escrita': "Se dijo qué documento canceló un abono",
  'bit.aplicacion.deshecha': "Se deshizo una aplicación, con su motivo",
  'bit.ejercicio.abierto': "Ejercicio abierto",
  'bit.ejercicio.cerrado': "Ejercicio cerrado",
  'bit.periodo.cerrado': "Se cerró el periodo",
  'bit.periodo.reabierto': "Se reabrió el periodo",
  'bit.activo.alta': "Se registró un bien depreciable",
  'bit.activo.depreciacion': "Se depreció el mes",
  'bit.obligacion.alta': "Se registró un ingreso cobrado por adelantado",
  'bit.obligacion.baja': "Se dio de baja una obligación de contrato",
  'bit.obligacion.devengo': "Se devengó el mes",
  'bit.deterioro.matriz': "Se declaró la matriz de deterioro",
  'bit.deterioro.provision': "Se ajustó la estimación por deterioro",
  'bit.renta.provision': "Se provisionó el impuesto de renta",
  'bit.empresa.tasa_de_renta': "Se cambió la tarifa de renta",
  'bit.proyecto.alta': "Se dio de alta un proyecto",
  'bit.proyecto.cierre': "Se cerró un proyecto",
  'bit.proyecto.avance': "Se declaró el avance de un proyecto",
  'bit.proyecto.reconocimiento': "Se reconoció el avance del mes",
  'bit.proyecto.categoria': "Se categorizó un proyecto",
  'bit.indice.meta': "Se declaró la meta de un índice",
  'bit.persona.declarada': "Se dio de alta o se corrigió una persona",
  'bit.horas.cargadas': "Se cargaron horas",
  'bit.jornada.declarada': "Se declararon las horas laborables de un mes",
  'bit.diferido.declarado': "Se declaró una diferencia temporaria",
  'bit.diferido.eliminado': "Se eliminó una diferencia temporaria",
  'bit.diferido.provisionado': "Se asentó el impuesto de renta diferido",
  'bit.horas.cambiadas': "Se corrigieron unas horas ya cargadas",
  'bit.proyecto.presupuesto': "Se declararon las horas presupuestadas de un proyecto",
  'bit.apertura.sin_saldos_previos': "Se declaró que la empresa no tiene saldos anteriores",
  'bit.banco.extracto': "Se importó un estado bancario",
  'bit.banco.cuenta_declarada': "Se ligó una cuenta del banco a su cuenta contable",
  'bit.asiento.fecha_corregida': "Se corrigió la fecha de un asiento manual, con motivo",
  'bit.banco.emparejado': "Se corrió el emparejamiento automático",
  'bit.banco.registrado': "Se registró el asiento de una nota del banco",
  'bit.banco.saldo_declarado': "Se declaró el saldo del estado bancario",
  'bit.empresa.configuracion': "Se cambió la configuración de la empresa",
  'bit.empresa.identificacion': "Se corrigió la identificación de la empresa",
  'bit.emisor.alta': "Se dio de alta el emisor de facturación",
  // ▶ Dice el PASO y no «se cambió algo»: quien lee la bitácora viene a saber
  // qué cambió, y el detalle guarda el «de» y el «a».
  'bit.emisor.numeracion': "Cambió quién asigna el consecutivo",
  'bit.operador.dado': "Se dio el permiso de operador",
  'bit.operador.quitado': "Se quitó el permiso de operador",
  'tar.title': "Trabajos programados",
  'tar.why': "El estado se calcula al mirarlo, no lo reporta el trabajo: un proceso muerto no se denuncia a sí mismo.",
  'tar.al_dia': "al día",
  'tar.muda': "MUDA",
  'tar.nunca': "NUNCA HA CORRIDO",
  'tar.sin_fuente': "sin fuente de la que leer",
  'tar.latido': "Latido de la base",
  'tar.replica_de_cambio': "Réplica de tipos de cambio",
  'dash.noSeries': "Todavía no hay meses con movimiento en este ejercicio.",
  'dash.noFx': "No hay saldos en moneda extranjera.",
  'dash.noPeriod': "No hay periodo abierto para este mes.",
  // ▶ Decía «Falta declarar en el catálogo» —una orden para quien lee— y la
  // inglesa decía un hecho. Dos idiomas afirmando cosas distintas, que es lo
  // que la revisión bilingüe no ve: comprueba que la clave esté en los dos
  // diccionarios, no que las dos entradas digan lo mismo.
  // Y la orden era imposible de cumplir: el catálogo no ofrece declararlo, ni
  // acá ni en ninguna parte. No es configuración del cliente — es una decisión
  // de producto abierta, escrita en la 0035: «una regla de negocio sin fuente».
  /* ▶ Decía «Books todavía no declara cuáles cuentas son efectivo y
     equivalentes». Ya las declara —la `0100` puso `flujo` en el catálogo—,
     así que la frase cambió de dueño: ahora solo sale cuando es ESTA empresa
     la que no tiene ninguna. */
  'dash.cashUndeclared': "Esta empresa no tiene ninguna cuenta declarada como efectivo, así que no hay saldo de caja que enseñar.",
  'tid.01': "Física · cédula nacional",
  'tid.02': "Jurídica · sociedad",
  'tid.03': "DIMEX",
  'tid.04': "NITE",
  'tid.EX': "Extranjera",
  'op.idType': "Tipo de identificación",
  'op.idTypeWhy': "Hay que elegirlo: cambia cómo se normaliza el número. Una persona física dada de alta como sociedad queda con una cédula canónica que no es la suya, y eso no se ve hasta que alguien la busca.",
  'op.currency': "Moneda local de los libros",
  'op.currencyWhy': "Tres letras ISO. NO se puede cambiar después del primer asiento: cambiarla es una migración, no una casilla.",
  'op.idReal': "La identificación es la de verdad",
  'op.idPlaceholder': "Es un relleno, todavía no la tengo",
  'op.idWhy': "Hay que elegir: no viene marcado. Un relleno que entre como cédula de verdad se usa en la primera declaración, y ahí ya no se ve que era inventado.",
  'op.pickRole': "Elija el rol",
  'ap.title': "Saldos de apertura",
  'ap.sub': "Los saldos con los que la empresa llega de su sistema anterior. Se cargan UNA vez, antes de cualquier asiento, y quedan en el libro como el asiento #1.",
  'ap.noPrior': "Esta empresa no tiene saldos anteriores",
  'ap.noPriorWhy': "Está declarado que sus libros empiezan el {desde}. No hay nada que cargar, y la puesta en marcha ya no lo pide.",
  'ap.noPriorTitle': "¿La empresa acaba de constituirse?",
  'ap.noPriorHelp': "Si sus libros empiezan en esta fecha y no viene de otro sistema, no hay saldos que cargar. Decilo acá y la puesta en marcha deja de pedirlos: si no, los va a pedir siempre, porque nunca van a llegar.",
  'ap.noPriorDo': "No hay saldos anteriores",
  'ap.noPriorNote': "Usa la fecha de arriba. Se anota en la bitácora y no se puede deshacer.",
  'dif.title': "Ingresos diferidos",
  'pry.title': "Proyectos",
  'pry.fromBacklog': "horas y costo: Backlog",
  'pry.kpiMargin': "Margen del mes",
  'pry.kpiHours': "Horas del mes",
  'pry.kpiActive': "Proyectos activos",
  'pry.kpiPending': "Pendiente de reconocer",
  'pry.reportedToRecognise': "Reportado por Backlog",
  'pry.pending': "Pendiente",
  'pry.recogniseNote': "Asienta exacto lo que Backlog reportó. Correr dos veces no duplica.",
  'pry.nothingPending': "Nada pendiente de reconocer este mes.",
  'pry.cardFoot': "{horas} h · contrato {precio}",
  'pry.manyCurrencies': "varias monedas",
  'pry.marginChip': "margen {pct} %",
  'pry.teamTitle': "Equipo · utilización del mes",
  'pry.teamWhy': "Horas por persona que reporta Backlog. La barra es la parte facturable.",
  'pry.billablePct': "{pct}% fact.",
  'pry.note': "Nota",
  'pry.budget': "Horas presup.",
  'hrs.title': "Horas",
  'hrs.sub': "Quién trabajó, en qué y cuánto. Books las recibe: no las captura.",
  'hrs.whereFrom': "De dónde vienen",
  'hrs.whereFromWhy': "Books RECIBE las horas. La captura sigue donde está hoy —la hoja de cálculo— y mañana en el producto de proyectos; la misma puerta sirve para las dos épocas. ▶ Por eso acá no hay semana, ni calendario, ni aprobaciones: eso trae detrás un calendario laboral, vacaciones y permisos, y convertiría Books en otro producto sin que nadie lo hubiera decidido.",
  'hrs.noEntries': "Las horas NO generan ningún asiento. El ingreso sigue saliendo del avance que se declara en",
  'hrs.monthTitle': "El mes",
  'hrs.shift': "Horas laborables del mes",
  'hrs.shiftWhy': "Las de UNA persona a tiempo completo. El disponible del equipo se multiplica por la suma de los FTE, y de ahí sale la utilización facturable. ▶ Se declara porque depende de los feriados, y un calendario laboral es justo lo que este producto decidió no traer: mide lo que ocurrió, no programa lo que va a ocurrir.",
  'hrs.shiftProposal': "la casa propone {horas} ({dias} días hábiles × 8)",
  'hrs.shiftUse': "Usar la propuesta",
  'hrs.shiftSave': "Guardar",
  'hrs.shiftNone': "Sin declarar. Mientras falte, la utilización facturable dice «sin datos» en vez de un número: con una meta del 75 % al lado, un índice calculado sobre un supuesto es peor que un índice que falta.",
  'hrs.monthEmpty': "No hay horas cargadas en este mes.",
  'hrs.total': "Horas",
  'hrs.billable': "Facturables",
  'hrs.internal': "Sin proyecto",
  'hrs.billablePct': "% facturable",
  'hrs.noPct': "sin horas",
  'hrs.peopleTitle': "Las personas",
  'hrs.peopleWhy': "Una persona de esta lista NO tiene que ser un usuario de Books: un consultor no necesita acceso a los libros para que sus horas cuenten. ▶ El código es el de la hoja de cálculo, y es lo que ata sus horas a ella: con un código nuestro habría dos nombres para la misma persona.",
  'hrs.noPeople': "Todavía no hay ninguna persona. Déla de alta antes de cargar horas: crearla al paso convertiría una errata del archivo en una persona nueva, y nadie volvería a mirar esta lista.",
  'hrs.code': "Código",
  'hrs.name': "Nombre",
  'hrs.fte': "FTE",
  'hrs.purpose': "Las horas no generan asientos y no bloquean el cierre. Alimentan la utilización del equipo y el costo de gestión por proyecto.",
  'hrs.loaded': "Cargado",
  'hrs.days': "{dias} día(s)",
  'hrs.inactive': "inactiva",
  'hrs.undeclared': "sin declarar",
  'hrs.edit': "Editar",
  'hrs.savePerson': "Guardar la persona",
  'hrs.fteWhy': "Vacío significa SIN DECLARAR, y se puede guardar así. No es cero: un FTE de cero diría que esa persona no trabaja, y una tarifa de cero que trabaja gratis.",
  'hrs.addTitle': "Cargar horas",
  'hrs.person': "Persona",
  'hrs.project': "Proyecto",
  'hrs.internalOption': "sin proyecto (horas internas)",
  'hrs.day': "Día",
  'hrs.hours': "Horas",
  'hrs.note': "Nota",
  'hrs.billableLine': "Facturable",
  'hrs.billableWhy': "Lo facturable es POR LÍNEA, no por proyecto: la misma persona el mismo día en el mismo proyecto puede tener horas facturables y retrabajo que no lo es — y el retrabajo es justo lo que la tarifa efectiva existe para encontrar.",
  'hrs.load': "Cargar",
  'hrs.receipt': "Recibidas {recibidas} · entraron {entraron} · cambiaron {cambiaron} · iguales {iguales}.",
  'hrs.receiptSame': "Nada cambió: esas horas ya decían lo mismo.",
  'hrs.listTitle': "Lo cargado",
  'hrs.listEmpty': "Nada cargado en este mes.",
  'hrs.yes': "sí",
  'hrs.no': "no",
  'hrs.noDelete': "Una hora cargada se corrige cargándola otra vez con otras horas, y el cambio queda en la bitácora con su antes y su ahora. No se borra: un borrado dejaría un total que cambió y nada que explique por qué.",
  'pry.backlogTitle': "Backlog",
  'pry.backlogViablePct': "Backlog viable",
  'pry.backlogOf': "{viable} de {total}",
  'pry.backlogNotViable': "No viable",
  'pry.backlogUncategorised': "Sin categorizar",
  'pry.backlogUncategorisedCount': "{cuantos} proyecto(s)",
  'pry.backlogWhy': "El backlog es lo firmado y todavía no reconocido, y no necesita horas: sale del precio del contrato menos el ingreso ya reconocido. ▶ La cifra con la que se planifica capacidad es la VIABLE — sólo «a toda marcha» y «movimiento lento» cuentan. Un backlog total sin esa distinción dice que hay trabajo donde puede no haberlo. Y lo que nadie categorizó va aparte: un proyecto sin mirar no es un proyecto estancado.",
  'pry.category': "Categoría",
  'pry.categoryNone': "sin categorizar",
  'pry.sub': "Los contratos de servicios que se ganan a lo largo del tiempo, y cuánto se ha ejecutado de cada uno.",
  'pry.monthTitle': "El avance del mes",
  'pry.period': "Periodo",
  'pry.project': "Proyecto",
  'pry.price': "Precio del contrato",
  'pry.currency': "Moneda del contrato",
  'pry.progress': "Avance (%)",
  'pry.hours': "Horas",
  'pry.rate': "Tarifa por hora",
  'pry.rateEffective': "efectiva {tarifa}",
  'pry.rateContracted': "contratada {tarifa}",
  'pry.rateAlert': "regalando margen",
  'pry.rateAlertWhy': "La tarifa efectiva —lo reconocido entre las horas facturables consumidas— quedó por debajo de la contratada. Sobrepasar las horas vendidas regala margen sin que ningún número del libro se mueva: el precio no cambia y las horas sí.",
  'pry.rateNone': "sin presupuesto no hay contratada",
  'pry.budgetNone': "sin presupuesto",
  'pry.suggest': "las horas sugieren {pct} %",
  'pry.suggestWhy': "Horas cargadas entre horas presupuestadas. Es una SUGERENCIA: el avance se declara porque es un juicio, y el botón sólo copia el número al campo para que se pueda cambiar antes de guardarlo.",
  'pry.noBudget': "hay horas, falta el presupuesto para poder sugerir",
  'pry.progressNone': "sin declarar",
  'pry.declaredIn': "declarado en {periodo}",
  'pry.target': "Ingreso que corresponde",
  'pry.recognised': "Ya reconocido",
  'pry.adjustment': "Se asentaría",
  'pry.declare': "Declarar",
  'pry.close': "Cerrar proyecto",
  'pry.closed': "cerrado",
  'pry.notePlaceholder': "160 de 400 horas",
  'pry.recognise': "Reconocer el avance",
  'pry.twiceIsFine': "Correrlo dos veces no duplica: se asienta la diferencia contra un objetivo que no cambia.",
  'pry.declareFirst': "El avance no sale de los libros: declárelo primero y después se reconoce. Books no puede medirlo — la planilla propia va a gastos de operación y no lleva proyecto.",
  'pry.doneOk': "Avance reconocido · {importe} · asiento {asiento}",
  'pry.doneNone': "No había nada que reconocer en ese mes.",
  'pry.noneAtAll': "Esta empresa no tiene proyectos abiertos en este corte.",
  'pry.mismatch': "El auxiliar dice que debería haber {objetivo} reconocido y los libros tienen {reconocido}. Ningún total se rompe por esto — el balance cuadra igual y lo único que sale mal es el ingreso del periodo.",
  'pry.negative': "{cuantos} proyecto(s) tienen el activo de contrato en negativo: se facturó más de lo reconocido, y eso es un PASIVO de contrato con el disfraz de un activo. Su sitio es la cuenta de ingreso diferido de servicios profesionales, y hoy se reclasifica a mano.",
  'pry.profitTitle': "Rentabilidad de gestión",
  'pry.profitWhy': "Ingreso reconocido − costo de gestión que reporta Backlog. No asienta nada.",
  'pry.profitNote': "Vista de gestión: el costo lo reporta Backlog por proyecto, ya calculado; no son asientos. La planilla real sigue en OPEX.",
  'pry.colProject': "Proyecto",
  'pry.colIncome': "Ingreso",
  'pry.colCost': "Costo",
  'pry.colMargin': "Margen",
  'pry.noCostYet': "costo pendiente de Backlog",
  'pry.costOtherCurrency': "costo en otra moneda",
  'pry.addTitle': "Los contratos",
  'pry.add': "Agregar",
  'pry.addHelp': "El código del proyecto ES el que llevan los asientos en su clasificación: con uno distinto habría dos nombres para el mismo proyecto y el ingreso reconocido no se podría atar a nada.",
  'pry.code': "Código",
  'pry.name': "Nombre",
  'pry.number': "Número de Backlog",
  'pry.numberHint': "la llave con la que el hub ata la factura; vacío si no se factura",
  'pry.customer': "Cliente",
  'pry.accountContract': "Cuenta de contrato",
  'pry.contractHint': "normalmente la de ingreso diferido: la factura va primero",
  'pry.groupDeferred': "Ingreso diferido — se facturó y todavía no se ganó",
  'pry.groupContractAsset': "Activo de contrato — se ganó y todavía no se facturó",
  'pry.accountIncome': "Cuenta de ingreso",
  'pry.onlyIncome': "solo ingresos",
  'pry.start': "Inicio",
  'pry.save': "Guardar",
  'dif.sub': "Lo cobrado por adelantado no es ingreso del mes: se gana a lo largo del plazo.",
  'dif.tiesTitle': "El auxiliar cuadra contra la contabilidad.",
  'dif.mismatchTitle': "El auxiliar NO cuadra contra la contabilidad.",
  'dif.tiesDetail': "Pendiente del auxiliar {auxiliar} · saldo contable {contable}. "
    + "Las dos cifras vienen por caminos distintos —el calendario de cada obligación "
    + "y los movimientos de las cuentas de contrato—, así que coincidir significa algo.",
  'dif.mismatchOver': "Sobran {diferencia} en las cuentas de contrato que ninguna obligación explica: alguien acreditó una a mano, y ese dinero no lo va a devengar nadie. Ningún total se rompe por esto — el balance cuadra igual y lo único que sale mal es el ingreso de los meses que vienen.",
  'dif.mismatchUnder': "Faltan {diferencia} en las cuentas de contrato: hay obligaciones cuyo cobro todavía no está asentado, o se devengó de más. Ningún total se rompe por esto — el balance cuadra igual y lo único que sale mal es el ingreso del periodo.",
  'dif.twoLedgers': "Ese pendiente son DOS auxiliares y no uno: {obligaciones} de "
    + "{cuantas} obligaciones por calendario, y {proyectos} de {cuantos} proyectos por "
    + "avance. Se suman acá porque comparten las cuentas de contrato, pero se arreglan "
    + "en dos sitios: las obligaciones en esta pantalla y los proyectos en la de Proyectos.",
  'dif.projectsOut': "Hay {cuantos} proyecto(s) con la cuenta de contrato en el ACTIVO: "
    + "su saldo no está en el diferido, así que quedan fuera de esta conciliación. No es "
    + "un error —se reconoció antes de facturar— pero esta cifra no los cubre.",
  'dif.nonCurrent': "De ese pendiente, {importe} vence a más de doce meses: en el balance "
    + "corresponde al pasivo NO corriente. Hoy hay que reclasificarlo a mano.",
  'dif.monthTitle': "El devengo del mes",
  'dif.period': "Periodo",
  'dif.obligation': "Obligación",
  'dif.term': "Plazo",
  'dif.termOf': "mes {mes} de {total}",
  'dif.lastMonth': "último mes",
  'dif.quota': "Cuota",
  'dif.total': "Total del mes",
  'dif.doIt': "Devengar el mes",
  'dif.twiceIsFine': "Correrlo dos veces no duplica: borra el asiento anterior y lo rehace.",
  'dif.doneNone': "No había nada que devengar en ese mes.",
  'dif.doneOkOne': "Devengada 1 obligación · asiento {asiento}.",
  'dif.doneOk': "Devengadas {obligaciones} obligaciones · asiento {asiento}.",
  'dif.noneAtAll': "Esta empresa no tiene ingresos cobrados por adelantado.",
  'dif.noneThisMonth': "Ninguna obligación tiene cuota en este mes.",
  'dif.listTitle': "Las obligaciones",
  'dif.add': "Agregar",
  'dif.addHelp': "El camino normal es la factura de venta: al facturar por anticipado se "
    + "dice en cuántos meses se gana y la obligación nace con ella. Este formulario es "
    + "para lo que viene de antes —una apertura— o para un cobro que no pasó por una "
    + "factura.",
  'dif.description': "Descripción",
  'dif.customer': "Cliente",
  'dif.accountDeferred': "Cuenta de contrato",
  'dif.onlyLiabilities': "solo pasivos",
  'dif.accountIncome': "Cuenta de ingreso",
  'dif.onlyIncome': "solo ingresos",
  'dif.docDate': "Fecha del documento",
  'dif.start': "Se devenga desde",
  'dif.amount': "Importe",
  'dif.months': "Meses",
  'dif.monthsOf': "{meses} meses",
  'dif.pending': "Pendiente",
  'dif.save': "Guardar",
  'dif.end': "Dar de baja",
  'dif.ended': "dada de baja",
  'dif.endNote': "Dar de baja detiene el devengo desde el mes siguiente — el mes de la "
    + "baja se devenga entero— y NO reconoce el pendiente como ingreso: que la empresa "
    + "tenga derecho a quedarse con lo cobrado es un juicio del contrato, no un hecho del "
    + "calendario. Para cambiar un contrato se da de baja éste y se abre otro.",
  'act.title': "Activos",
  'act.sub': "Los bienes que se deprecian, y la depreciación del mes. El cálculo sale del registro de cada bien, no de un archivo aparte.",
  'act.monthTitle': "La depreciación del mes",
  'act.period': "Periodo",
  'act.asset': "Bien",
  'act.life': "Mes de vida",
  'act.lifeOf': "mes {mes} de {total}",
  // ▶ Se marca porque el último NO lleva la cuota: lleva lo que falta, para que
  // la acumulada cierre exacta contra el costo. Sin la marca, el número cambia
  // el último mes y parece un error.
  'act.lastMonth': "último — lleva lo que falta",
  'act.quota': "Cuota",
  'act.total': "Total del mes",
  'act.doIt': "Depreciar este mes",
  'act.twiceIsFine': "Se puede correr dos veces: la segunda reemplaza el asiento de la primera, no lo suma.",
  'act.doneOk': "Listo. {activos} bienes depreciados en el asiento #{asiento}.",
  'act.doneOkOne': "Listo. Un bien depreciado en el asiento #{asiento}.",
  'act.doneNone': "No había nada que depreciar en este mes.",
  'act.noneAtAll': "Todavía no hay ningún bien registrado.",
  'act.noneThisMonth': "Hay bienes registrados, pero ninguno se deprecia en este mes: o todavía no entraron en servicio, o ya agotaron su vida.",
  'act.listTitle': "Bienes registrados",
  'act.add': "Registrar un bien",
  'act.addHelp': "Cada bien dice sus tres cuentas: la del bien, la de depreciación acumulada y la del gasto. Un vehículo y un edificio no van contra las mismas, y por eso no las elige el sistema.",
  'act.description': "Descripción",
  'act.accountAsset': "Cuenta del bien",
  // Dice qué se ofrece, porque la lista está filtrada: sin esto, alguien busca
  // una cuenta que sí existe y no la encuentra, y no sabe por qué.
  'act.onlyAssets': "Sólo cuentas de activo",
  'act.onlyExpenses': "Sólo cuentas de gasto",
  'act.accountAccum': "Depreciación acumulada",
  'act.accountExpense': "Gasto de depreciación",
  'act.bought': "Fecha de compra",
  'act.start': "Empieza a depreciar",
  'act.cost': "Costo",
  'act.lifeMonths': "Vida (meses)",
  'act.taxLife': "Vida fiscal (meses)",
  'act.taxLifeSame': "igual que la contable",
  'act.taxLifeIs': "fiscal {meses} m",
  'act.residual': "Valor residual",
  'act.department': "Departamento",
  'act.save': "Registrar",
  'act.retired': "dado de baja",
  // ── La conciliación bancaria (0066) ────────────────────────────────────
  'ban.title': "Banco",
  'ban.sub': "El extracto del banco al lado del libro. Lo que no empareja es el resultado, no un error: tiene nombre contable y se registra.",
  'ban.account': "Cuenta de banco",
  'ban.onlyAssets': "Sólo cuentas de activo",
  'ban.accountsTitle': "Las cuentas del banco",
  'ban.inDefault': "Depósito en la cuenta",
  'ban.outDefault': "Pago desde la cuenta",
  'ban.record': "Registrar movimiento",
  'ban.recorded': "Movimiento registrado · asiento {asiento}.",
  'ban.direction': "Entra o sale",
  'ban.amountWhy': "en la moneda de la cuenta, tal como lo movió el banco",
  'ban.against': "Contra qué cuenta",
  'ban.againstWhy': "de dónde vino el dinero, o a dónde fue. Un depósito de un cliente va contra su cuenta por cobrar; uno de un socio, contra la suya.",
  'ban.currencyMismatch': "Esa cuenta está declarada en otra moneda. Para mover entre dos monedas usá Transferir, que pide los dos importes —el que sale y el que entra— y reconoce la diferencia de cambio.",
  'ban.reference': "Referencia",
  'ban.referenceHint': "número de documento, comprobante o transferencia",

  'ban.declareAccount': "Declarar cuenta",
  'ban.transfer': "Transferir",
  'ban.bank': "Banco",
  'ban.number': "Número",
  'ban.numberWhy': "tal como lo dice el banco: es para reconocerla en un estado de cuenta",
  'ban.holder': "Titular",
  'ban.currency': "Moneda",
  'ban.undeclared': "sin declarar",
  'ban.noCash': "El catálogo no declara ninguna cuenta de efectivo.",
  'ban.save': "Guardar",
  'ban.declared': "Cuenta declarada.",
  'ban.from': "Sale de",
  'ban.to': "Entra a",
  'ban.leaves': "Lo que sale",
  'ban.arrives': "Lo que entra",
  'ban.arrivesWhy': "en la moneda de destino, tal como lo acreditó el banco. La diferencia "
    + "contra el tipo del día se reconoce como diferencial de cambio.",
  'ban.transferred': "Transferencia registrada · asiento {asiento}.",

  'ban.period': "Periodo",
  'ban.pickHelp': "Elija la cuenta de banco que va a conciliar. Cada cuenta corriente se concilia contra su propio extracto.",
  'ban.pickHelpSome': "Elija la cuenta que va a conciliar. Con extracto cargado: {cuentas}.",
  'ban.balanceTitle': "El cuadre",
  'ban.perBooks': "Saldo según libros",
  'ban.creditNotes': "Notas de crédito no registradas",
  'ban.debitNotes': "Notas de débito no registradas",
  'ban.adjustedBooks': "Saldo ajustado del libro",
  'ban.perBank': "Saldo según el estado bancario",
  'ban.inTransit': "Depósitos en tránsito",
  'ban.outstandingChecks': "Cheques girados y no cobrados",
  'ban.adjustedBank': "Saldo ajustado del banco",
  'ban.notDeclared': "sin declarar",
  'ban.cannotSquare': "Sin el saldo del estado bancario no hay cuadre posible. Declárelo abajo: es el único dato de la conciliación que no está en el sistema.",
  'ban.squares': "Cuadra.",
  'ban.doesNotSquare': "No cuadra. Diferencia:",
  'ban.declareBalance': "Saldo del estado bancario",
  'ban.declare': "Declarar",
  'ban.declareHelp': "El saldo final que dice el papel del banco. No se deduce del extracto importado: ese número le faltaría todo lo anterior al primer archivo que se subió.",
  'ban.importTitle': "Traer el estado de cuenta",
  'ban.importHelp': "El CSV tal como lo exporta el banco. Las columnas se reconocen solas —fecha, descripción, crédito y débito, con sus nombres en español o en inglés— y lo que el banco llama *crédito* es plata que entró a la cuenta.",
  'ban.row': "fila {n}",
  'ban.signIn': "El positivo es plata que entró",
  'ban.signOut': "El positivo es plata que salió",
  'ban.signInUsed': "Leído como: positivo entra.",
  'ban.signOutUsed': "Leído como: positivo sale.",
  'ban.readOk': "{filas} filas leídas, del {desde} al {hasta}.",
  'ban.dateGuessed': "Ninguna fila demuestra el orden de la fecha —no hay ningún día mayor que 12—, así que se leyó dd/mm/aaaa. Si su banco exporta mm/dd/aaaa, revise las fechas de abajo antes de importar.",
  'ban.date': "Fecha",
  'ban.description': "Descripción",
  'ban.amount': "Importe",
  'ban.entry': "Asiento",
  'ban.in': "Entró",
  'ban.out': "Salió",
  'ban.andMore': "y {n} más.",
  'ban.import': "Importar {n} filas",
  'ban.twiceIsFine': "Se puede subir el mismo archivo dos veces: las filas repetidas no entran otra vez.",
  'ban.importOk': "Listo. {nuevas} filas nuevas, {repetidas} que ya estaban.",
  'ban.pendingTitle': "Lo pendiente",
  'ban.tolerance': "Días",
  'ban.matchAuto': "Emparejar lo evidente",
  'ban.matchAutoHelp': "Empareja sólo los pares que no admiten duda: mismo importe, misma dirección y un único candidato de los dos lados. Dos pagos iguales la misma semana quedan para usted, a propósito.",
  'ban.matchOk': "{hechos} emparejados. {ambiguos} filas con más de un candidato, que quedan acá.",
  'ban.sideBooks': "En el libro y no en el banco",
  'ban.sideBank': "En el banco y no en el libro",
  'ban.nothingPending': "Nada pendiente.",
  'ban.noStatement': "Todavía no se ha traído ningún estado de cuenta de esta cuenta.",
  'ban.matchPair': "Emparejar los dos marcados",
  'ban.matchPairHelp': "Marque una fila de cada lado. Los importes tienen que ser el mismo: un depósito al que el banco le cobró comisión son dos hechos, no uno.",
  'ban.post': "Registrar",
  'ban.postTitle': "El asiento que falta",
  'ban.willDebit': "Va al DEBE de {cuenta} y al haber de la contrapartida.",
  'ban.willCredit': "Va al HABER de {cuenta} y al debe de la contrapartida.",
  'ban.counterpart': "Contrapartida",
  'ban.counterpartHelp': "La elige usted. La misma palabra en el extracto es un gasto en una empresa y una cuenta por cobrar en otra, así que el sistema no la adivina. La fecha del asiento es la del banco, no la de hoy.",
  'ban.memo': "Glosa",
  'ban.postDo': "Registrar y emparejar",
  'ban.postOk': "Listo. Asiento #{asiento}, y la fila quedó emparejada con él.",
  'ban.matchedTitle': "Ya conciliado",
  'ban.show': "Ver",
  'ban.hide': "Ocultar",
  'ban.nothingMatched': "Nada conciliado todavía en este mes.",
  'ban.unmatch': "Deshacer",
  'ban.rows': "{n} filas",
  'ban.rowsOne': "1 fila",
  'ban.noStatementYet': "Todavía no hay ningún estado de cuenta de esta cuenta, así que no hay nada que conciliar: lo que el libro tiene no son partidas conciliatorias, es el mayor. Traiga el extracto abajo.",
  'ban.noCashAccounts': "El catálogo de esta empresa no declara ninguna cuenta de efectivo, así que no hay contra qué conciliar. Una cuenta de banco se declara en el catálogo como efectivo: no se adivina por ser de activo —la lista anterior incluía depreciación acumulada—.",
  'ban.beforeMatching': "· antes de emparejar",
  'ban.noneMatchedYet': "Todavía no hay ninguna fila emparejada, así que los dos saldos ajustados cuentan cada partida en los dos lados a la vez y no se pueden comparar. Empareje abajo —automático o a mano— y esta línea dirá si cuadra. ({filas} fila(s) en el extracto.)",
  'ban.matchedCount': "{hechas} de {filas} fila(s) del extracto emparejadas.",
  // Por qué no se pudo leer el archivo del banco. Ver `nucleo/extracto.ts`.
  'ex.err.sin_filas': "El archivo no tiene ninguna fila debajo de la cabecera.",
  'ex.err.falta_fecha': "No encuentro la columna de la fecha. Estas son las columnas que vinieron:",
  'ex.err.falta_importe': "No encuentro las columnas del importe. Hacen falta crédito y débito, o una sola de monto. Estas son las que vinieron:",
  'ex.err.solo_monto': "El archivo trae una sola columna de importe, con signo. Dígame qué significa el positivo: no se puede adivinar, porque las dos lecturas cargan igual de bien.",
  'ex.err.fecha_ilegible': "Esa fecha no la puedo leer. Se aceptan aaaa-mm-dd y dd/mm/aaaa.",
  'ex.err.fechas_contradictorias': "El archivo mezcla los dos órdenes de fecha: unas filas sólo tienen sentido como dd/mm y otras sólo como mm/dd. Elegir uno correría de mes la mitad de los movimientos.",
  'ex.err.importe_ilegible': "Ese importe no lo puedo leer.",
  'ex.err.dos_lados': "Esa fila no dice si entró o salió plata: trae las dos columnas llenas, o las dos vacías.",
  'ex.err.sin_lineas': "El archivo tiene filas, pero ninguna con fecha.",
  'ap.already': "Ya está cargada",
  'ap.alreadyWhy': "Se carga una sola vez: hacerlo dos veces duplicaría el patrimonio y el balance seguiría cuadrando, así que nada avisaría. Una corrección posterior es un asiento de ajuste.",
  'ap.date': "Fecha",
  'ap.dateHint': "El PRIMER día de un periodo del ejercicio. Una apertura fechada el 14 dice que la empresa nació ese día.",
  'ap.file': "Archivo CSV",
  'ap.columns': "Cabecera: cuenta, debe, haber, detalle, customer, project, department.",
  'ap.templateTitle': "Descargue la plantilla",
  // Dice lo que trae y lo que NO trae. Lo segundo importa más: un archivo con
  // ceros cargaría sin quejarse y metería 123 líneas de nada en el asiento.
  'ap.templateWhy': "Trae las cuentas de SU catálogo, una por fila y sin importes. Llene solo las que tengan saldo y borre el resto: una fila en cero afirma que la cuenta arrancó en cero, que no es lo mismo que no tener saldo.",
  'ap.templateGet': "Descargar CSV",
  'ap.paste': "O pegue el CSV",
  'ap.pasteHint': "Lo mismo que el archivo. Sirve para corregir una fila sin volver a exportar.",
  'ap.row': "Fila",
  'ap.err.sin_filas': "El archivo no tiene filas debajo de la cabecera.",
  'ap.err.falta_cuenta': "La cabecera necesita al menos la columna «cuenta». Vino",
  'ap.err.sin_importe': "Esa cuenta no trae ni debe ni haber.",
  'ap.err.sin_lineas': "No hay ninguna fila con cuenta.",
  // ▶ Los conteos van SIEMPRE con lo que cuentan. Aparecieron seis números
  // desnudos en cabeceras de tarjeta —«123», «1», «8»— y en una pantalla de
  // contabilidad un número solo se lee como un código o como un saldo.
  'ap.lineCount': "{lineas} líneas",
  'ap.lineCountOne': "1 línea",
  'ap.preview': "Lo que se va a mandar",
  'ap.account': "Cuenta",
  'ap.debit': "Debe",
  'ap.credit': "Haber",
  'ap.detail': "Detalle",
  'ap.unreadable': "No se puede anticipar el cuadre: hay un importe que no se lee",
  'ap.difference': "Diferencia",
  'ap.previewNote': "Los importes se muestran tal como vinieron. El cuadre es un anticipo; quien decide es la base.",
  'ap.load': "Cargar la apertura",
  'ap.onlyOnce': "Una sola vez, y antes de cualquier otro asiento.",

  // -- E8.2 - El balance del sistema anterior ---------------------------------
  'mp.via': "¿Con qué archivo viene?",
  'mp.viaPlantilla': "La plantilla nuestra",
  // ▶ Decía «es la que se descarga desde el catálogo», y en el catálogo no
  // había ninguna descarga: la plantilla existía sólo como comando nuestro.
  // Una frase perfecta señalando un sitio donde no se puede hacer nada.
  'mp.viaPlantillaWhy': "Ya trae los códigos de cuenta de su catálogo. Se descarga en el paso siguiente.",
  'mp.viaBalance': "El balance de comprobación de mi sistema anterior",
  'mp.viaBalanceWhy': "Viene con SUS códigos de cuenta. Acá se le indica a qué cuenta nuestra corresponde cada una, una sola vez.",
  'mp.file': "Balance de comprobación",
  'mp.columns': "Necesita las dos columnas: código, nombre, debe y haber.",
  'mp.paste': "O pegue el balance",
  'mp.pasteHint': "Lo mismo que el archivo. Sirve para quitar una fila de totales o arreglar una cabecera sin volver a exportar del otro sistema — lo que ya haya resuelto abajo no se pierde.",
  'mp.err.sin_filas': "El archivo no tiene filas debajo de la cabecera.",
  'mp.err.falta_codigo': "No se encontró la columna del código de cuenta. La cabecera trae",
  'mp.err.solo_saldo': "Ese archivo trae una sola columna de saldo, y no se puede saber si un número positivo es debe o haber: cada sistema lo firma distinto y adivinar mal invierte cuentas sin que nada falle. Exporte el balance con las columnas de debe y haber. La cabecera trae",
  'mp.err.sin_importe': "Esa cuenta no trae ni debe ni haber.",
  'mp.err.sin_lineas': "No hay ninguna fila con código de cuenta.",
  'mp.title': "A qué cuenta corresponde cada una",
  'mp.sub': "A la izquierda, sus cuentas tal como vinieron. A la derecha, la nuestra. Se guarda: la próxima vez que suba un archivo, esto ya viene resuelto.",
  'mp.theirs': "Su cuenta",
  'mp.ours': "Nuestra cuenta",
  'mp.pick': "— elegir —",
  'mp.suggested': "sugerida",
  'mp.suggestedWhy': "Las sugerencias solo aparecen cuando el nombre se parece mucho. Revíselas igual: una sugerencia es un ahorro de búsqueda, no una decisión.",
  'mp.mapped': "{cuentas} cuentas mapeadas",
  'mp.mappedOne': "1 cuenta mapeada",
  'mp.pending': "sin resolver",
  'mp.pendingWhy': "Faltan cuentas por corresponder. Las que queden sin cuenta NO entran en la apertura, y su saldo se perdería sin que el asiento dejara de cuadrar.",
  'mp.save': "Guardar las correspondencias",
  'mp.saved': "Guardado",
  'mp.resolved': "resueltas",
  // -- La pregunta de los resultados ------------------------------------------
  'mp.mode': "Su balance trae cuentas de ingreso y de gasto",
  'mp.modeWhy': "Eso pasa cuando el balance es de mitad de año. Hay dos respuestas correctas y la que corresponde depende de lo que usted vaya a hacer, así que la elige usted.",
  'mp.modeClean': "Arranque limpio",
  'mp.modeCleanWhy': "Los ingresos y gastos se netean contra utilidades acumuladas y entra solo el resultado. El sistema arranca sin historia: los estados de resultados de este año empiezan en cero acá y el año completo se declara en el sistema anterior.",
  'mp.modeMid': "Traer el año en curso",
  'mp.modeMidWhy': "Los ingresos y gastos entran tal cual, con su saldo acumulado del año. El estado de resultados de este año queda completo acá desde enero, y el sistema anterior ya no hace falta.",
  'mp.modeNone': "Su balance no trae cuentas de resultado, así que no hay nada que decidir.",
  'mp.willLoad': "Lo que va a entrar en la apertura",
  'mp.dropped': "sin cuenta — no entran",
  'mp.provenance': "Cada línea de la apertura guarda de qué cuenta suya vino, para que dentro de un año se pueda ver.",
  'sb.SB040': "Esta empresa ya tiene su apertura cargada.",
  'sb.SB041': "Ya hay asientos registrados: la apertura va antes que cualquier otro.",
  'sb.SB042': "La fecha tiene que ser el primer día de un periodo del ejercicio.",
  'sb.SB050': "No hay tipo de cambio de esa moneda para esa fecha: esa línea no se puede convertir.",
  'sb.SB060': "El periodo está cerrado. Una corrección se hace con un asiento en el mes siguiente.",
  'sb.SB090': "El estado ya está en esa moneda: no hay nada que convertir.",
  'sb.SB091': "Faltan tipos de cambio para convertir el estado a esa moneda.",
  'sb.SB100': "Ese valor no está en el vocabulario analítico de esa dimensión.",
  /* ── El cierre mensual ────────────────────────────────────────────────
     Los nombres de los diez pasos viven ACÁ y no en la base: son rótulos, y
     un rótulo que sale de la base llega en un solo idioma. Lo que sí sale de
     la base es cuál bloquea y cuál se puede firmar, que son reglas. */
  'idx.motivo.sin_modalidad': "La dimensión «modalidad» no está declarada en el catálogo: este índice no se puede separar de su gemelo.",
  'idx.motivo.sin_ingreso_neto': "No hay ingreso neto en el periodo: un porcentaje sobre cero no existe.",
  'idx.motivo.sin_gasto_de_area': "No hay gasto del área en el periodo: el índice necesita un denominador.",
  'idx.motivo.opex_sin_modalidad': "Hay gasto del área de soporte en el periodo sin la modalidad puesta. Si se calculara, faltaría ese gasto en el denominador y el índice saldría mejor de lo que es.",
  'idx.motivo.mantenimiento_sin_modalidad': "Hay mantenimiento en el periodo sin la modalidad puesta. Se dejaría fuera del numerador y el índice saldría peor de lo que es.",
  'cierre.title': "Cierre del mes",
  'cierre.sub': "Los diez pasos. El sistema comprueba los que puede; los demás los firma una persona, y consta quién.",
  'cp.title': "Clientes y proveedores",
  'doc.title': "Cuentas por pagar",
  'doc.sub': "Las facturas de sus proveedores. Registrarlas acá escribe el asiento.",
  'doc.new': "Registrar una factura",
  'doc.post': "Registrar y asentar",
  'doc.list': "Facturas por pagar",
  'doc.search': "Buscar",
  'doc.searchHint': "número, proveedor o glosa",
  'doc.onlyOpen': "Solo con saldo",
  'doc.onlyOpenWhy': "Lo pagado no se borra: se destilda esto y vuelve entero.",
  'doc.hidden': "{n} sin saldo, fuera de la lista",
  'doc.count': "{n} en la lista",
  'doc.supplier': "Proveedor",
  'doc.supplierWhy': "Su cuenta habitual y su moneda se aplican solas, y se pueden cambiar antes de registrar.",
  'doc.noSuppliers': "Todavía no hay proveedores activos. El primero se da de alta en Clientes y proveedores.",
  'doc.allSuppliers': "Todos los proveedores",
  'doc.pick': "Elija uno",
  'doc.number': "Número de la factura",
  'doc.numberHint': "el que trae el papel",
  'doc.numberWhy': "Es el número del proveedor, no uno nuestro: uno propio sería un segundo nombre para la misma factura, y él no lo conoce.",
  'doc.date': "Fecha",
  // ▶ El pie de la tabla de documentos (0091). Dice sobre cuántos suma, porque
  // la lista viene recortada: sin el número, un total sobre 100 de 340 facturas
  // se lee como el total de todo.
  'doc.totalN': "Total de {n} documentos vivos",
  'doc.overdueN': "{n} vencidas",
  'doc.due': "Vence",
  'doc.dueWhy': "Vacío significa que el papel no lo dice, no que venza hoy.",
  'doc.dueProposed': "Propuesto con los {n} días de crédito de su ficha. Corríjalo si el papel dice otra cosa.",
  'doc.subtotal': "Subtotal",
  'doc.tax': "Impuesto",
  'doc.taxWhy': "Vacío es cero, y entonces el asiento no lleva línea de impuesto. Hay ventas y compras exentas, y son normales.",
  'doc.currency': "Moneda",
  'doc.currencyWhy': "La de la ficha de la contraparte, si la tiene; si no, la de los libros.",
  'doc.account': "Cuenta del gasto",
  'doc.accountAuto': "la habitual del proveedor",
  'doc.accountWhy': "Sólo ésta se pregunta. La de impuesto y la de por pagar las sabe el sistema, y cuál de las dos por pagar lo decide la moneda.",
  'doc.memo': "Glosa",
  'doc.memoAuto': "se arma con el número y el nombre",
  'doc.total': "Total",
  'doc.entry': "Asiento",
  'doc.overdue': "vencida hace {n} días",
  'doc.posted': "Factura {numero} registrada. Quedó en el asiento #{asiento}.",
  'doc.none': "Todavía no hay ninguna. La primera se registra con el botón de arriba.",
  'doc.noneFound': "Ninguna de ese proveedor.",
  'pay.new': "Registrar un pago",
  'pay.post': "Registrar el pago",
  'pay.amount': "Importe del pago",
  'pay.otherCurrency': "El cliente pagó en otra moneda (depósito en colones)",
  'pay.otherCurrencyWhy': "Para una factura en dólares pagada con un depósito en colones. Cancela la factura a su valor en libros, deja el banco por lo recibido, y la diferencia va a diferencial o a saldo a favor.",
  'pay.received': "Monto recibido en el banco",
  'pay.bankLocalWhy': "La cuenta de banco en colones que recibió el depósito.",
  'pay.invoicesToCancel': "Facturas a cancelar",
  'pay.invoicesToCancelWhy': "Se cancelan enteras: después quedan en cero, en dólares y en colones",
  'pay.amountOwn': "Monto (su moneda)",
  'pay.equivLocal': "En colones",
  'pay.remainsAfter': "Queda después",
  'pay.diffTreatment': "La diferencia entre lo recibido y las facturas",
  'pay.diffFx': "Diferencial cambiario (ganancia o pérdida)",
  'pay.diffCredit': "Saldo a favor del cliente",
  'pay.diffTreatmentWhy': "Un redondeo de céntimos es diferencial; un sobrepago que el cliente espera que se le acredite es saldo a favor.",
  'bal.title': "Saldos de caja y bancos",
  'bal.asOf': "A la fecha",
  'bal.account': "Cuenta",
  'bal.balance': "Saldo",
  'bal.export': "Exportar",
  'bal.none': "No hay cuentas de caja ni banco declaradas.",
  'bal.asOfNote': "Saldo en libros al {fecha}, cada cuenta en su moneda. El de una cuenta en dólares dice cuántos dólares hay, no su valor en colones.",
  'pay.from': "Cuenta de donde sale",
  'pay.fromHint': "banco, caja — o la cuenta por pagar de quien pagó",
  'pay.thirdParty': "¿De quién es esa cuenta?",
  'pay.thirdPartyWhy': "Esa cuenta lleva el nombre de una contraparte, así que no "
    + "basta con decir cuál es: hay que decir a quién se le debe. Es el caso de quien "
    + "pagó de su bolsillo — el proveedor queda pagado y esa persona queda como "
    + "acreedora, con su deuda envejeciendo como cualquier otra.",
  'pay.fromWhy': "El código de la cuenta del catálogo. Es la que baja al pagar.",
  'pay.reference': "Referencia",
  'pay.referenceHint': "cheque, transferencia",
  'pay.referenceWhy': "Va en el detalle de la línea del asiento. No hay una ficha aparte: ni un pago ni un cobro tienen ningún dato que el asiento no guarde ya.",
  'pay.applyTo': "A qué facturas va",
  'pay.applyWhy': "Dejarlo vacío es válido: el informe lo aplica a la más vieja y lo dice.",
  'pay.applyHere': "Aplicar acá",
  'pay.open': "Queda",
  'pay.settled': "saldada",
  'pay.nothingOpen': "A este proveedor no le queda nada abierto.",
  'pay.posted': "Pago registrado en el asiento #{asiento}, aplicado a {n} factura(s).",
  'cob.title': "Cuentas por cobrar",
  'cob.sub': "Las facturas que emitió. Registrarlas acá escribe el asiento.",
  'cob.new': "Registrar una factura",
  'cob.post': "Registrar y asentar",
  'cob.list': "Facturas por cobrar",
  'cob.none': "Todavía no hay ninguna. La primera se registra con el botón de arriba.",
  'cob.noneFound': "Ninguna de ese cliente.",
  'cob.posted': "Factura {numero} registrada. Quedó en el asiento #{asiento}.",
  'cob.customer': "Cliente",
  'cob.customerWhy': "Su cuenta de venta y su moneda se aplican solas, y se pueden cambiar antes de registrar.",
  'cob.noCustomers': "Todavía no hay clientes activos. El primero se da de alta en Clientes y proveedores.",
  'cob.allCustomers': "Todos los clientes",
  'cob.account': "Cuenta del ingreso",
  'cob.accountAuto': "la de venta del cliente",
  'cob.accountWhy': "Sólo ésta se pregunta. La de impuesto y la de por cobrar las sabe el sistema, y cuál de las dos por cobrar lo decide la moneda. El cliente lo pone el sistema en las dos líneas: toda cuenta de ingreso lo exige.",
  'cob.numberWhy': "Es el número que emitió el sistema de Hacienda. Acá se anota lo que se emitió allá; uno propio sería un segundo nombre para la misma factura.",
  'cob.payNew': "Registrar un cobro",
  'cob.payPost': "Registrar el cobro",
  'cob.payPosted': "Cobro registrado en el asiento #{asiento}, aplicado a {n} factura(s).",
  'cob.payFrom': "Cuenta donde entra",
  'cob.payFromWhy': "El código de la cuenta del catálogo. Es la que sube al cobrar.",
  'cob.applyTo': "A qué facturas va",
  'cob.applyWhy': "Dejarlo vacío es válido: el informe lo aplica a la más vieja y lo dice.",
  'cob.nothingOpen': "A este cliente no le queda nada abierto.",
  'cob.noneApplied': "Nadie ha dicho qué cobro canceló esta factura, y la antigüedad tampoco tiene con qué colocarla: no hay ningún cobro de ese cliente sin repartir.",
  'nc.new': "Registrar una nota de crédito",
  'nc.post': "Registrar la nota",
  'nc.number': "Número de la nota",
  'nc.against': "Contra qué factura va",
  'nc.againstWhy': "Obligatorio, y no sólo porque la factura electrónica lo pida: una nota sin referencia es un abono que no dice qué cancela. Por eso mismo se aplica sola a esa factura.",
  'nc.reason': "Motivo",
  'nc.reasonHint': "devolución, descuento pactado",
  'nc.reasonWhy': "Va en el comprobante, y es lo único que explica meses después por qué una factura vale menos de lo que dice.",
  'nc.accountAuto': "la misma de la factura",
  'nc.accountWhy': "La cuenta que se está deshaciendo. Vacío usa la habitual de la contraparte, que es contra la que fue la factura.",
  'nc.chip': "nota contra {numero}",
  'nc.posted': "Nota {numero} registrada. Quedó en el asiento #{asiento} y se aplicó a su factura.",
  'nd.posted': "Nota {numero} registrada. Quedó en el asiento #{asiento}, y suma a lo que se debe.",
  'nc.kind': "Qué hace la nota",
  'nc.credit': "Baja lo que se debe",
  'nc.debit': "Sube lo que se debe",
  'nc.creditWhy': "Una nota de crédito deshace parte de la factura: una devolución, un descuento. Se aplica sola a la factura que dice.",
  'nc.debitWhy': "Una nota de débito cobra de más sobre esa factura: intereses, un flete que no venía. Abre deuda nueva, así que no se aplica a nada y sí puede tener vencimiento.",
  'ap.new': "Repartir un abono",
  'ap.why': "Para lo que se asentó a mano y nadie colocó",
  'ap.post': "Aplicar",
  'ap.which': "Qué abono",
  'ap.whichWhy': "Salen los que todavía tienen algo sin repartir. Es lo que la columna «se adivina» del informe cuenta: acá se deja de adivinar.",
  'ap.none': "No hay ningún abono sin repartir. Todo lo que se pagó ya dice a qué factura fue.",
  'ap.done': "Aplicado a {n} factura(s). El informe deja de adivinar esa parte.",
  'pay.loading': "Buscando…",
  'pay.noneApplied': "Nadie ha dicho qué pago canceló esta factura, y la antigüedad tampoco tiene con qué colocarla: no hay ningún pago de ese proveedor sin repartir.",
  'pay.placed': "lo coloca la antigüedad",
  'pay.undo': "Deshacer",
  'pay.undoWhy': "¿por qué?",
  'pay.undoConfirm': "Confirmar",
  'pay.undone': "deshecha: {motivo}",
  'pay.undoneOk': "Aplicación deshecha. No se borró: quedó la contraria, con su motivo.",
  'cp.sub': "Quién le compra y a quién le compra. El código de cada uno es lo que viaja dentro de cada asiento suyo.",
  'cp.new': "Nueva contraparte",
  'cp.list': "Contrapartes",
  'cp.count': "{n} en la lista",
  'cp.name': "Nombre",
  'cp.nameHint': "Como aparece en su factura",
  'cp.code': "Código",
  'cp.codeAuto': "se deriva del nombre",
  'cp.codeWhy': "Déjelo vacío y la base lo arma con el nombre. Es lo que viaja escrito en cada movimiento.",
  'cp.codeLocked': "No se cambia: está escrito dentro de cada asiento suyo, y cambiarlo los dejaría apuntando a nadie.",
  'cp.role': "Papel",
  'cp.role.all': "Clientes y proveedores",
  'cp.role.cliente': "Solo clientes",
  'cp.role.proveedor': "Solo proveedores",
  'cp.isCustomer': "Es cliente",
  'cp.isSupplier': "Es proveedor",
  'cp.roleWhy': "Puede ser las dos cosas: se le compra y se le vende. Al menos una hace falta, o no sale en ninguna antigüedad.",
  'cp.customer': "Cliente",
  'cp.supplier': "Proveedor",
  'cp.idType': "Tipo de identificación",
  'cp.idNone': "No tiene o no la dio",
  'cp.id': "Identificación",
  'cp.idWhy': "Se guarda como usted la escribe, y la forma canónica la calcula la base. Un proveedor de otro país va como Extranjera, con su número y su país — «US EIN 27-0805829»: dos números iguales de países distintos no son el mismo contribuyente, y por eso la base no empareja los extranjeros entre sí. Uno que no la da —Meta, Google— se deja sin identificación: el campo es opcional a propósito."
  ,
  // ▶ La moneda dejó de ser un campo de texto de tres letras (0094).
  /* ▶ La moneda de una ficha vieja cuyo tipo de cambio ya no se publica. Sale
     en la lista igual, rotulada, porque borrarla del desplegable haría que
     abrir esa ficha y guardarla le quitara la moneda a un documento que sí la
     tenía — un campo no puede perder un dato por el hecho de mirarlo. */
  'cur.gone': "ya no se publica",
  'doc.currencyFromParty': "la de la ficha, o la de los libros",
  'cfg.currencyNone': "sin elegir",
  'op.currencyNone': "sin elegir",
  'cp.email': "Correo",
  'cp.emailNone': "sin declarar",
  /* ▶ La pista dice PARA QUÉ sirve, no qué es un correo. Un campo de contacto
     en una ficha contable parece decorativo hasta que alguien entiende que de
     ahí sale el receptor del comprobante electrónico. */
  'cp.emailWhy': "A dónde se le escribe. De acá va a salir el receptor del comprobante electrónico, así que conviene que sea el de facturación y no el de alguien en particular. Se guarda en minúsculas: «Info@X.com» e «info@x.com» son el mismo buzón.",
  'cp.currencyLocal': "la de los libros",
  'cp.currencyWhy': "Sólo aparecen las monedas en que esta empresa puede operar: la de los libros y las que tienen tipo de cambio publicado. Una moneda sin tipo produce facturas que no se pueden registrar.",
  'cp.credit': "Días de crédito",
  'cp.creditNone': "sin declarar",
  'cp.creditWhy': "Vacío no es cero: «nadie lo declaró» y «paga de contado» son dos cosas distintas.",
  'cp.currency': "Moneda habitual",
  /* ▶ DECÍA «la de los libros», Y ASÍ SE LEÍA IGUAL QUE LA OPCIÓN `CRC`.
     Con el campo hecho desplegable (0094) las dos quedaron una debajo de la
     otra: «la de los libros» y «CRC · la de los libros». Y NO son lo mismo:
     vacío es `null` —nadie lo declaró— y `CRC` es alguien diciendo que a este
     proveedor se le paga en colones. La `0080` lo dejó escrito para las tres
     columnas de identificación y para los días de crédito; el rótulo de este
     campo se había quedado con el de cuando era un `<input>` con `placeholder`,
     donde no había ninguna otra opción al lado con que confundirse. */
  'cp.currencyNone': "sin declarar",
  'cp.account': "Cuenta habitual del gasto",
  'cp.accountSale': "Cuenta habitual del ingreso",
  'cp.accountSaleWhy': "El código del catálogo contra el que suele ir lo que se le vende. Son dos cuentas y no una porque a alguien se le puede comprar Y vender.",
  'cp.accountNone': "sin declarar",
  'cp.accountWhy': "El código del catálogo contra el que suele ir lo que se le compra. Se aplica sola al registrarle una factura, y la factura puede cambiarla. Sin ella, cada factura suya hay que decirle a mano contra qué cuenta va.",
  'cp.note': "Nota",
  'cp.balance': "Saldo",
  'cp.inFavour': "a favor",
  'cp.edit': "Editar",
  'cp.editing': "Editando {codigo}",
  'cp.save': "Guardar",
  'cp.create': "Dar de alta",
  'cp.created': "{nombre} queda dada de alta, con el código {codigo}.",
  'cp.saved': "{nombre} queda guardada.",
  'cp.activate': "Activar",
  'cp.deactivate': "Desactivar",
  'cp.inactive': "inactiva",
  'cp.unnamed': "falta nombrarla",
  'cp.none': "Todavía no hay ninguna. La primera se da de alta antes de registrarle una factura.",
  'cp.noneFound': "Ninguna con ese filtro.",
  'cp.search': "Buscar",
  'cp.searchHint': "nombre, código o cédula",
  'cierre.period': "Periodo",
  'cierre.exercises': "Ejercicios",
  'cierre.exercisesManage': "Administrar ejercicios · abrir o cerrar un año",
  'cierre.exercisesWhy': "Abrir un año es decidir que se puede escribir en él. Queda en la bitácora.",
  'cierre.exerciseYear': "Año",
  'cierre.exerciseState': "Estado",
  'cierre.exerciseEntries': "Asientos",
  'cierre.exerciseFuture': "sin abrir",
  'cierre.exerciseOpenN': "{n} abiertos",
  'cierre.exerciseClosedN': "{n} cerrados",
  'cierre.exerciseFutureN': "{n} sin abrir",
  // ▶ El cierre anual (0092). El aviso dice el NÚMERO DEL ASIENTO y no
  // «listo»: el cierre no es un trámite, es un asiento con sus líneas, y quien
  // lo corre tiene derecho a ir a verlo.
  'cierre.closeYear': "Cerrar {anio}",
  'cierre.yearClosed': "cerrado",
  'cierre.yearClosedOk': "Ejercicio {anio} cerrado. El resultado del año quedó en patrimonio, en el asiento #{n}.",
  'cierre.openExercise': "Abrir {anio}",
  'cierre.exerciseOpened': "Ejercicio {anio} abierto: {n} periodos quedaron listos para registrar.",
  'cierre.paso.1': "Asientos del mes cuadrados y contabilizados",
  'cierre.paso.2': "Tipos de cambio del BCCR completos para el mes",
  'cierre.paso.3': "Ajuste por diferencial cambiario",
  'cierre.paso.4': "Devengo de ingresos diferidos",
  'cierre.paso.5': "Reconocimiento de servicios profesionales por avance",
  'cierre.paso.6': "Depreciación y amortización",
  'cierre.paso.7': "Provisión de impuesto de renta",
  'cierre.paso.8': "Estimación de deterioro de cuentas por cobrar",
  'cierre.paso.9': "Congelar los índices del mes",
  'cierre.paso.10': "Bloquear el periodo",
  'cierre.paso.11': "Impuesto de renta diferido · NIC 12",
  'cierre.estado.cumplido': "Comprobado",
  'cierre.estado.afirmado': "Firmado",
  'cierre.estado.pendiente': "Pendiente",
  'cierre.estado.no_aplica': "No aplica",
  'cierre.motivo.por_construccion': "Garantizado por la base: un asiento descuadrado no se guarda.",
  'cierre.motivo.sin_moneda_extranjera': "La empresa no tiene partidas en moneda extranjera.",
  'cierre.motivo.faltan_tipos': "Faltan tipos de cambio",
  'cierre.motivo.falta_revaluar': "Hay que correr la revaluación antes de cerrar.",
  'cierre.motivo.sin_activos': "La empresa no tiene activos que depreciar en este mes.",
  'cierre.motivo.sin_obligaciones': "Esta empresa no tiene ingresos cobrados por adelantado en este periodo.",
  'cierre.motivo.falta_devengar': "Hay ingresos diferidos con cuota de este mes y todavía no se devengaron.",
  'cierre.motivo.sin_utilidad': "El ejercicio no tiene utilidad hasta este mes, así que no hay impuesto que provisionar.",
  'cierre.motivo.falta_declarar_tasa': "Esta empresa no declaró su tarifa de renta. Se declara en Configuración — el producto no trae ninguna porque depende del tramo de ingresos brutos de cada empresa.",
  'cierre.motivo.falta_provisionar_renta': "Hay utilidad y la provisión de renta todavía no se asentó.",
  'cierre.motivo.sin_cartera': "Esta empresa no tiene cuentas por cobrar abiertas en este corte.",
  'cierre.motivo.matriz_sin_confirmar': "La matriz de deterioro todavía es la propuesta del producto y nadie la ha confirmado. Se revisa en Configuración.",
  'cierre.motivo.falta_provisionar_deterioro': "La estimación por deterioro no está en su objetivo y el ajuste todavía no se asentó.",
  'cierre.motivo.sin_proyectos': "Esta empresa no tiene proyectos que se reconozcan por avance.",
  'cierre.motivo.falta_declarar_avance': "Hay proyectos sin avance declarado. El avance no sale de los libros — lo declara quién conoce el proyecto, en la pantalla de Proyectos.",
  'cierre.motivo.falta_reconocer_avance': "El avance está declarado y el ingreso todavía no se reconoció.",
  'cierre.motivo.falta_depreciar': "Hay que correr la depreciación en /activos antes de cerrar.",
  'cierre.motivo.al_cerrar': "Ocurre al cerrar, en la misma transacción.",
  'cierre.motivo.sin_diferencias': "Esta empresa no tiene ninguna diferencia temporaria declarada para el año, así que no hay impuesto diferido que reconocer. Se declaran en Provisiones: las bases fiscales no salen del libro — dependen de la ley, y deducirlas sería inventarlas.",
  'cierre.motivo.falta_provisionar_diferido': "Hay diferencias declaradas y su efecto todavía no está asentado. El botón asienta la DIFERENCIA contra lo que ya hay, no el total otra vez.",
  'cierre.motivo.firmado_no_comprobado': "Firmado por una persona, no comprobado por el sistema.",
  'cierre.motivo.necesita_modulo': "Necesita el módulo correspondiente; hoy se firma.",
  'cierre.blocks': "bloquea el cierre",
  'cierre.sign': "Firmar",
  'cierre.signWhy': "Firmar es decir «esto lo hice», con su nombre y su hora. No es una comprobación, y por eso solo se puede en los pasos que el sistema no puede comprobar.",
  'cierre.note': "Nota",
  'cierre.revalue': "Correr la revaluación",
  'cierre.provisionTax': "Provisionar la renta",
  'cierre.provisionDeferred': "Asentar el impuesto diferido",
  'cierre.doneDeferred': "Impuesto diferido asentado",
  'cierre.recogniseProgress': "Reconocer el avance",
  'cierre.doneProgress': "Avance reconocido",
  'cierre.provisionBad': "Ajustar la estimación",
  'cierre.provisionOk': "{importe} · asiento {asiento}",
  'cierre.provisionNone': "ya estaba en su objetivo, no hizo falta asiento",
  'cierre.revalueWhy': "Ajusta las partidas monetarias al tipo del último día del mes. Se puede correr de nuevo: rehace el asiento en vez de duplicarlo.",
  'cierre.close': "Cerrar el periodo",
  'cierre.closed': "Periodo cerrado",
  'cierre.reopen': "Reabrir",
  'cierre.reopenWhy': "Al reabrir se borran las firmas: se hicieron sobre un mes que va a cambiar.",
  'cierre.doneCount': "{lineas} líneas ajustadas",
  'cierre.doneNone': "no había nada que ajustar",
  'cierre.done': "Revaluación corrida",
  'cierre.doneTax': "Renta provisionada",
  'cierre.doneBad': "Estimación ajustada",
  'cierre.export': "Lo que se lleva del mes",
  'cierre.exportWhy': "Los mismos archivos que los reportes, ya del periodo que está cerrando. Exportar es LEER: una empresa suspendida los sigue teniendo.",
  'cierre.exportOpen': "El mes no está cerrado: estos números todavía pueden cambiar",
  'cierre.groupAction': "Requieren tu acción",
  'cierre.groupDone': "Resueltos y lo que no aplica",
  'cierre.progress': "Faltan {n} de {m} pasos que bloquean el cierre",
  'cierre.ready': "Todo listo: ya podés cerrar el mes",
  'cierre.avanceReady': "El avance de los proyectos está declarado y listo para reconocer. Reconocelo abajo para poder cerrar el mes.",
  'cierre.avanceBacklog': "Backlog reportó {n} proyecto(s) en avance, {total} por reconocer. Reconocelo abajo para poder cerrar el mes.",
  'cierre.avanceProject': "Proyecto",
  'cierre.avanceAmount': "Por reconocer",
  'cierre.fromBacklog': "de Backlog",
  'cierre.atClose': "al cerrar",
  'sb.SB061': "No existe ese periodo en el ejercicio.",
  'sb.SB070': "Falta el tipo de cambio del último día del mes: la revaluación no corre a medias.",
  'sb.SB071': "Hay cuentas que se revalúan sin categoría NIIF 18. Hay que asignarla antes.",
  'sb.SB072': "No hay cuenta de resultado para el diferencial de esa categoría.",
  'sb.SB080': "El periodo no se puede cerrar todavía: quedan pasos que bloquean.",
  'sb.SB081': "Ese paso lo comprueba el sistema: no se puede firmar.",
  'sb.SB082': "El periodo ya está cerrado.",
  // ── La gente de la empresa (E8.5) ────────────────────────────────────────
  'us.title': "Usuarios de la empresa",
  'us.sub': "Quién entra a estos libros, y con qué permiso. Lo administra la empresa: no hay que pedírnoslo.",
  'us.invite': "Invitar a alguien",
  'us.email': "Correo",
  'us.emailHint': "El mismo con el que va a entrar. Le llega un enlace para poner su contraseña.",
  'us.name': "Nombre",
  'us.role': "Rol",
  'us.pickRole': "— elegir —",
  'us.roleHint': "Administrador agrega y quita gente. Contador registra y cierra. Solo lectura mira y exporta.",
  'us.send': "Invitar",
  'us.invited': "Invitación enviada. La persona ya es miembro; el correo le llega con el enlace para poner su contraseña.",
  'us.noMail.sin_llave': "Quedó como miembro, pero el correo NO salió: falta configurar el envío. Puede entrar usando «Olvidé mi contraseña».",
  'us.noMail.ya_existe': "Quedó como miembro. Ya tenía cuenta en Books, así que no se le mandó un correo nuevo: entra con su contraseña de siempre.",
  'us.noMail.rechazada': "Quedó como miembro, pero el correo NO salió. Puede entrar usando «Olvidé mi contraseña».",
  'us.who': "Quiénes entran",
  // Igual que en el catálogo: la esquina de la tarjeta llevaba el número solo.
  'us.count': "{personas} personas",
  'us.countOne': "1 persona",
  'us.person': "Persona",
  'us.since': "Desde",
  'us.you': "usted",
  'us.pending': "sin entrar",
  'us.pendingWhy': "Es miembro, pero todavía no ha entrado ni una vez. Si el correo se perdió, puede entrar con «Olvidé mi contraseña».",
  'us.remove': "Quitar",
  'us.lastAdmin': "único administrador",
  'us.lastAdminWhy': "Si se va, la empresa se queda sin nadie que pueda invitar — ni siquiera a otro administrador. Nombre a otro primero.",
  'us.foot': "Quitar a alguien le quita el acceso, no borra lo que registró: su nombre sigue en la bitácora y en cada asiento suyo.",
  'sb.sin_enlace': "Su cuenta entró bien, pero Books todavía no la reconoce. Su administrador tiene que agregarla con este mismo correo.",
  'sb.sin_membresia': "No tiene acceso a esa empresa. Elija otra en el selector, o pídale a su administrador que lo agregue.",
  'sb.usuario_retirado': "Su usuario está desactivado. Su administrador puede volver a activarlo.",
  'sb.sin_token': "Su sesión venció. Vuelva a entrar.",
  'sb.sin_empresa': "No hay una empresa seleccionada. Vuelva al selector y elija en cuál trabajar.",
  // ▶ Cuando sale alguno de estos dos, el defecto es NUESTRO y no de quien lo
  // usa. Por eso el texto lo dice: la persona no puede arreglarlo, así que lo
  // único útil es que sepa que no es culpa suya y tenga algo que contar.
  'sb.faltan_datos': "La pantalla mandó una petición incompleta. Es un defecto nuestro, no algo que usted haya hecho mal: vuelva a intentarlo y, si sigue igual, cuéntenos en qué pantalla y qué estaba haciendo.",
  'sb.accion_desconocida': "La pantalla pidió una operación que el servidor no reconoce. Suele ser una versión vieja cargada en el navegador: recargue la página. Si sigue igual, es un defecto nuestro.",
  // ▶ Éste sí lo puede corregir quien lo ve, así que dice QUÉ forma espera.
  'sb.fecha_invalida': "Esa fecha no tiene la forma que hace falta: se escribe con el año, el mes y el día, en ese orden y con guiones — aaaa-mm-dd.",
  'sb.SB113': "Hay que decir con qué rol entra esa persona.",
  'sb.SB115': "Ese correo no está en Books todavía.",
  'sb.SB116': "Ése es el correo con el que se demuestra el producto: no administra.",
  'sb.SB130': "Hay que ser administrador de la empresa para administrar su gente.",
  // ▶ Los tres actos que compartían `SB130` y no administran gente. Ver la
  // `0090`: un código con cuatro significados sólo puede tener una frase, y las
  // otras tres salen falsas y creíbles.
  'sb.SB227': "Hay que ser administrador de la empresa para abrir un ejercicio.",
  'sb.SB228': "Hay que ser administrador de la empresa para cerrar un ejercicio.",
  'sb.SB229': "Hay que ser administrador de la empresa para declarar que no hay saldos anteriores.",
  'sb.SB131': "Ese rol no existe. Son tres: administrador, contador y solo lectura.",
  'sb.SB132': "La empresa se quedaría sin ningún administrador, y entonces nadie podría volver a invitar. Nombre a otro antes de quitar o rebajar a éste.",
  'sb.SB133': "Esa persona existe en Books, pero no es miembro de esta empresa.",
  'sb.SB120': "El mapeo tiene que venir como una lista de correspondencias.",
  'sb.SB121': "Esa forma de tratar las cuentas de resultado no existe. Son dos: arranque limpio o traer el año en curso.",
  'sb.SB160': "Esa cuenta no recibe movimientos: o es de mayor, o está desactivada, o no existe en el catálogo.",
  'sb.SB161': "Una fila del extracto no dice si entró o salió plata. Una fila con las dos columnas llenas, o con las dos vacías, no afirma ningún movimiento.",
  'sb.SB162': "Ese movimiento del libro no existe, o no es de la cuenta que se está conciliando.",
  'sb.SB163': "El importe del banco y el del libro no son el mismo. Un depósito al que el banco le cobró comisión son DOS hechos: se emparejan por separado y la comisión se registra.",
  'sb.SB164': "Eso ya estaba conciliado. Actualice la pantalla para ver con qué.",
  'sb.SB165': "La fila del banco y el movimiento del libro van en sentidos contrarios. Lo que el banco llama crédito es plata que entró, y en el libro eso es un debe.",
  'sb.SB166': "Esa fila del extracto ya no existe.",
  'sb.SB167': "El extracto tiene que venir como una lista de filas.",
  'sb.SB168': "La tolerancia de días está fuera de rango. Va de 0 a 60.",
  'sb.SB169': "La antigüedad es de lo que le deben o de lo que debe. No hay un tercer lado.",
  'sb.SB170': "Hay que ser administrador de la empresa para cambiar su configuración.",
  'sb.SB171': "La moneda de los libros no se puede cambiar: ya hay asientos escritos. Los importes están guardados *en* esa moneda sin llevarla escrita, así que cambiarla no los convertiría — los reinterpretaría, sin que nada fallara.",
  'sb.SB172': "Ese idioma no existe. Son dos: español e inglés.",
  'sb.SB173': "La razón social no puede quedar vacía: encabeza los estados financieros.",
  'sb.SB174': "La moneda va en código ISO de tres letras — CRC, USD, EUR.",
  'sb.SB175': "La identificación no puede quedar vacía.",
  'sb.SB176': "Ese tipo de identificación no existe. Son cinco: física, jurídica, DIMEX, NITE y extranjera.",
  'sb.SB200': "El ejercicio de ese año todavía no está abierto. Los periodos existen —están los de los diez años siguientes— y abrir el año es un acto aparte: un periodo abierto es un sitio donde alguien puede escribir, y un dedo que se equivoca de década escribiría en uno que nadie mira.",
  'sb.SB201': "Ese ejercicio no existe en esta empresa.",
  'sb.SB202': "Ese ejercicio ya estaba abierto.",
  'sb.SB210': "Ese ejercicio todavía no se puede cerrar: hacen falta los once primeros meses cerrados y el último abierto.",
  'sb.SB211': "Ese ejercicio ya tiene su asiento de cierre. Hacer otro sumaría el resultado del año dos veces al patrimonio.",
  'sb.SB212': "No hay nada que cerrar: ese año no tiene resultado.",
  'sb.SB101': "Esa contraparte no existe en esta empresa. Déla de alta en Clientes y proveedores: su código es lo que viaja escrito dentro de cada asiento suyo.",
  'sb.SB180': "Ese código no vale. Minúsculas, números y guiones, hasta sesenta caracteres.",
  'sb.SB181': "Ya hay una contraparte con ese código.",
  'sb.SB182': "Una contraparte tiene que ser cliente, proveedor o las dos cosas. Sin eso no sale en ninguna de las dos antigüedades, y una ficha que no aparece en ningún sitio es una ficha perdida.",
  'sb.SB183': "Ese correo no tiene forma de correo. Algo, una arroba, algo, un punto y algo — sin espacios.",
  'sb.SB190': "Esa contraparte no está marcada como proveedor. Una factura de compra contra quien sólo es cliente deja un saldo que la antigüedad de pagar no enseña, y eso se ve igual que no deberle nada.",
  'sb.SB191': "Esa factura ya está registrada para esa contraparte. Meterla dos veces es el mismo pasivo contado dos veces, y el balance seguiría cuadrando mientras dice el doble.",
  'sb.SB192': "Falta decir contra qué cuenta va el gasto. Póngala en la factura, o déle una cuenta habitual a la contraparte para no repetirla en cada una.",
  /* ▶ Los dos que se partieron de `SB192` (0097). Hablan del CATÁLOGO y no del
     formulario, que es la diferencia que la frase de arriba borraba: quien los
     recibe no tiene que revisar lo que escribió — tiene que ir a `/catalogo`. */
  'sb.SB230': "El catálogo no tiene una cuenta por pagar o por cobrar que reciba movimiento en esa moneda. No es un dato que falte en el formulario: revise en Catálogo que esa cuenta esté activa y que sea la que recibe — si le colgó una auxiliar, la que recibe es la auxiliar.",
  'sb.SB231': "El catálogo no tiene una cuenta de IVA que reciba movimiento para ese lado. Una factura con impuesto necesita dónde ponerlo; sin ella el impuesto se mezclaría con el gasto y no se declararía.",
  'sb.SB232': "Ese trabajo no está en el catálogo de tareas, así que nadie vigila si dejó de correr.",
  'sb.SB233': "El devengo no puede empezar antes del mes de la factura. Se factura por anticipado y el servicio arranca después, no al revés: si el contrato ya estaba corriendo, la parte ya ganada no es ingreso diferido.",
  'sb.SB234': "La obligación no trae la clasificación que sus cuentas exigen —por ejemplo la modalidad, SaaS o local, que el índice de mantenimiento necesita para no mezclar dos negocios—. Se pide ahora y no al cerrar el mes, que es donde se descubría antes.",
  'sb.SB235': "Esa obligación no se puede dar de baja así: o no existe, o ya está dada de baja, o la fecha es anterior a cuando empezó a devengarse. Para cambiar un contrato se abre otra obligación; la baja no se reescribe.",
  'sb.SB236': "Las dos cuentas de una obligación no son intercambiables: el dinero cobrado por adelantado va a una cuenta de PASIVO de contrato, y lo que se gana cada mes a una de INGRESO. Al revés daría un asiento que cuadra y que reconoce ingreso contra ingreso.",
  'sb.SB237': "Esta empresa no declaró su tarifa del impuesto sobre la renta, y sin ella no se puede provisionar. Se declara en Configuración: el producto no trae ninguna porque depende del tramo de ingresos brutos de cada empresa, y una inventada produce un número con cara de oficial.",
  'sb.SB238': "La matriz de deterioro trae un tramo que no existe o un porcentaje fuera de 0 a 100. Los tramos son los mismos de la antigüedad de cartera — corriente, 1 a 30, 31 a 60, 61 a 90, más de 90 y sin vencimiento.",
  'sb.SB239': "El catálogo no tiene alguna de las cuentas que la provisión necesita —la estimación por deterioro, el gasto por deterioro, el impuesto de renta o la renta por pagar—. Revíselas en Catálogo: tienen que estar activas y recibir movimiento.",
  'sb.SB240': "Ese proyecto no existe en esta empresa, o ya está cerrado. Un proyecto cerrado no admite declaraciones nuevas: si hay que corregir lo reconocido, se corrige con un asiento — reabrir la estimación de algo que se declaró terminado es otra decisión.",
  'sb.SB241': "El avance va entre 0 y 100. Es el porcentaje ejecutado del contrato, y el ingreso reconocido sale de multiplicarlo por el precio.",
  'sb.SB242': "Las dos cuentas de un proyecto no son intercambiables: lo ejecutado y no facturado va a una cuenta de ACTIVO de contrato, y el ingreso del periodo a una de INGRESO. Al revés daría un asiento que cuadra y que reconoce ingreso contra ingreso.",
  'sb.SB243': "Esa categoría de backlog no existe. Son las cinco del Módulo 5 — a toda marcha, movimiento lento, estancado, por ir a buscar y cliente desacoplado— y salen del modelo, no de una lista escrita en la pantalla.",
  'sb.SB244': "Ese índice no existe, o la meta no tiene forma de número. Dejarla vacía sí se puede: significa que alguien miró y decidió no fijarla todavía, que no es lo mismo que no haberla mirado.",
  'sb.SB245': "Esa persona no está en la lista, o sus datos no sirven. Déla de alta antes de cargar sus horas: crearla al paso convertiría una errata del archivo en una persona nueva, y nadie volvería a mirar la lista.",
  'sb.SB246': "Una fila de horas no vino bien formada, y el mensaje dice cuál y qué le falta. Ninguna entró: media carga se ve igual que una carga entera.",
  'sb.SB247': "Ese presupuesto de horas no sirve para sugerir nada. Déjelo vacío si no hay presupuesto —entonces no se sugiere avance—: vacío y cero no son lo mismo, y entre cero horas no se puede dividir.",
  'sb.SB248': "Esas horas laborables no sirven para medir nada. Son las de UNA persona a tiempo completo en el mes —de 1 a 400—: el disponible del equipo se multiplica por la suma de los FTE.",
  'sb.SB249': "Esa diferencia temporaria no sirve para calcular nada. Hacen falta el concepto, las dos bases —la contable y la fiscal, una base ausente no se puede restar— y si la partida es un activo o un pasivo: de eso depende el signo. En un activo el contable mayor produce un PASIVO diferido; en un pasivo, un ACTIVO.",
  'sb.SB254': "Esa cuenta no es de efectivo, así que no es una cuenta bancaria. Una cuenta del banco se liga a una cuenta de efectivo del catálogo — si la cuenta es nueva, revise que reciba movimiento y que cuelgue del efectivo.",
  'sb.SB255': "Una transferencia va entre dos cuentas de efectivo DISTINTAS. Si el dinero salió hacia un gasto o hacia un proveedor es un pago, no una transferencia, y llamarlo así esconde de qué se trata.",
  'sb.SB256': "Entre dos cuentas en la misma moneda, lo que sale y lo que entra son iguales. Si el banco cobró una comisión, va como un asiento de gasto aparte: no es un diferencial de cambio, y meterla ahí la escondería en una cuenta de resultado que no le toca.",
  'sb.SB062': "Ese asiento no existe en estos libros. Si lo copiaste del número, revisá que no te hayas comido un dígito.",
  'sb.SB065': "Ese asiento se generó desde otro módulo, no desde el diario: por eso no se corrige desde acá. El `hint` te dice de dónde: si vino del banco, se arregla desde Bancos; si vino de un pago aplicado, desde la antigüedad del documento; si vino del cierre, se vuelve a correr el cierre con los números correctos.",
  'sb.SB066': "El período del asiento está cerrado — o el período al que querías moverlo. Un asiento en un mes cerrado no se mueve: se corrige con otro asiento (uno nuevo que lo compense), para que el saldo del mes cerrado quede intacto.",
  'sb.SB067': "Ese asiento ya tiene esa fecha. Nada que corregir — y escribirle lo mismo encima generaría una fila en la auditoría sin motivo real.",
  'sb.SB257': "Una cuenta bancaria lleva banco y número. Son para que alguien la reconozca en un estado de cuenta — sin ellos, la fila no dice nada que la cuenta contable no dijera ya.",
  'sb.SB253': "Ese proyecto se reconoce con el monto que Backlog reporta, no con un porcentaje declarado acá — o al revés. Las dos formas fijan el mismo objetivo, así que tenerlas juntas son dos escritores sobre el mismo saldo y gana el que escriba de último, sin que nada descuadre. Si el proyecto cambió de manos, hay que borrar las declaraciones de la forma vieja antes de empezar con la nueva.",
  'sb.SB252': "Esa cuenta no lleva una sola contraparte, así que no se le puede poner dueño. Este campo es para pagar contra una cuenta por pagar o por cobrar — la de quien pagó por la empresa. Si la plata salió de un banco, no hace falta decir de quién es: un banco no tiene acreedor.",
  'sb.SB251': "Esa cuenta es la cuenta de contrato de un proyecto, y ese saldo se libera por AVANCE: lo reconoce el porcentaje que alguien declara, no un calendario. Con las dos cosas encima, el ingreso se reconocería dos veces y el balance cuadraría igual. La mensualidad de ese mismo proyecto sí es una obligación — va en la cuenta de mantenimiento, que el catálogo tiene aparte.",
  'sb.SB250': "El catálogo no tiene las tres cuentas del impuesto diferido: el activo, el pasivo y el gasto. El modelo las trae con «NIC 12» escrito al lado — revise en Catálogo que estén activas y que reciban movimiento.",
  'sb.SB258': "El tratamiento de la diferencia tiene que ser «diferencial cambiario» o «saldo a favor». Es la decisión de qué hacer con lo que el depósito no calza con la factura, y el sistema no la adivina.",
  'sb.SB259': "Esta vía es para cobrar una factura en moneda extranjera con un depósito en colones. O la cuenta de banco que eligió no es en colones, o el documento ya está en la moneda de los libros — en ese caso es un cobro normal.",
  'sb.SB260': "Esa factura ya tiene abonos, y esta vía cancela facturas enteras. Aplique el resto con un pago normal, o quite el abono anterior antes.",
  'sb.SB261': "Se recibió menos de lo que suman las facturas, así que no hay un saldo a favor que dejar. Si la da por saldada igual, la diferencia es un diferencial (pérdida); si no, aplique un abono parcial con un pago normal.",
  'sb.SB193': "Los importes de la factura no dan. El subtotal más el impuesto tiene que sumar algo que se deba, y ninguno de los dos va en negativo: una devolución es una nota de crédito, no una factura al revés.",
  'sb.SB194': "Esa contraparte está desactivada. Actívela en Clientes y proveedores si todavía le compra — desactivar no borra su historia, deja de hacer negocio nuevo.",
  'sb.SB195': "Esa contraparte no está marcada como cliente. Una factura de venta contra quien sólo es proveedor deja un saldo que la antigüedad de cobrar no enseña, y eso se ve igual que no tener nada por cobrarle.",
  'sb.SB196': "Entre esta nota y las anteriores se acreditaría más de lo que dice la factura. Se mide contra el TOTAL de la factura y no contra lo que le queda abierto: una nota sobre algo ya pagado es legítima y deja saldo a favor, pero acreditar más de lo facturado no lo es.",
  'sb.SB197': "Ese documento no existe, o no es una factura. Una nota de crédito va contra una factura; contra otra nota no significa nada.",
  'sb.SB001': "Ese código de empresa no vale. Es el nombre de un esquema y de un rol en la base, así que va en minúsculas, empieza por letra y lleva sólo letras, números y guiones bajos.",
  'sb.SB002': "Esa empresa no existe.",
  'sb.SB052': "Los tipos de cambio no vinieron como una lista.",
  'sb.SB053': "La moneda de esa línea no es la de la cuenta. Una cuenta que está declarada en una moneda y recibe importes en otra deja de tener saldo en ninguna: la revaluación ajustaría algo que no existe y la conciliación compararía contra un extracto que sí está en una sola.",
  'sb.SB156': "Una corrida sin filas tiene que decir de qué moneda es. Sin filas la moneda no sale de ningún lado, y anotarla a nombre de la equivocada sería peor que no anotarla.",
  'sb.SB111': "No hay ninguna operación abierta.",
  'sb.SB112': "Falta decir si la empresa es de demostración y si su identificación es provisional. Ninguna de las dos se supone: una empresa de verdad tratada como demo, o al revés, se ve igual hasta que importa.",
  'sb.SB114': "Encender el permiso de operador exige decir qué lo autoriza. Un permiso sin motivo escrito es uno que después nadie puede revisar.",
  'sb.SB140': "Esta empresa no tiene emisor. Se da de alta antes, y se crea en el portal de Hacienda: no se puede por programa.",
  'sb.SB141': "Ese emisor todavía no puede tomar un consecutivo: o no tiene credenciales puestas, o su numeración la asigna otro sistema y acá sólo se anota el número que ése asignó.",
  'sb.SB142': "El emisor no tiene espacio de numeración para ese tipo de documento. Se abre al darlo de alta.",
  'sb.SB143': "Ese comprobante no existe en esta empresa.",
  'sb.SB144': "Ese comprobante ya está en un estado final. Un rechazo no se edita: se corrige emitiendo otro.",
  'sb.SB145': "Ese tipo de documento no existe.",
  'sb.SB146': "Ese estado de comprobante no existe.",
  'sb.SB147': "Ese asiento no existe en los libros de esta empresa.",
  'sb.SB148': "Esa empresa no existe.",
  'sb.SB149': "La identificación de esa empresa está declarada como provisional. Un espacio de consecutivos nace pegado a una cédula, y abrirlo contra una de relleno dejaría comprobantes emitidos a nombre de un número que no es suyo.",
  'sb.SB155': "No se sabe quién lleva la numeración. Son tres: el sistema de Hacienda, este producto, o apagada.",
  'sb.SB151': "Esta empresa ya tiene saldos de apertura cargados.",
  'sb.SB152': "Ya está declarado que esta empresa no tiene saldos previos. Cambiar desde cuándo empiezan los libros de una empresa que ya opera movería el suelo de todo lo registrado.",
  'sb.SB153': "La depreciación no puede empezar antes de la compra.",
  'sb.SB154': "La cuenta de gasto de ese activo exige una clasificación que el activo no trae. Sin ella, la depreciación caería en una línea que los informes por área no saben dónde poner.",
  'sb.SB220': "Esa línea no sirve para aplicar acá. Se aplica un abono de una cuenta por cobrar o por pagar CONTRA un documento de la misma cuenta: ni el banco, ni otra línea del mismo lado.",
  'sb.SB221': "A ese documento no le queda tanto por aplicar. Aplicar de más lo dejaría en negativo en la antigüedad mientras el balance sigue igual — el informe y el libro diciendo cosas distintas sin que nada falle.",
  'sb.SB222': "A ese abono no le queda tanto por repartir. Un pago no puede cancelar más de lo que pagó.",
  'sb.SB223': "El documento y el abono no son de la misma contraparte. Pagarle a uno la factura de otro descuadraría las dos fichas a la vez y en sentidos contrarios, con el total general cuadrando — que es lo que lo haría invisible.",
  'sb.SB224': "El documento y el abono están en cuentas distintas. Cancelar una deuda es hacer bajar el saldo de SU cuenta; entre cuentas distintas la antigüedad diría cero y el balance seguiría diciendo que se debe. Cada moneda tiene su cuenta.",
  'sb.SB225': "Esa aplicación no está bien formada. Cada una dice a qué documento va y cuánto, el importe viaja como texto, y deshacer exige decir por qué.",
  'sb.SB226': "Ese documento no tiene una línea de cuentas por cobrar o por pagar que aplicar. O no existe, o su asiento no toca ninguna cuenta de esa antigüedad.",
  'sb.SB110': "Su usuario no es operador.",
  // ── E8.4 · La puesta en marcha vista por el CLIENTE ──────────────────────
  // ▶ Los mismos pasos, dichos como TAREA y no como logro.
  //
  // `pm.*` los escribe en pasado —«están cargados»— porque en la consola van
  // con su casilla al lado: la marca dice si se cumplió. En la lista del
  // cliente solo aparecen los que FALTAN, así que «Los saldos de apertura
  // están cargados» se leía como si ya estuviera hecho, encima de un botón que
  // pide hacerlo.
  'pmc.step.identificacion_real': "Poner la cédula de verdad",
  'pmc.step.primer_acceso': "Dar acceso a alguien más",
  'pmc.step.entro_alguien': "Que la persona invitada entre",
  'pmc.step.apertura_cargada': "Cargar los saldos de apertura",
  'pmc.title': "Para terminar de poner en marcha",
  'pmc.sub': "Esto desaparece solo cuando no quede nada. No hace falta hacerlo hoy: la empresa ya funciona.",
  'pmc.ours': "De esto nos encargamos nosotros:",
  'pmc.oursBody': "no hay nada que usted tenga que hacer.",
  'pmc.goUsers': "Ir a usuarios",
  'pmc.goOpening': "Cargar los saldos",
  'pmc.why.primer_acceso': "Todavía nadie más entra a estos libros. Si va a trabajar acompañado, agregue a su gente.",
  'pmc.why.entro_alguien': "Hay invitaciones sin usar: alguien quedó como miembro y no ha entrado nunca. Puede que el correo se haya perdido.",
  'pmc.why.apertura_cargada': "Faltan los saldos con los que la empresa llega de su sistema anterior. Sin ellos, los reportes empiezan en cero y no cuadran con su contabilidad real.",
  'pm.title': "Puesta en marcha",
  'pm.empresa_creada': "La empresa está creada",
  'pm.identificacion_real': "La identificación es la de verdad",
  'pm.primer_acceso': "Alguien tiene acceso",
  'pm.entro_alguien': "Ya entró alguien",
  'pm.apertura_cargada': "Los saldos de apertura están cargados",
  'pm.byOperator': "nos toca",
  'pm.byAccountant': "le toca al cliente",
  'pm.ready': "Lista para trabajar",
  'pm.pending': "Falta algo",
  'op.backToCompanies': "Volver a mis empresas",
  'op.noBooksNote': "Esta consola no muestra saldos, asientos ni índices: crear un cliente no da acceso a sus libros.",
  'op.console': "Consola de operador",
} as const;

/** Toda cadena que se muestra tiene una clave de este tipo. */
export type Clave = keyof typeof ES;

export const EN: Record<Clave, string> = {
  'app.tagline': "Balance. File. Grow.",
  'nav.dashboard': "Dashboard",
  // ▶ Decía "Journal", y `jr.title` —la otra pantalla, la de al lado en el
  // mismo menú— también. **Dos entradas distintas con el mismo nombre**, y sólo
  // en inglés: en español son "Asientos" y "Diario". Se vio abriendo la
  // aplicación en inglés, no en una prueba — el control de idioma comprueba
  // que ninguna clave falte, no que dos no digan lo mismo.
  //
  // Ésta es la pantalla de REGISTRAR uno (`form.title`: «Record journal
  // entry»), así que el menú dice lo que se va a hacer ahí.
  'nav.entries': "New entry",
  'nav.invoices': "Invoices",
  'nav.reports': "Reports",
  'nav.integrations': "Integrations",
  'nav.users': "Users",
  'nav.settings': "Settings",
  'nav.group.accounting': "Accounting",
  'nav.group.system': "System",
  'wip.noDate': "Outside the MVP · no date",
  'cfg.title': "Settings",
  'cfg.sub': "What this company can change about itself. The chart of accounts, the periods and the people have their own screens.",
  'cfg.readOnly': "You need to be a company administrator to change anything here. You can see all of it.",
  'cfg.saved': "Saved.",
  'cfg.index': "Sections on this page",
  'cfg.company': "The company",
  'cfg.policy': "Accounting policy",
  'cfg.targets': "Ratio targets",
  'cfg.targetsWhy': "Each ratio’s FORMULA comes from the model and is the same for everyone — which accounts go above and below; what the result is compared against is a decision for whoever runs this company. ▶ Empty means not set, and it can be saved that way: “not yet” is a decision. While nobody looks at them, the dashboard shows them marked as house targets.",
  'cfg.targetModel': "model: {meta}",
  'cfg.targetNone': "not set",
  'cfg.targetSave': "Save targets",
  'cfg.targetSaved': "Saved",
  'cfg.targetUnconfirmed': "{cuantas} target(s) are still the model’s and nobody has confirmed them.",
  'cfg.taxRate': "Income tax rate (%)",
  'cfg.taxRateNone': "not declared",
  'cfg.taxRateWhy': "Month-end uses it to provide for income tax on the profit before tax in the books. The product ships without one: it depends on each company’s gross income bracket, and an invented rate produces a number that looks official. Leaving it empty says nobody declared it — which is not the same as zero per cent, a real rate. ▶ And this provision is NOT the tax on the return: taxable income carries differences this product does not know about.",
  'cfg.matrix': "Impairment matrix for receivables (%)",
  'cfg.matrixUnconfirmed': "These percentages are still the product’s proposal and nobody has confirmed them. Month-end will not tick the impairment step until they are saved.",
  'cfg.matrixWhy': "IFRS 9 allows estimating expected losses with an ageing matrix, and each entity estimates the percentages from its own collection history. The bands are the ones from the receivables ageing. ▶ “No due date” starts at zero on purpose: a document without a date is not overdue — what it is missing is the date.",
  'cfg.matrixSave': "Save the matrix",
  'cfg.matrixSaved': "Saved",
  'cfg.deferred': "Temporary differences · IAS 12",
  'cfg.deferredWhy': "The CARRYING amount of each item is in the ledger; the TAX base is known only to whoever files the return — it depends on allowed depreciation, on which expenses are deductible and on criteria that change with the law. ▶ So it is declared. With no declared differences there is no deferred tax, and closing step 11 says “not applicable” instead of blocking.",
  'cfg.deferredSign': "The sign comes from the NATURE of the item, not from which base is larger: on an ASSET a higher carrying amount produces a deferred LIABILITY, and on a LIABILITY it produces an ASSET. The two middle cases are the ones that flip by themselves.",
  'cfg.deferredConcept': "Concept",
  'cfg.deferredKind': "The item is",
  'cfg.deferredAsset': "an asset",
  'cfg.deferredLiability': "a liability",
  'cfg.deferredBook': "Carrying amount",
  'cfg.deferredTax': "Tax base",
  'cfg.deferredAdd': "Declare the difference",
  'cfg.deferredDrop': "Remove",
  'cfg.deferredEffect': "Effect",
  'cfg.deferredNoRate': "The tax rate above is missing: without it the differences show but not their effect.",
  'cfg.deferredNone': "None declared for {anio}.",
  'cfg.deferredTarget': "Target for the year",
  'cfg.deferredLiabilityNet': "deferred liability",
  'cfg.deferredAssetNet': "deferred asset",
  'cfg.tramo.corriente': "Current",
  'cfg.tramo.tramo_1_30': "1 to 30 days",
  'cfg.tramo.tramo_31_60': "31 to 60 days",
  'cfg.tramo.tramo_61_90': "61 to 90 days",
  'cfg.tramo.tramo_mas_90': "Over 90 days",
  'cfg.tramo.sin_vencimiento': "No due date",
  'cfg.name': "Legal name",
  'cfg.nameEmpty': "The legal name heads the financial statements: it cannot be left empty.",
  'cfg.code': "Code",
  'cfg.codeWhy': "The code names the schema where these books live, so it can never be changed — not here and not anywhere else.",
  'cfg.taxId': "Tax ID",
  'cfg.taxIdProvisional': "provisional",
  'cfg.taxIdType': "ID type",
  'cfg.taxIdCanonical': "Canonical form",
  'cfg.taxIdNone': "no canonical form",
  'cfg.taxIdForeign': "A foreign ID has no canonical form: it cannot be compared with another without knowing which country it is from. That does not make it any less real.",
  'cfg.taxIdUnmatched': "That number does not match the chosen type, so it could not be canonicalised and the company stays marked as provisional. Check the type or the number.",
  'cfg.taxIdWhy': "It is stored exactly as you write it, and the canonical form is computed by the database. The padding zeros are part of the form: 1-234-567 and 1-0234-0567 are the same ID.",
  'cfg.taxIdSave': "Save tax ID",
  'cfg.currency': "The currency of the books",
  'cfg.currencyCode': "ISO code",
  'cfg.currencyOpen': "There are no entries yet, so the currency can still be changed. After the first one it cannot: choose it now.",
  'cfg.currencyLocked': "It cannot be changed: there are {asientos} entries written in {moneda}. The amounts are *in* that currency without carrying it, so changing it would not convert them — it would reinterpret them, with nothing failing.",
  'cfg.currencyLockedOne': "It cannot be changed: there is already one entry written in {moneda}. The amounts are *in* that currency without carrying it, so changing it would not convert them — it would reinterpret them, with nothing failing.",
  'cfg.currencyForm': "Three letters: CRC, USD, EUR.",
  'cfg.language': "Language",
  'cfg.languageDefault': "Default language",
  'cfg.spanish': "Spanish",
  'cfg.english': "English",
  'cfg.languageWhy': "It is the one somebody who has not chosen yet opens with. It does not change yours: the ES/EN switch above saves your preference in this browser and wins over this one.",
  'cfg.save': "Save",
  'cfg.saveNote': "Whatever changes is recorded in the audit log, with the previous value.",
  'cfg.nothingChanged': "Nothing to save.",
  'cfg.opening': "Opening balances",
  'cfg.openingLoaded': "They are already loaded.",
  'cfg.openingNone': "This company is declared to have no prior balances: its books start on {desde}.",
  'cfg.openingPending': "They have not been loaded, and nobody has declared that there are none.",
  'cfg.openingGo': "Go to Opening balances",
  'cfg.elsewhere': "What is not changed here",
  'cfg.elsewhereWhy': "Each of these has its own screen, and repeating its buttons here would be two places for the same act.",
  'cfg.elsewhereChart': "the accounts, their names and their sub-accounts",
  'cfg.elsewherePeriods': "opening, closing and reopening a month",
  'cfg.elsewherePeople': "who gets in and with what role",
  'nav.main': "Main navigation",
  'err.screen': "This screen failed and could not be drawn.",
  'err.screenHelp': "The rest of Books still works: pick another screen from the menu. If it happens again on the same one, it is a defect on our side and we want to know.",
  'err.detail': "Show the technical detail",
  'common.loading': "Loading…",
  'common.debit': "Debit",
  'common.credit': "Credit",
  'common.account': "Account",
  'common.date': "Date",
  'common.description': "Description",
  'common.amount': "Amount",
  'common.currency': "Currency",
  'common.rate': "Exchange rate",
  'common.total': "Total",
  'common.source': "Source",
  'common.status': "Status",
  'common.folio': "No.",
  'common.reference': "Reference",
  'common.export': "Export",
  'common.export.csv': "CSV",
  'common.export.excel': "Excel",
  'common.export.print': "Print",
  'common.cancel': "Cancel",
  'common.dismiss': "Dismiss",
  'common.save': "Save",
  'common.search': "Search",
  'common.manual': "Manual",
  'common.posted': "Posted",
  'common.draft': "Draft",
  'common.all': "All",
  'common.month': "Month",
  'common.quarter': "Quarter",
  'common.year': "Year",
  'common.asset': "Asset",
  'common.liability': "Liability",
  'common.equity': "Equity",
  'common.income': "Income",
  'common.expense': "Expense",
  'common.mixed': "Mixed",
  'common.functional': "Functional currency",
  'common.original': "Original currency",
  'common.equivalent': "Colón equivalent",
  'common.admin': "Admin",
  'common.accountant': "Accountant",
  'common.readonly': "Read only",
  'common.unknownRole': "Unknown role",
  'rol.admin': "Admin",
  'rol.contador': "Accountant",
  'rol.solo_lectura': "Read only",
  'estado.activa': "Active",
  'estado.suspendida': "Suspended",
  'estado.cerrada': "Closed",
  'estado.por_emitir': "Not sent",
  'estado.esperando_a_hacienda': "Waiting on the tax authority",
  'estado.aceptado': "Accepted",
  'estado.rechazado': "Rejected",
  'estado.fallido': "Sending failed",
  'numera.atv': "Numbered by the tax office",
  'numera.books': "Numbered by Books",
  'numera.apagado': "Switched off",
  'origen.manual': "Manual",
  'origen.integracion': "Integration",
  'origen.cierre': "Closing",
  'origen.apertura': "Opening",
  'origen.depreciacion': "Depreciation",
  'origen.devengo': "Revenue recognition",
  'origen.provision': "Income tax provision",
  'origen.deterioro': "Impairment of receivables",
  'origen.avance': "Project progress",
  'origen.revaluacion': "Revaluation",
  'origen.diferencial': "Realised FX difference",
  'origen.diferido': "Deferred tax",
  'origen.banco': "Bank note",
  'origen.cierre_anual': "Year-end close",
  'fuente.vigente_declarado': "Declared in force",
  'fuente.copiado_del_anterior': "Copied from the previous day",
  'dash.sub': "Closes in *{dias} days* · {asientos} entries",
  'ver.hay': "There is a new version of Skoped Books.",
  'ver.actualizar': "Update",
  'ver.despues': "Later",
  'ver.instalar': "Install",
  'ver.instalarPor': "Open it as an app, without the browser bar.",
  'ver.puedeInstalar': "Books can be installed as an app — it opens in its own window, without the browser bar.",
  'ver.ahora': "Install now",
  'ver.luego': "Later",
  'dash.period.open': "Period open: {periodo}",
  'dash.newEntry': "New entry",
  'dash.kpi.income': "Income this period",
  'dash.kpi.expense': "Expenses this period",
  'dash.kpi.profit': "Profit before tax",
  'dash.kpi.tax': "Estimated tax (D‑100)",
  'dash.kpi.taxNote': "Provision at 30%",
  'dash.kpi.taxChip': "File before Sep 15",
  'dash.margin': "Margin",
  'dash.vsPrev': "vs. {mes}",
  'dash.chart.title': "Income vs. expenses",
  'dash.equation': "Accounting equation",
  'dash.equationSub': "Live check of the trial balance.",
  'dash.difference': "Difference",
  'dash.balancedTitle': "Books balanced.",
  'dash.balancedBody': "Assets = Liabilities + Equity at the period close, across everything recorded up to that date.",
  'dash.fxTitle': "Dollar exposure",
  'dash.fxSub': "USD balances revalued at the closing rate.",
  'dash.fxAccounts': "USD accounts",
  'dash.fxGain': "FX gain/loss this month",
  'dash.fxNote': "A one-colón move in the {moneda} rate shifts profit by {monto}.",
  'dash.integrations': "Active integrations",
  'dash.manage': "Manage integrations",
  'dash.inactive': "inactive",
  'dash.entries': "Recent entries",
  'dash.searchPh': "Search description…",
  'dash.integration': "Integration",
  'dash.ofEntries': "of 128 entries",
  'dash.viewJournal': "Open full journal →",
  'dash.viewIn': "Show in",
  'form.title': "Record journal entry",
  'form.sub': "Double entry. The books are kept in *colones*: a dollar entry is converted and must balance in colones.",
  'form.post': "Post entry",
  'form.saveDraft': "Save draft",
  'form.header': "Header",
  'form.docType': "Document type",
  'doc.journal': "Journal",
  'doc.receipt': "Receipt",
  'doc.payment': "Payment",
  'doc.adjust': "Adjustment",
  'doc.closing': "Closing",
  'form.descPh': "e.g. Course sale · Intro to Programming",
  'form.descHint': "Shown in the journal and in the general ledger detail.",
  'form.refPh': "INV‑00042 / receipt",
  'form.templates': "Quick templates",
  'form.tmpl.sale': "Cash sale",
  'form.tmpl.expense': "Expense from bank",
  'form.tmpl.payroll': "Payroll",
  'form.tmpl.credit': "Credit sale + VAT",
  'form.tmpl.usd': "Sale in dollars",
  'form.tmpl.fx': "FX difference",
  'form.lines': "Entry lines",
  'form.hint': "Enter moves down · Ctrl+↵ posts",
  'form.addLine': "Add line",
  'form.lineDetail': "Line detail",
  'form.optional': "Optional",
  'form.rateLabel': "Today's exchange rate",
  'form.rateSource': "BCCR reference · sell",
  'form.rateHint': "Applied to dollar lines, converted *one by one*. The BCCR declares it for this date and it is not typed over: recording a different agreed rate needs a reason, and that lands in a later stage.",
  'form.balanced': "Balanced — Debit = Credit = {monto}",
  'form.unbalanced': "Out of balance — difference ",
  'form.overDebit': " too much on the debit side",
  'form.overCredit': " too much on the credit side",
  'form.noAmounts': "No amounts — enter at least one debit and one credit line",
  'form.postedAs': "Posted as AS‑0129 · ",
  'form.effect': "Effect on the balance sheet",
  'form.effectEmpty': "Add lines to see the effect by account type.",
  'form.trace': "Audit trail",
  'form.user': "User",
  'form.period': "Period",
  'form.open': "Open",
  'form.traceNote': "Every posted entry is written to event_logs and is never deleted: it is reversed with an adjusting entry.",
  'form.goldenRule': "The golden rule",
  'form.goldenBody': "Whatever goes in one side comes out the other. If the entry does not balance, *Skoped Books will not post it* — it keeps it as a draft until you close it.",
  'form.fxNote': "Dollar figures are informational. *Balancing is validated in colones*, the currency of the books.",
  'rep.title': "Financial and tax reports",
  'rep.sub': "Every report is computed from the journal — it makes no difference whether an entry was typed or arrived through an integration.",
  'rep.sendTax': "Send to tax authority",
  'rep.tab.balance': "Balance Sheet",
  'rep.tab.income': "Income Statement",
  'rep.tab.equity': "Changes in equity",
  'rep.equityTitle': "Statement of changes in equity",
  'rep.equityMeta': "From the start of the year to {periodo}",
  'rep.equityComponent': "Component",
  'rep.equityOpening': "Opening balance",
  'rep.equityMovement': "Movements in the period",
  'rep.equityClosing': "Closing balance",
  'rep.equityTotal': "Total equity",
  'rep.equityEmpty': "This company has no equity recorded yet.",
  'rep.equityNote': "The opening balance is what each component brought at the start "
    + "of the year: the opening entry is not a first-day contribution. Movements are "
    + "NOT classified by nature —contribution, dividend, transfer to reserve—: that "
    + "needs the counterpart of each line, and it is said instead of guessed. The last "
    + "column totals to the equity in the balance sheet.",
  'rep.tab.cash': "Cash flows",
  'rep.cashTitle': "Statement of cash flows",
  'rep.cashMeta': "From the start of the year to {periodo} · indirect method",
  'rep.cashConcept': "Item",
  'rep.cashAmount': "Amount",
  'rep.cashOperating': "Operating activities",
  'rep.cashInvesting': "Investing activities",
  'rep.cashFinancing': "Financing activities",
  'rep.cashFx': "Effect of exchange rate changes",
  'rep.cashUnclassified': "Accounts with no declared activity",
  'rep.cashOpTotal': "Net cash from operating activities",
  'rep.cashInvTotal': "Net cash from investing activities",
  'rep.cashFinTotal': "Net cash from financing activities",
  'rep.cashChange': "Net change in cash",
  'rep.cashTies': "Closing cash comes from the cash accounts, and the change "
    + "comes from every other account. Both paths give the same number.",
  'rep.cashMismatch': "Closing cash does not match the change: there are "
    + "movements this statement is not reading.",
  'rep.cashUnclassifiedWarn': "Some balance-sheet accounts have no declared "
    + "activity. The total still ties — what is incomplete is how it splits "
    + "across the three activities.",
  'rep.cashEmpty': "This period has no cash movements.",
  'rep.cashNote': "Indirect method: it starts from the profit for the period and "
    + "adjusts for what did not move cash. The statement does NOT exclude "
    + "non-cash transactions —recognising a lease shows up as investing and "
    + "financing for the same amount—: that needs the counterpart of each "
    + "entry, and it is said instead of guessed. Interest and income tax stay "
    + "in operating.",
  'rep.tab.trial': "Trial Balance",
  'rep.tab.d100': "D‑100 · Costa Rica",
  'rep.tab.iva': "VAT · monthly",
  'rep.ivaTitle': "VAT reconciliation",
  'rep.ivaMeta': "Value added tax · {periodo} · Costa Rica tax authority",
  'rep.tab.aging': "Aging",
  'rep.tab.balances': "Bank balances",
  'rep.tab.health': "Health",
  'rep.group.statements': "Financial statements",
  'rep.group.review': "Review",
  'rep.group.fiscal': "Fiscal",
  'rep.group.management': "Management",
  'rep.report': "Report",
  'rep.printAsOf': "As of {fecha}",
  'rep.printCurrency': "Figures expressed in {moneda}",
  'rep.printPreparedBy': "Prepared by",
  'rep.printReviewedBy': "Reviewed by",
  'rep.printIssued': "Issued on {fecha}",
  'rep.printTitle.balance': "Statement of financial position",
  'rep.printTitle.income': "Statement of income",
  'rep.printTitle.equity': "Statement of changes in equity",
  'rep.printTitle.cash': "Statement of cash flows",
  'rep.printTitle.trial': "Trial balance",
  'rep.printTitle.d100': "D-101 return (income tax)",
  'rep.printTitle.iva': "VAT reconciliation",
  'rep.printTitle.aging': "Aging of balances",
  'rep.printTitle.health': "Financial health panel",
  'rep.health.title': "Financial health panel",
  'rep.health.sub': "Revenue and cost by stream, OpEx by department, and EBITDA on one page.",
  'rep.health.meta': "As of the close of {periodo}",
  'rep.health.month': "{periodo}",
  'rep.health.ytd': "YTD",
  'rep.health.pctNR': "% NR",
  'rep.health.grossRevenue': "Gross revenue",
  'rep.health.cogs': "Cost of sales",
  'rep.health.netRevenue': "Net revenue (NR)",
  'rep.health.grossMargin': "Gross margin",
  'rep.health.opex': "Operating expenses",
  'rep.health.ebit': "Operating result (EBIT)",
  'rep.health.depreciation': "+ Depreciation and amortization",
  'rep.health.ebitda': "EBITDA",
  'rep.health.ebitdaMargin': "EBITDA margin",
  'rep.health.nonOperating': "Non-operating result",
  'rep.health.netIncome': "Period result",
  'rep.health.ratios': "Ratios",
  'rep.health.ratioValue': "Value",
  'rep.health.ratioTarget': "Target",
  'rep.health.noData': "No operating movements for the period. The panel fills in automatically as revenue and expenses are recorded.",
  'rep.health.undept': "No department",
  'rep.agingTitle': "Aged balances",
  'rep.agingMeta': "Documents open · {periodo} · days are counted to the end of the period",
  'rep.agingSide': "Which side of the aging",
  'rep.agingReceivable': "Receivable",
  'rep.agingPayable': "Payable",
  'rep.agingFifo': "Whatever someone stated was paid comes off first; the rest is applied to the oldest document. The “guessed” column says how much of each row that rule placed because nobody allocated it: at zero, that row is a fact. The rule exists because one has to be chosen, and of all the possible ones it is the only one under which the buckets add up to exactly the account balance.",
  'rep.agingGuessed': "Guessed",
  'rep.agingStated': "stated",
  'rep.agingNoDueDate': "The buckets count days from the document's *due date*. Anything not yet due goes under “Current”. Anything with no due date — hand-posted entries, opening balances, or a document that did not state one — goes in its own column: deriving it from the counterparty's credit terms would make “31–60 overdue” mean two different things, one read off the paper and one calculated.",
  'rep.agingNoSupplier': "No supplier",
  'rep.agingCustomer': "Customer",
  'rep.agingSupplier': "Supplier",
  'rep.agingCurrent': "Current",
  'rep.aging1': "1–30 days",
  'rep.agingNoDue': "No due date",
  'rep.aging31': "31–60",
  'rep.aging61': "61–90",
  'rep.aging90': "Over 90",
  'rep.agingTotal': "Total",
  'rep.agingCredit': "In credit",
  'rep.agingOpen': "Open documents",
  'rep.agingLessCredit': "less credit notes",
  'rep.agingLedger': "Account balance",
  'rep.agingMismatch': "Does not match the account:",
  'rep.agingNoDueAll': "The whole portfolio has no due date, so the buckets say "
    + "nothing: a document with no due date cannot be aged. It is fixed when "
    + "recording it, not here.",
  'rep.agingAll': "All",
  'rep.agingDocs': "{n} documents",
  'rep.agingDocsOne': "1 document",
  'rep.agingDays': "{n} days",
  'rep.agingOf': "of {total}",
  'rep.agingNoCustomer': "No customer · came in with the opening entry",
  'rep.agingEmpty': "Nothing is open in this cut.",
  'rep.ivaWarn': "*Computed from the journal, and it is not the D-104 form.* These are the figures to type into ATV, not a file to file. Check every line against your vouchers: VAT is *monthly*, so this month is this month and not the year to date.",
  'rep.ivaHow': "Output VAT comes from the VAT payable accounts and input VAT from the VAT receivable ones, sub-accounts included. Open a line to see which account it came from.",
  'rep.presented': "Presented in",
  'rep.provisionalId': "provisional ID",
  'rep.noId': "no ID on file",
  'rep.balanceTitle': "Balance Sheet",
  'rep.balanceMeta': "{empresa} · {identificacion} · as of {fecha}",
  'rep.equationChip': "Assets = Liabilities + Equity",
  'rep.incomeTitle': "Income Statement",
  'rep.incomeMeta': "1 to {fecha}",
  'rep.marginChip': "Pre-tax margin 38.8%",
  'rep.trialTitle': "Trial Balance",
  'rep.trialMeta': "Totals and balances · {periodo} · {cuentas} accounts with movement",
  'rep.sumsChip': "Totals agree",
  'rep.d100Title': "D‑101 return",
  'rep.d100Meta': "Income tax · fiscal year {anio} · Costa Rica tax authority",
  'rep.d100Due': "Due",
  'rep.d100Warn': "*Draft computed from the journal.* Check every box against your vouchers —you can open a box to see which accounts make it up— and type it into ATV. Skoped Books computes; filing and tax liability remain yours.",
  'rep.d100Currency': "*The D‑100 is filed in colones.* Dollar transactions are already converted at each transaction’s rate, not at the closing rate.",
  'rep.box': "Box",
  'rep.concept': "Concept",
  'rep.generated': "Generated on {fecha}",
  'rep.d100Breakdown': "Download the breakdown",
  'rep.d100Open': "— show the {cuentas} accounts",
  'rep.d100OpenOne': "— show the account",
  'rep.d100Hide': "— hide the {cuentas} accounts",
  'rep.d100HideOne': "— hide the account",
  'rep.d100NoAccounts': "This period has no movement in any box, so there is no breakdown to download.",
  'rep.exported': " generated.",
  'rep.exportedBody': " ready to download.",
  'rep.fileOf': "File for ",
  'int.title': "Integrations",
  'int.sub': "Optional. The system is fully usable with none of them on — they only save you typing.",
  'int.docs': "API documentation",
  'int.connect': "Connect your own system",
  'int.plan': "Books works fully with no integration at all: everything can be entered by hand. What follows is what it *will* be able to receive, and which accounts each event would land in.",
  'int.active': "active",
  'int.activeOne': "active",
  'int.endpoint': "Endpoint",
  'int.apiKey': "API key",
  'int.keyNote': "Stored encrypted. Rotate it if you suspect exposure.",
  'int.mapping': "Account mapping",
  'int.copy': "Copy",
  'int.copied': "Copied",
  'int.show': "Show",
  'int.hide': "Hide",
  'int.currencyLabel': "Event currency",
  'int.currencyHint': "Dollar events are converted at the rate of the event date.",
  'int.entries': "Entries",
  'int.lastEvent': "Last event",
  'int.errors': "Errors 30 d",
  'int.never': "never",
  'int.log': "Event log",
  'int.processed': "Processed",
  'int.withError': "Failed",
  'int.eventCount': "{eventos} events",
  'int.eventOne': "event",
  'int.retention': "Every inbound event is logged with its source, payload and entry.",
  'login.sub': "Your books, balanced and ready to file.",
  'login.emailHint': "you@company.com",
  'login.email': "Email",
  'login.password': "Password",
  'login.enter': "Sign in",
  'login.or': "OR",
  'login.ms': "Sign in with Microsoft",
  'login.noAccount': "No account?",
  'login.createCompany': "Create your company free",
  'login.forgot': "Forgot my password",
  'login.forgotSub': "We will send you a link to set a new one.",
  'login.forgotSend': "Send the link",
  'login.forgotSent': "If that address is in Books, an email with the link is on its way. It expires in an hour.",
  'login.backToSignIn': "Back to sign in",
  // -- Setting a password: invitation and recovery ---------------------------
  'clave.checking': "Checking the link…",
  'clave.subInvite': "Welcome. Set the password you will sign in with.",
  'clave.subRecovery': "Set your new password.",
  'clave.new': "New password",
  'clave.repeat': "Repeat it",
  'clave.save': "Save the password",
  'clave.rule': "Eight characters or more.",
  'clave.done': "Password saved",
  'clave.doneBody': "You can now sign in to Books with it.",
  'clave.enter': "Sign in",
  'clave.back': "Go to the sign-in screen",
  'clave.linkTitle': "This link does not work",
  'clave.sin_enlace': "This address is opened from a link in a Books email. If you typed it by hand, there is nothing to do here.",
  'clave.enlace_vencido': "The link expired. They last an hour and work only once. Ask for another from “Forgot my password”.",
  'clave.enlace_usado': "That link was already used. Each one works only once; ask for another if you need it.",
  'clave.enlace_denegado': "The link was rejected. Ask for a new one from “Forgot my password”.",
  'clave.enlace_desconocido': "The link could not be read. Ask for a new one from “Forgot my password”.",
  'clave.sin_configurar': "The identity provider is not configured. That is on us, not on you.",
  'clave.sin_conexion': "We could not reach the identity provider. Please try again.",
  'clave.err.vacia': "Type a password.",
  'clave.err.corta': "Too short: eight characters or more.",
  'clave.err.no_se_pudo': "It could not be saved. The link may have expired while you filled the form: ask for another.",
  'clave.err.no_coincide': "The two do not match.",
  'clave.err.sin_conexion': "There was no connection. The password was NOT saved.",
  'login.badEmail': "Check the email format.",
  'login.badPass': "Password must be at least 6 characters.",
  'login.signedIn': "Signed in",
  'login.whichCompany': "Which company are you working in today?",
  'login.rolesSub': "Your user has access to {n} companies, with its own role in each.",
  'login.switchUser': "← Switch user",
  'login.addCompany': "Add company",
  'login.opening': "Opening ",
  'login.role': "Role: ",
  'login.readonlyNote': " — you will not be able to create or edit entries",
  'login.toDashboard': "Go to dashboard →",
  'login.byLabs': "A Skoped Labs product",
  'login.entries': "entries",
  'lab.title': "Colour lab",
  'lab.sub': "Five candidate product colours applied to real screens. Pick one above and the whole interface re-tints.",
  'lab.pick': "Candidates",
  'lab.why': "Why",
  'lab.risk': "Risk",
  'lab.contrast': "Contrast on white",
  'lab.family': "Family board",
  'lab.familySub': "The candidate beside the nine existing products. If it is hard to tell apart, it fails.",
  'lab.candidate': "Candidate",
  'lab.applied': "Applied to the interface",
  'lab.semantics': "Living with accounting semantics",
  'lab.semanticsSub': "An accounting product already spends green on income and red on expense. The accent has to survive next to both without being confused for either.",
  'lab.text': "Accent text",
  'lab.button': "Primary button",
  'lab.console': "Sign-in console",

  /* — agregadas después del porte — */
  'dash.period.closed': "Period closed: {periodo}",
  'fx.declared': "Rate declared by the BCCR as effective for this date",
  'fx.copied': "CAUTION: copied from the previous day. Does not back a tax document",
  'dash.kpi.cash': "Cash on hand",
  'dash.unbalancedTitle': "The books do not balance.",
  'dash.unbalancedBody': " Assets do not equal liabilities plus equity. Do not close the period until this is resolved.",
  'form.pickAccount': "Choose an account…",
  'form.removeLine': "Remove line",
  'form.unreadable': "One of the amounts cannot be read. Please check it — it is not treated as zero.",
  'form.off': "Out of balance — off by {monto}",
  'form.authority': "This balance check is a preview. The database decides: an unbalanced entry is saved as a draft, never posted.",
  'form.noRate': "No exchange rate for {moneda} on this date. That line is neither converted nor added: the entry cannot be posted until this is resolved.",

  /* — reportes y NIIF 18 — */
  'er.g4': "Operating revenue",
  'er.g4.total': "Total operating revenue",
  'er.g5': "Cost of sales",
  'er.g5.total': "Total cost of sales",
  'er.g6': "Operating expenses",
  'er.g6.total': "Total operating expenses",
  'er.g7': "Investing category",
  'er.g7.total': "Total investing category",
  'er.g8': "Financing category",
  'er.g8.total': "Total financing category",
  'er.g9': "Income tax",
  'er.g9.total': "Total income tax",
  'er.ingresoNeto': "Net revenue",
  'er.utilidadOperativa': "Operating profit",
  'er.antesDeFinanciacion': "Profit before financing and income taxes",
  'er.antesDeImpuestos': "Profit before tax",
  'er.resultadoDelPeriodo': "Profit for the period",
  'er.niif18': "Mandatory IFRS 18 subtotal",
  'er.mpm': "MPM",
  'er.mpmTitle': "Management-defined performance measure",
  'er.mpmNote': "*Net revenue* is not an IFRS subtotal: it is a measure management defines, and the operating ratios are built on it. IFRS 18 requires disclosing it as such, reconciled to the closest subtotal — operating profit.",
  'er.niif18Note': "Presented under IFRS 18, effective for periods beginning 1 January 2027. The two marked subtotals are required by the standard.",
  'rep.readingAid': "Reading aid",
  'rep.readingAidNote': "The books are kept in colones. Showing the figures in dollars *is not an accounting translation*: it posts nothing, changes no balance, and is not the presentation currency of the statements. It is only a way to read them.",
  'rep.noRate': "There is no exchange rate for the last day of this period: without it the amounts cannot be read in dollars, and one is not invented.",
  'rep.d100NoView': "The return is filed in colones and does not accept this view: it is a tax document, not a management reading.",
  'rep.prevMonth': "{periodo}",
  'rep.thisMonth': "{periodo}",
  'rep.ytd': "{anio} YTD",
  'rep.pctRevenue': "% of revenue",
  'rep.code': "Code",
  'rep.totalAsset': "Total assets",
  'rep.reclassified': "reclassified",
  'rep.reclassifiedWhy': "This line’s balance is NOT the ledger’s: it carries a presentation reclassification. Two things are presented where they belong without being posted — the contract liability falling due beyond twelve months of the cut-off, and the contract asset that went negative, which is a liability — because both depend on the cut-off date and an entry would pin them to one instant.",
  'rep.totalLiability': "Total liabilities",
  'rep.totalEquity': "Total equity",
  'rep.liabPlusEquity': "Liabilities + Equity",
  'rep.balanced': "Balanced",
  'rep.notBalanced': "DOES NOT BALANCE",
  'rep.revalued': "Revalued at the closing rate",

  /* — integraciones — */
  'int.subReal': "Optional, and none is required. Books works fully with manual entry; an integration only saves typing.",
  'int.none': "None connected",
  'int.available': "Ready to connect",
  'int.planned': "In a later stage",
  'int.stage': "Stage",
  'int.mapsTo': "The event would be recorded in",
  'int.noMapping': "Posts nothing: it feeds data, not the ledger.",
  'int.eventCurrency': "Event currency",
  'int.keyPending': "The key is generated on connection",
  'int.keyWhy': "There is no key yet, which is why none is shown. A credential-shaped string on a demo screen ends up copied somewhere.",
  'int.logEmpty': "No events have arrived yet",
  'int.logEmptyBody': "Once an integration is connected, every event will appear here with its source, what it carried and the entry it produced — or the error that stopped it.",
  'int.manualNote': "An entry typed by hand and one that arrived through an integration are recorded the same and are worth the same. The difference lives in the source field: that is traceability, not hierarchy.",
  'int.retentionPending': "The log retention period is pending confirmation with the accountant. No figure is asserted here without backing.",
  'int.mech': "How it arrives",
  'int.mech.api': "The source system pushes the events. A key is generated on connection.",
  'int.mech.archivo': "You upload the file. No key or credential involved.",
  'int.mech.servicio': "Books queries the service with its own token. Nothing to configure here.",
  'form.copiedRate': "The exchange rate for this date was copied from the previous day, not declared effective by the BCCR. The entry can be saved as a draft, but not posted.",

  /* — acceso — */
  'acc.checking': "Checking your session…",
  'acc.notConfigured': "Access is not configured",
  'acc.notConfiguredBody': "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are not set. It says so here instead of showing a form that cannot work: typing a correct password and seeing it fail, anyone would conclude the password is wrong.",
  'acc.byInvitation': "Books is entered by invitation. If your company already uses Books, ask your administrator to add you.",
  'acc.errCredentials': "Wrong email or password.",
  'acc.errOffline': "The service could not be reached. It is not your password: try again in a moment.",
  'acc.errNoAccess': "Your access to that company is no longer active. Pick another one.",
  'acc.noCompanies': "Your user is not in any company",
  'acc.operatorOnly': "You signed in as a Skoped Labs administrator",
  'acc.operatorOnlyBody': "This account manages clients and keeps no company’s books: that is on purpose. What you can do is in the console.",
  'acc.noCompaniesBody': "The invitation may not have been completed, or you may have been removed. Your administrator can add you again.",
  'acc.listFailed': "Your companies could not be listed",
  'acc.listFailedBody': "This is a connection failure, not a lack of permissions. Please try again.",
  'acc.retry': "Try again",
  'acc.signOut': "Sign out",
  'acc.opening': "Opening your books…",
  'acc.floor': "Read and download only",
  'acc.floorWhy': "This company is suspended. You can read and export its closed periods, statements and audit log — that is never withheld. Entries cannot be recorded.",
  'acc.readOnly': "Your role is read-only in this company.",
  'acc.switchCompany': "Switch company",
  'sb.SB020': "An entry needs at least two lines.",
  'sb.SB021': "The entry does not balance: debits and credits must be equal.",
  'sb.SB022': "That account takes no entries: it is a control account or it is deactivated.",
  'sb.SB023': "The classification that account requires is missing.",
  'sb.SB025': "This company is suspended: you can read and export, not record.",
  'sb.SB026': "The amount arrived as a number and must travel as text.",
  'sb.SB027': "That date falls outside every period in the financial year.",
  'sb.SB030': "The code must hang from its parent account.",
  'sb.SB031': "That account does not exist.",
  'sb.SB032': "The account already has entries and cannot become a control account.",
  'sb.SB033': "That cannot be changed: the statements and the indices depend on it.",
  'sb.SB034': "A template account is not deleted; it is deactivated.",
  'sb.SB035': "The account tree would be left half-done.",
  'sb.SB036': "The account needs a name in both languages.",
  'sb.unknown': "The operation could not be completed.",
  'sb.conexion': "There was no connection. Nothing was saved - please try again.",

  'jr.recordedAt': "Recorded on — when it was keyed in, which is not the accounting date",
  'jr.count': "{asientos} entries",
  'jr.countOne': "1 entry",
  'jr.title': "Journal",
  'jr.sub': "The entries for the period you choose, newest first. A recorded entry is not edited: it is corrected with another one.",
  'jr.period': "Period",
  'jr.wholeYear': "All of {anio}",
  'jr.trimmed': "capped at {limite}",
  'jr.ledgerTrimmed': "Showing the first {limite} movements of the period. The running balance of the visible rows is correct, but *it is not the account's closing balance*: later movements are missing. Narrow the period to see it in full.",
  'jr.empty': "No entries in this period yet.",
  'jr.editDate': "Date",
  'jr.editDateHint': "correct the date of this manual entry",
  'jr.newDate': "New date",
  'jr.editReason': "Reason",
  'jr.editReasonHint': "why it is being corrected; it goes into the audit log",
  'jr.editSave': "Save",
  'jr.editDone': "Entry #{numero} date corrected.",
  'jr.notHere': "Entry #{asiento} is not in this period. It may be in another month, or it may have been voided.",
  'jr.number': "Entry",
  'jr.date': "Date",
  'jr.memo': "Memo",
  'jr.total': "Total",
  'jr.origin': "Source",
  'jr.ledger': "Ledger",
  'jr.account': "Account",
  'jr.balance': "Balance",
  'jr.pickAccount': "Pick an account to see its ledger",
  'jr.saved': "Entry recorded",

  'cls.customer': "Customer",
  'cls.project': "Project",
  'cls.department': "Department",
  'cls.modalidad': "Modality",
  'cls.supplier': "Supplier",
  'voc.department.ps': "Professional services",
  'voc.department.support': "Support / Maintenance",
  'voc.department.cloud': "Cloud / Infrastructure",
  'voc.department.rnd': "R&D",
  'voc.department.sales': "Sales",
  'voc.department.marketing': "Marketing",
  'voc.department.ga': "G&A",
  'voc.stream.maintenance': "Maintenance",
  'voc.stream.subscription': "Subscription",
  'voc.stream.hosting': "Hosting",
  'voc.stream.transactional': "Transactional",
  'voc.stream.ps': "Professional services",
  'voc.stream.license': "Licenses",
  'voc.stream.hardware': "Hardware",
  'voc.stream.other': "Other",
  'voc.modalidad.saas': "SaaS",
  'voc.modalidad.on_premise': "On-premise",
  'cls.required': "Required to post",

  'cat.title': "Chart of accounts",
  'cat.count': "{cuentas} accounts",
  'cat.countOne': "1 account",
  'cat.sub': "You can rename any account, hang sub-accounts and deactivate the ones you do not use. What does not change is its structure: the statements and the indices depend on it, not on the name.",
  'cat.rename': "Rename",
  'cat.addChild': "Add sub-account",
  'cat.deactivate': "Deactivate",
  'cat.activate': "Activate",
  'cat.nameEs': "Name (Spanish)",
  'cat.nameEn': "Name (English)",
  'cat.bothLangs': "Both languages go together: renaming only one leaves two catalogues that do not match.",
  'cat.newCode': "New account code",
  'cat.inheritNote': "A new account inherits its parent's attributes. That is why it cannot unbalance a statement.",
  'cat.ledgerAccount': "Control account",
  'cat.inactive': "Deactivated",
  'cat.save': "Save",
  'cat.cancel': "Cancel",
  'cat.search': "Search",
  'cat.searchHint': "code or name, in both languages",
  'cat.group': "Group",
  'cat.allGroups': "All groups",
  'cat.onlyPostable': "Only accounts that take postings",
  'cat.hideInactive': "Hide deactivated ones",
  'cat.clearFilters': "Clear filters",
  'cat.countFiltered': "{cuantas} of {total} accounts",
  'cat.noMatch': "No account matches your search. The chart is still complete: what changed is what is being shown.",
  'fx.none': "No exchange rate published for this date.",
  'fx.asking': "Looking up the exchange rate…",
  'rep.period': "Period",
  'idx.title': "Operating ratios",
  'idx.fromLedger': "Straight from the ledger. Nobody types them.",
  'idx.target': "target",
  'idx.noTarget': "no target set",
  'idx.motivo.sin_fte': "This ratio measures against team capacity (FTE), which the product no longer captures. There is no base to measure against.",
  'idx.motivo.sin_jornada': "This ratio needs the month's working hours, which the product no longer captures.",
  'idx.motivo.sin_disponible': "Availability for the month came out at zero or less: time off ate the declared working hours. That is an odd figure and worth seeing, not covering with a percentage.",
  'idx.targetProposed': "house target",
  'idx.targetProposedWhy': "This target comes from the accounting model and nobody in this company has confirmed it yet. Review it in Settings.",
  'idx.provisional': "provisional",
  'idx.provisionalWhy': "The month has not closed: computed on the year to date and not final.",
  'idx.noData': "Cannot be computed with what is there.",
  'op.title': "Operator console",
  'op.sub': "Set up clients and hand out access. No one's books are visible from here.",
  'op.created': "Client created: {razon} · {codigo}. Nobody has access to it yet — “Accesses”, on its row.",
  'op.notOperator': "Your user is not an operator.",
  'op.notOperatorWhy': "The console belongs to whoever operates Skoped, not to whoever keeps a company's books. If you got here from a saved link, your books are still where they were.",
  'op.backToBooks': "Back to Books",
  'op.failed': "The operation could not be completed.",
  'op.newClient': "New client",
  'op.clientCount': "{empresas} companies",
  'op.clientCountOne': "1 company",
  'op.clientCountFiltered': "{cuantas} of {total} companies",
  'op.billing': "Electronic invoicing",
  'op.billingWhy': "Electronic invoices are issued in ATV and *the tax office assigns the number*. This list says who numbers each client, because two counters over the same legal sequence produce gaps or duplicates — and a number cannot be moved back.",
  'op.noIssuerYet': "Not decided",
  'op.startIssuer': "Register issuer",
  'op.toBooks': "Let Books number",
  'op.toAtv': "Let the tax office number",
  'op.turnOff': "Switch off",
  'op.billingNone': "No company has invoicing yet. Demos are not listed: they do not invoice.",
  'op.booksWarn': "Letting Books number opens the door for this deployment to take numbers from a legal sequence the tax office is already counting. If both number, gaps or duplicates appear — and a number cannot be moved back. The audit log records who said so.",
  'op.clients': "Clients",
  'op.search': "Search",
  'op.searchHint': "code, legal name or tax id",
  'op.allStates': "All states",
  'op.demoFilter': "Demo",
  'op.demoAll': "Clients and demos",
  'op.demoWithout': "Real clients only",
  'op.demoOnly': "Demos only",
  'op.noMatch': "No company matches the filter. They are all there: what changed is what is being shown.",
  'op.noClients': "No company has been created yet.",
  'op.code': "Code",
  'op.codeHint': "Lower case, no spaces. It is the schema name and can NEVER be changed.",
  'op.legalName': "Legal name",
  'op.taxId': "Tax ID",
  'op.state': "State",
  'op.members': "Access",
  'op.access': "Access",
  'op.create': "Create client",
  'op.grant': "Grant access",
  'op.revoke': "Revoke",
  'op.email': "Email",
  'op.emailHint': "The SAME one used to invite them in the identity provider. A different one creates a user who is nobody.",
  'op.personName': "Name",
  'op.role': "Role",
  'op.hasEntered': "has signed in",
  'op.neverEntered': "never signed in",
  'op.noMembers': "Nobody has access yet.",
  'op.provisional': "The tax ID is a placeholder",
  'op.provisionalWhy': "For a company whose registration is not finished. It stays flagged until corrected, and anyone issuing a tax document must refuse while it is.",
  'op.provisionalChip': "placeholder",
  'op.kind': "Client or test company?",
  'op.kindReal': "Real client",
  'op.kindDemo': "Test company",
  'op.kindWhy': "You have to choose: nothing is pre-selected. A test that comes in as a client gets billed and counted in the metrics; a client that comes in as a test stops being reported and nobody finds out.",
  'op.demoChip': "test",
  'op.invited': "Access granted, and the invitation email was sent.",
  'op.alreadyInAuth': "Access granted. That person already had an account, so no new invitation was sent: they can sign in.",
  'op.inviteByHand': "Access granted. The invitation was NOT sent —the administration key is missing— so they have to be invited by hand in the identity provider.",
  'op.inviteFailed': "Access granted. The invitation could not be sent: they have to be invited by hand in the identity provider.",
  'fac.title': "Electronic invoices",
  'fac.sub': "The documents this company issues to the tax authority, and how each one ended up.",
  'fac.issuer': "The issuer",
  'fac.documents': "Documents",
  'fac.branch': "branch {sucursal} · terminal {terminal}",
  'fac.number': "Number",
  'fac.kind': "Kind",
  'fac.state': "State",
  'fac.entry': "Entry",
  'fac.count': "{n} documents",
  'fac.countOne': "1 document",
  'fac.noIssuer': "This company's invoices are issued in ATV, outside Books.",
  'fac.noIssuerWhy': "This is a decision, not a pending task: *invoices are issued directly in the tax authority's system today and Books records the entry*. Issuing from here arrives when invoicing lives in the hub; until then this screen stays empty on purpose.",
  'fac.empty': "No document has been issued yet.",
  'fac.emptyNoIssuer': "There are no documents here because none are issued here. The sale is in the books: it is posted as an entry, in the journal.",
  'fac.trimmed': "There are more than {limite} documents and *the most recent ones* are shown. What is missing is the oldest.",
  'fac.step.atv': "Your invoices are issued in ATV, and *the tax office assigns the number*. Books records the number that was issued and the entry it belongs to — it does not take numbers: two counters over the same sequence produce gaps or duplicates, and a number cannot be moved back.",
  'fac.step.apagado': "This issuer is switched off and does not issue. Documents already sent remain valid: switching off voids nothing.",
  'fac.kind.factura': "Invoice",
  'fac.kind.nota_credito': "Credit note",
  'fac.kind.nota_debito': "Debit note",
  'fac.kind.tiquete': "Ticket",
  'fac.kind.factura_compra': "Purchase invoice",
  'fac.kind.exportacion': "Export invoice",
  'fac.kind.recibo_pago': "Payment receipt",
  'fac.why': "An accepted document is not deleted or edited: it is voided with another document, which carries its own number. That is why “accepted” and “rejected” are final.",
  'bit.title': "Audit log",
  'bit.sub': "Who did what in this company's books, and when.",
  'bit.why': "It is part of the suspension floor: a suspended company reads it in full. That is where the order that suspended it lives.",
  'bit.floor': "This company is suspended, and this screen is still yours: the audit log is one of the things the floor guarantees.",
  'bit.from': "From",
  'bit.to': "To",
  'bit.action': "What happened",
  'bit.who': "Who",
  'bit.detail': "Detail",
  'bit.trimmed': "There are more than {limite} records and *the most recent ones* are shown. What is missing is the oldest, which is usually what people come here to find: narrow it down with From and To to see it.",
  'common.system': "the system",
  'common.removedUser': "user removed",
  'jr.who': "Who wrote it",
  'bit.empty': "Nothing recorded yet.",
  'bit.empresa.creada': "The company was created",
  'bit.empresa.provisionada': "The chart of accounts and periods were seeded",
  'bit.membresia.dada': "Access was granted",
  'bit.membresia.quitada': "Access was revoked",
  'bit.miembro.invitado': "Person invited to the company",
  'bit.miembro.rol_cambiado': "Role changed",
  'bit.miembro.retirado': "Person removed from the company",
  'bit.mapeo.guardado': "Account mapping saved",
  'bit.apertura.cargada': "Opening balances were loaded",
  'bit.periodo.revaluado': "The period revaluation was run",
  'bit.diferencial.realizado': "The realised exchange difference on settlement was posted",
  'bit.contraparte.alta': "A counterparty was created",
  'bit.contraparte.editada': "A counterparty was edited",
  'bit.contraparte.activada': "A counterparty was activated",
  'bit.contraparte.desactivada': "A counterparty was deactivated",
  'bit.documento.registrado': "An invoice was recorded with its entry",
  'bit.pago.registrado': "A payment was recorded with its entry",
  'bit.cobro.en_moneda': "A foreign-currency invoice was collected with a colón deposit",
  'bit.aplicacion.escrita': "It was stated which document a payment cancelled",
  'bit.aplicacion.deshecha': "An allocation was undone, with its reason",
  'bit.ejercicio.abierto': "Financial year opened",
  'bit.ejercicio.cerrado': "Financial year closed",
  'bit.periodo.cerrado': "The period was closed",
  'bit.periodo.reabierto': "The period was reopened",
  'bit.activo.alta': "Depreciable asset registered",
  'bit.activo.depreciacion': "Month depreciated",
  'bit.obligacion.alta': "Revenue billed in advance registered",
  'bit.obligacion.baja': "Contract obligation ended",
  'bit.obligacion.devengo': "Month recognised",
  'bit.deterioro.matriz': "Impairment matrix declared",
  'bit.deterioro.provision': "Allowance for impairment adjusted",
  'bit.renta.provision': "Income tax provided for",
  'bit.empresa.tasa_de_renta': "Income tax rate changed",
  'bit.proyecto.alta': "Project registered",
  'bit.proyecto.cierre': "Project closed",
  'bit.proyecto.avance': "Project progress declared",
  'bit.proyecto.reconocimiento': "Progress recognised for the month",
  'bit.proyecto.categoria': "Project categorised",
  'bit.indice.meta': "Ratio target declared",
  'bit.persona.declarada': "A person was added or corrected",
  'bit.horas.cargadas': "Hours were loaded",
  'bit.jornada.declarada': "A month’s working hours were declared",
  'bit.diferido.declarado': "A temporary difference was declared",
  'bit.diferido.eliminado': "A temporary difference was removed",
  'bit.diferido.provisionado': "Deferred income tax was posted",
  'bit.horas.cambiadas': "Previously loaded hours were corrected",
  'bit.proyecto.presupuesto': "A project’s budgeted hours were declared",
  'bit.apertura.sin_saldos_previos': "Declared that the company has no prior balances",
  'bit.banco.extracto': "A bank statement was imported",
  'bit.banco.cuenta_declarada': "A bank account was tied to its accounting account",
  'bit.asiento.fecha_corregida': "The date of a manual entry was corrected, with reason",
  'bit.banco.emparejado': "Automatic matching was run",
  'bit.banco.registrado': "A bank note was posted to the ledger",
  'bit.banco.saldo_declarado': "The bank statement balance was declared",
  'bit.empresa.configuracion': "The company settings were changed",
  'bit.empresa.identificacion': "The company tax ID was corrected",
  'bit.emisor.alta': "Invoicing issuer registered",
  'bit.emisor.numeracion': "Changed who assigns the invoice number",
  'bit.operador.dado': "Operator permission was granted",
  'bit.operador.quitado': "Operator permission was revoked",
  'tar.title': "Scheduled jobs",
  'tar.why': "The state is computed when you look at it, not reported by the job: a dead process does not report itself.",
  'tar.al_dia': "up to date",
  'tar.muda': "SILENT",
  'tar.nunca': "HAS NEVER RUN",
  'tar.sin_fuente': "no source to read from",
  'tar.latido': "Database heartbeat",
  'tar.replica_de_cambio': "Exchange rate replica",
  'dash.noSeries': "No months with activity in this financial year yet.",
  'dash.noFx': "There are no foreign-currency balances.",
  'dash.noPeriod': "There is no open period for this month.",
  'dash.cashUndeclared': "This company has no account declared as cash, so there is no cash balance to show.",
  'tid.01': "Individual · national ID",
  'tid.02': "Legal entity · company",
  'tid.03': "DIMEX",
  'tid.04': "NITE",
  'tid.EX': "Foreign",
  'op.idType': "Identification type",
  'op.idTypeWhy': "It has to be chosen: it changes how the number is normalised. An individual set up as a company ends up with a canonical ID that is not theirs, and it does not show until someone looks them up.",
  'op.currency': "Local currency of the books",
  'op.currencyWhy': "Three ISO letters. It CANNOT be changed after the first entry: changing it is a migration, not a field.",
  'op.idReal': "The identification is the real one",
  'op.idPlaceholder': "It is a placeholder, I do not have it yet",
  'op.idWhy': "You have to choose: nothing is pre-selected. A placeholder that comes in as a real ID gets used on the first tax return, and by then it no longer looks made up.",
  'op.pickRole': "Choose the role",
  'ap.title': "Opening balances",
  'ap.sub': "The balances the company arrives with from its previous system. They are loaded ONCE, before any entry, and stay in the books as entry #1.",
  'ap.noPrior': "This company has no prior balances",
  'ap.noPriorWhy': "Its books are declared to start on {desde}. There is nothing to load, and getting started no longer asks for it.",
  'ap.noPriorTitle': "Was the company just incorporated?",
  'ap.noPriorHelp': "If its books start on this date and it is not coming from another system, there are no balances to load. Say so here and getting started stops asking: otherwise it will ask forever, because they are never coming.",
  'ap.noPriorDo': "There are no prior balances",
  'ap.noPriorNote': "Uses the date above. It is recorded in the audit log and cannot be undone.",
  'dif.title': "Deferred revenue",
  'pry.title': "Projects",
  'pry.fromBacklog': "hours and cost: Backlog",
  'pry.kpiMargin': "Margin this month",
  'pry.kpiHours': "Hours this month",
  'pry.kpiActive': "Active projects",
  'pry.kpiPending': "Pending to recognize",
  'pry.reportedToRecognise': "Reported by Backlog",
  'pry.pending': "Pending",
  'pry.recogniseNote': "Posts exactly what Backlog reported. Running twice doesn't double.",
  'pry.nothingPending': "Nothing pending to recognize this month.",
  'pry.cardFoot': "{horas} h · contract {precio}",
  'pry.manyCurrencies': "several currencies",
  'pry.marginChip': "{pct}% margin",
  'pry.teamTitle': "Team · utilization this month",
  'pry.teamWhy': "Hours per person reported by Backlog. The bar is the billable share.",
  'pry.billablePct': "{pct}% billable",
  'pry.note': "Note",
  'pry.budget': "Budget hrs",
  'hrs.title': "Hours",
  'hrs.sub': "Who worked, on what and how much. Books receives them; it does not capture them.",
  'hrs.whereFrom': "Where they come from",
  'hrs.whereFromWhy': "Books RECEIVES hours. Capture stays where it is today — the spreadsheet — and tomorrow in the projects product; the same door serves both eras. ▶ That is why there is no week, no calendar and no approvals here: those bring a work calendar, holidays and leave behind them, and would turn Books into a different product without anyone having decided it.",
  'hrs.noEntries': "Hours post NO journal entries. Revenue still comes from the progress declared in",
  'hrs.monthTitle': "The month",
  'hrs.shift': "Working hours in the month",
  'hrs.shiftWhy': "For ONE full-time person. Team availability is that times the sum of the FTEs, and billable utilisation comes from it. ▶ It is declared because it depends on public holidays, and a work calendar is exactly what this product decided not to bring: it measures what happened, it does not schedule what will.",
  'hrs.shiftProposal': "the house proposes {horas} ({dias} weekdays × 8)",
  'hrs.shiftUse': "Use the proposal",
  'hrs.shiftSave': "Save",
  'hrs.shiftNone': "Not declared. While it is missing, billable utilisation says “no data” instead of a number: with a 75 % target beside it, a ratio computed on an assumption is worse than a missing one.",
  'hrs.monthEmpty': "No hours loaded for this month.",
  'hrs.total': "Hours",
  'hrs.billable': "Billable",
  'hrs.internal': "No project",
  'hrs.billablePct': "Billable %",
  'hrs.noPct': "no hours",
  'hrs.peopleTitle': "People",
  'hrs.peopleWhy': "Someone on this list does NOT have to be a Books user: a contractor does not need access to the ledger for their hours to count. ▶ The code is the spreadsheet’s, and it is what ties their hours to it: with a code of our own there would be two names for the same person.",
  'hrs.noPeople': "There is nobody here yet. Add them before loading hours: creating them on the fly would turn a typo in the file into a new person, and nobody would look at this list again.",
  'hrs.code': "Code",
  'hrs.name': "Name",
  'hrs.fte': "FTE",
  'hrs.purpose': "Hours post no journal entries and don't block the close. They feed team utilization and per-project management cost.",
  'hrs.loaded': "Loaded",
  'hrs.days': "{dias} day(s)",
  'hrs.inactive': "inactive",
  'hrs.undeclared': "not declared",
  'hrs.edit': "Edit",
  'hrs.savePerson': "Save person",
  'hrs.fteWhy': "Empty means NOT DECLARED, and it can be saved that way. It is not zero: an FTE of zero would say that person does not work, and a rate of zero that they work for free.",
  'hrs.addTitle': "Load hours",
  'hrs.person': "Person",
  'hrs.project': "Project",
  'hrs.internalOption': "no project (internal hours)",
  'hrs.day': "Day",
  'hrs.hours': "Hours",
  'hrs.note': "Note",
  'hrs.billableLine': "Billable",
  'hrs.billableWhy': "Billable is PER LINE, not per project: the same person on the same day on the same project can have billable hours and rework that is not — and rework is exactly what the effective rate exists to find.",
  'hrs.load': "Load",
  'hrs.receipt': "Received {recibidas} · added {entraron} · changed {cambiaron} · unchanged {iguales}.",
  'hrs.receiptSame': "Nothing changed: those hours already said the same thing.",
  'hrs.listTitle': "Loaded",
  'hrs.listEmpty': "Nothing loaded for this month.",
  'hrs.yes': "yes",
  'hrs.no': "no",
  'hrs.noDelete': "A loaded hour is corrected by loading it again with different hours, and the change stays in the audit trail with its before and after. It is not deleted: a deletion would leave a total that changed and nothing to explain why.",
  'pry.backlogTitle': "Backlog",
  'pry.backlogViablePct': "Viable backlog",
  'pry.backlogOf': "{viable} of {total}",
  'pry.backlogNotViable': "Not viable",
  'pry.backlogUncategorised': "Uncategorised",
  'pry.backlogUncategorisedCount': "{cuantos} project(s)",
  'pry.backlogWhy': "Backlog is what is signed and not yet recognised, and it needs no hours: it is the contract price less the revenue already recognised. ▶ The figure you plan capacity with is the VIABLE one — only “full throttle” and “slow burn” count. A total backlog without that split says there is work where there may be none. And what nobody categorised goes apart: an unreviewed project is not a stalled one.",
  'pry.category': "Category",
  'pry.categoryNone': "uncategorised",
  'pry.sub': "Service contracts earned over time, and how much of each one has been performed.",
  'pry.monthTitle': "This month’s progress",
  'pry.period': "Period",
  'pry.project': "Project",
  'pry.price': "Contract price",
  'pry.currency': "Contract currency",
  'pry.progress': "Progress (%)",
  'pry.hours': "Hours",
  'pry.rate': "Hourly rate",
  'pry.rateEffective': "effective {tarifa}",
  'pry.rateContracted': "contracted {tarifa}",
  'pry.rateAlert': "giving away margin",
  'pry.rateAlertWhy': "The effective rate — revenue recognised over billable hours consumed — fell below the contracted one. Going past the hours sold gives away margin without a single ledger figure moving: the price does not change and the hours do.",
  'pry.rateNone': "no budget, no contracted rate",
  'pry.budgetNone': "no budget",
  'pry.suggest': "hours suggest {pct} %",
  'pry.suggestWhy': "Loaded hours over budgeted hours. It is a SUGGESTION: progress is declared because it is a judgement, and the button only copies the number into the field so it can be changed before saving.",
  'pry.noBudget': "hours are loaded; the budget is missing to suggest anything",
  'pry.progressNone': "not declared",
  'pry.declaredIn': "declared in {periodo}",
  'pry.target': "Revenue due",
  'pry.recognised': "Already recognised",
  'pry.adjustment': "Would be posted",
  'pry.declare': "Declare",
  'pry.close': "Close project",
  'pry.closed': "closed",
  'pry.notePlaceholder': "160 of 400 hours",
  'pry.recognise': "Recognise progress",
  'pry.twiceIsFine': "Running it twice does not duplicate: it posts the difference against a target that does not change.",
  'pry.declareFirst': "Progress does not come from the books: declare it first and then it is recognised. Books cannot measure it — own payroll goes to operating expenses and carries no project.",
  'pry.doneOk': "Progress recognised · {importe} · entry {asiento}",
  'pry.doneNone': "There was nothing to recognise in that month.",
  'pry.noneAtAll': "This company has no open projects at this cut-off.",
  'pry.mismatch': "The subledger says {objetivo} should be recognised and the books hold {reconocido}. No total breaks because of this — the balance sheet still ties and the only thing wrong is the period’s revenue.",
  'pry.negative': "{cuantos} project(s) have a negative contract asset: more was billed than recognised, and that is a contract LIABILITY disguised as an asset. It belongs in the professional-services deferred revenue account, and today it is reclassified by hand.",
  'pry.profitTitle': "Management profitability",
  'pry.profitWhy': "Recognized income − the management cost Backlog reports. Posts nothing.",
  'pry.profitNote': "Management view: Backlog reports the cost per project, already computed; it isn't journal entries. Actual payroll stays in OPEX.",
  'pry.colProject': "Project",
  'pry.colIncome': "Income",
  'pry.colCost': "Cost",
  'pry.colMargin': "Margin",
  'pry.noCostYet': "cost pending from Backlog",
  'pry.costOtherCurrency': "cost in another currency",
  'pry.addTitle': "Contracts",
  'pry.add': "Add",
  'pry.addHelp': "The project code IS the one entries carry in their classification: with a different one there would be two names for the same project and the recognised revenue could not be tied to anything.",
  'pry.code': "Code",
  'pry.name': "Name",
  'pry.number': "Backlog number",
  'pry.numberHint': "the key the hub ties the invoice with; empty if not invoiced",
  'pry.customer': "Customer",
  'pry.accountContract': "Contract account",
  'pry.contractHint': "usually the deferred revenue one: the invoice comes first",
  'pry.groupDeferred': "Deferred revenue — billed and not yet earned",
  'pry.groupContractAsset': "Contract asset — earned and not yet billed",
  'pry.accountIncome': "Income account",
  'pry.onlyIncome': "income only",
  'pry.start': "Start",
  'pry.save': "Save",
  'dif.sub': "Money billed in advance is not this month's revenue: it is earned over the term.",
  'dif.tiesTitle': "The subledger ties to the books.",
  'dif.mismatchTitle': "The subledger does NOT tie to the books.",
  'dif.tiesDetail': "Subledger pending {auxiliar} · book balance {contable}. The two "
    + "figures come by different paths —each obligation's schedule and the movements of "
    + "the contract accounts— so agreeing between them means something.",
  'dif.mismatchOver': "There is {diferencia} in the contract accounts that no obligation explains: someone credited one by hand, and nobody is going to recognise that money. No total breaks because of this — the balance sheet still ties and the only thing wrong is the revenue of the months ahead.",
  'dif.mismatchUnder': "There is {diferencia} missing from the contract accounts: some obligations have not had their billing posted, or too much has been recognised. No total breaks because of this — the balance sheet still ties and the only thing wrong is the period’s revenue.",
  'dif.twoLedgers': "That pending amount is TWO subledgers, not one: {obligaciones} from "
    + "{cuantas} calendar obligations, and {proyectos} from {cuantos} projects recognised by "
    + "progress. They add up here because they share the contract accounts, but they are "
    + "fixed in two places: obligations on this screen and projects on the Projects one.",
  'dif.projectsOut': "There are {cuantos} project(s) whose contract account is an ASSET: "
    + "their balance is not in deferred revenue, so they fall outside this reconciliation. "
    + "That is not an error —they were recognised before billing— but this figure misses them.",
  'dif.nonCurrent': "Of that pending amount, {importe} falls due beyond twelve months: on "
    + "the balance sheet it belongs to NON-current liabilities. For now it is reclassified "
    + "by hand.",
  'dif.monthTitle': "This month's recognition",
  'dif.period': "Period",
  'dif.obligation': "Obligation",
  'dif.term': "Term",
  'dif.termOf': "month {mes} of {total}",
  'dif.lastMonth': "last month",
  'dif.quota': "Instalment",
  'dif.total': "Month total",
  'dif.doIt': "Recognise the month",
  'dif.twiceIsFine': "Running it twice does not duplicate: it deletes the previous entry and rebuilds it.",
  'dif.doneNone': "There was nothing to recognise in that month.",
  'dif.doneOkOne': "1 obligation recognised · entry {asiento}.",
  'dif.doneOk': "{obligaciones} obligations recognised · entry {asiento}.",
  'dif.noneAtAll': "This company has no revenue billed in advance.",
  'dif.noneThisMonth': "No obligation has an instalment due this month.",
  'dif.listTitle': "Obligations",
  'dif.add': "Add",
  'dif.addHelp': "The normal path is the sales invoice: when you bill in advance you say "
    + "over how many months it is earned and the obligation is born with it. This form is "
    + "for what comes from before —an opening balance— or for money received without "
    + "an invoice.",
  'dif.description': "Description",
  'dif.customer': "Customer",
  'dif.accountDeferred': "Contract account",
  'dif.onlyLiabilities': "liabilities only",
  'dif.accountIncome': "Income account",
  'dif.onlyIncome': "income only",
  'dif.docDate': "Document date",
  'dif.start': "Recognised from",
  'dif.amount': "Amount",
  'dif.months': "Months",
  'dif.monthsOf': "{meses} months",
  'dif.pending': "Pending",
  'dif.save': "Save",
  'dif.end': "End",
  'dif.ended': "ended",
  'dif.endNote': "Ending an obligation stops recognition from the following month — the "
    + "month it ends is recognised in full— and does NOT take the pending amount to "
    + "revenue: whether the company may keep what it billed is a judgement about the "
    + "contract, not a fact of the calendar. To change a contract you end this one and open "
    + "another.",
  'act.title': "Fixed assets",
  'act.sub': "The assets being depreciated, and this month's depreciation. It is computed from each asset's record, not from a separate file.",
  'act.monthTitle': "This month's depreciation",
  'act.period': "Period",
  'act.asset': "Asset",
  'act.life': "Month of life",
  'act.lifeOf': "month {mes} of {total}",
  'act.lastMonth': "last — carries the remainder",
  'act.quota': "Charge",
  'act.total': "Month total",
  'act.doIt': "Depreciate this month",
  'act.twiceIsFine': "It can be run twice: the second run replaces the first entry, it does not add to it.",
  'act.doneOk': "Done. {activos} assets depreciated in entry #{asiento}.",
  'act.doneOkOne': "Done. One asset depreciated in entry #{asiento}.",
  'act.doneNone': "There was nothing to depreciate this month.",
  'act.noneAtAll': "No assets registered yet.",
  'act.noneThisMonth': "There are assets registered, but none depreciates this month: either they are not in service yet, or their life is over.",
  'act.listTitle': "Registered assets",
  'act.add': "Register an asset",
  'act.addHelp': "Each asset states its three accounts: the asset, accumulated depreciation and the expense. A vehicle and a building do not use the same ones, which is why the system does not pick them.",
  'act.description': "Description",
  'act.accountAsset': "Asset account",
  'act.onlyAssets': "Asset accounts only",
  'act.onlyExpenses': "Expense accounts only",
  'act.accountAccum': "Accumulated depreciation",
  'act.accountExpense': "Depreciation expense",
  'act.bought': "Purchase date",
  'act.start': "Depreciation starts",
  'act.cost': "Cost",
  'act.lifeMonths': "Life (months)",
  'act.taxLife': "Tax life (months)",
  'act.taxLifeSame': "same as the accounting life",
  'act.taxLifeIs': "tax {meses} m",
  'act.residual': "Residual value",
  'act.department': "Department",
  'act.save': "Register",
  'act.retired': "retired",
  // ── Bank reconciliation (0066) ─────────────────────────────────────────
  'ban.title': "Bank",
  'ban.sub': "The bank statement next to the ledger. What does not match is the result, not an error: it has an accounting name, and it gets posted.",
  'ban.account': "Bank account",
  'ban.onlyAssets': "Asset accounts only",
  'ban.accountsTitle': "Bank accounts",
  'ban.inDefault': "Deposit into the account",
  'ban.outDefault': "Payment from the account",
  'ban.record': "Record movement",
  'ban.recorded': "Movement posted · entry {asiento}.",
  'ban.direction': "In or out",
  'ban.amountWhy': "in the account's currency, exactly as the bank moved it",
  'ban.against': "Against which account",
  'ban.againstWhy': "where the money came from, or went to. A customer deposit goes against their receivable; a shareholder's, against theirs.",
  'ban.currencyMismatch': "That account is declared in another currency. To move between two currencies use Transfer, which asks for both amounts —what leaves and what arrives— and recognises the FX difference.",
  'ban.reference': "Reference",
  'ban.referenceHint': "document, voucher or transfer number",

  'ban.declareAccount': "Declare account",
  'ban.transfer': "Transfer",
  'ban.bank': "Bank",
  'ban.number': "Number",
  'ban.numberWhy': "as the bank writes it: it is there so someone recognises it on a statement",
  'ban.holder': "Account holder",
  'ban.currency': "Currency",
  'ban.undeclared': "not declared",
  'ban.noCash': "The chart of accounts declares no cash accounts.",
  'ban.save': "Save",
  'ban.declared': "Account declared.",
  'ban.from': "Out of",
  'ban.to': "Into",
  'ban.leaves': "Amount leaving",
  'ban.arrives': "Amount arriving",
  'ban.arrivesWhy': "in the destination currency, exactly as the bank credited it. The "
    + "difference against the day's rate is recognised as an FX difference.",
  'ban.transferred': "Transfer posted · entry {asiento}.",

  'ban.period': "Period",
  'ban.pickHelp': "Pick the bank account to reconcile. Each current account is reconciled against its own statement.",
  'ban.pickHelpSome': "Pick the account to reconcile. Statements loaded for: {cuentas}.",
  'ban.balanceTitle': "The reconciliation",
  'ban.perBooks': "Balance per books",
  'ban.creditNotes': "Unrecorded credit notes",
  'ban.debitNotes': "Unrecorded debit notes",
  'ban.adjustedBooks': "Adjusted book balance",
  'ban.perBank': "Balance per bank statement",
  'ban.inTransit': "Deposits in transit",
  'ban.outstandingChecks': "Outstanding cheques",
  'ban.adjustedBank': "Adjusted bank balance",
  'ban.notDeclared': "not declared",
  'ban.cannotSquare': "Without the bank statement balance there is nothing to reconcile against. Declare it below: it is the one figure that is not anywhere in the system.",
  'ban.squares': "It reconciles.",
  'ban.doesNotSquare': "It does not reconcile. Difference:",
  'ban.declareBalance': "Bank statement balance",
  'ban.declare': "Declare",
  'ban.declareHelp': "The closing balance the bank's own statement shows. It is not derived from the imported rows: that figure would be missing everything before the first file uploaded.",
  'ban.importTitle': "Bring in the statement",
  'ban.importHelp': "The CSV exactly as the bank exports it. Columns are recognised on their own —date, description, credit and debit, named in Spanish or English— and what the bank calls a *credit* is money that came into the account.",
  'ban.row': "row {n}",
  'ban.signIn': "Positive means money in",
  'ban.signOut': "Positive means money out",
  'ban.signInUsed': "Read as: positive is money in.",
  'ban.signOutUsed': "Read as: positive is money out.",
  'ban.readOk': "{filas} rows read, from {desde} to {hasta}.",
  'ban.dateGuessed': "No row proves the date order —there is no day past the 12th— so it was read as dd/mm/yyyy. If your bank exports mm/dd/yyyy, check the dates below before importing.",
  'ban.date': "Date",
  'ban.description': "Description",
  'ban.amount': "Amount",
  'ban.entry': "Entry",
  'ban.in': "In",
  'ban.out': "Out",
  'ban.andMore': "and {n} more.",
  'ban.import': "Import {n} rows",
  'ban.twiceIsFine': "The same file can be uploaded twice: repeated rows do not come in again.",
  'ban.importOk': "Done. {nuevas} new rows, {repetidas} that were already there.",
  'ban.pendingTitle': "Still open",
  'ban.tolerance': "Days",
  'ban.matchAuto': "Match the obvious",
  'ban.matchAutoHelp': "Matches only pairs that admit no doubt: same amount, same direction, and a single candidate on both sides. Two identical payments in the same week are left to you, on purpose.",
  'ban.matchOk': "{hechos} matched. {ambiguos} rows had more than one candidate and stay here.",
  'ban.sideBooks': "In the ledger, not on the statement",
  'ban.sideBank': "On the statement, not in the ledger",
  'ban.nothingPending': "Nothing open.",
  'ban.noStatement': "No statement has been brought in for this account yet.",
  'ban.matchPair': "Match the two selected",
  'ban.matchPairHelp': "Tick one row on each side. The amounts must be the same: a deposit the bank charged a fee on is two facts, not one.",
  'ban.post': "Post",
  'ban.postTitle': "The missing entry",
  'ban.willDebit': "It goes to the DEBIT of {cuenta} and the credit of the counterpart.",
  'ban.willCredit': "It goes to the CREDIT of {cuenta} and the debit of the counterpart.",
  'ban.counterpart': "Counterpart account",
  'ban.counterpartHelp': "You pick it. The same wording on a statement is an expense at one company and a receivable at another, so the system does not guess. The entry is dated by the bank, not by today.",
  'ban.memo': "Memo",
  'ban.postDo': "Post and match",
  'ban.postOk': "Done. Entry #{asiento}, and the row is matched against it.",
  'ban.matchedTitle': "Already matched",
  'ban.show': "Show",
  'ban.hide': "Hide",
  'ban.nothingMatched': "Nothing matched in this month yet.",
  'ban.unmatch': "Undo",
  'ban.rows': "{n} rows",
  'ban.rowsOne': "1 row",
  'ban.noStatementYet': "There is no bank statement for this account yet, so there is nothing to reconcile: what the ledger holds are not reconciling items, they are the account's own entries. Bring the statement in below.",
  'ban.noCashAccounts': "This company’s chart of accounts does not declare any cash account, so there is nothing to reconcile against. A bank account is declared as cash in the chart: it is not guessed from being an asset — the previous list included accumulated depreciation.",
  'ban.beforeMatching': "· before matching",
  'ban.noneMatchedYet': "No row has been matched yet, so both adjusted balances count every item on both sides at once and cannot be compared. Match below — automatically or by hand — and this line will say whether it reconciles. ({filas} row(s) in the statement.)",
  'ban.matchedCount': "{hechas} of {filas} statement row(s) matched.",
  // Why the bank file could not be read. See `nucleo/extracto.ts`.
  'ex.err.sin_filas': "The file has no rows below the header.",
  'ex.err.falta_fecha': "I cannot find the date column. These are the columns that came in:",
  'ex.err.falta_importe': "I cannot find the amount columns. Either credit and debit, or a single amount column. These are the ones that came in:",
  'ex.err.solo_monto': "The file has a single signed amount column. Tell me what a positive means: it cannot be guessed, because both readings load equally well.",
  'ex.err.fecha_ilegible': "I cannot read that date. yyyy-mm-dd and dd/mm/yyyy are accepted.",
  'ex.err.fechas_contradictorias': "The file mixes both date orders: some rows only make sense as dd/mm and others only as mm/dd. Picking one would shift half the movements to another month.",
  'ex.err.importe_ilegible': "I cannot read that amount.",
  'ex.err.dos_lados': "That row does not say whether money came in or went out: it has both columns filled, or both empty.",
  'ex.err.sin_lineas': "The file has rows, but none with a date.",
  'ap.already': "Already loaded",
  'ap.alreadyWhy': "It is loaded only once: doing it twice would double equity and the balance sheet would still balance, so nothing would warn you. A later correction is an adjusting entry.",
  'ap.date': "Date",
  'ap.dateHint': "The FIRST day of a period in the financial year. An opening dated the 14th says the company was born that day.",
  'ap.file': "CSV file",
  'ap.columns': "Header: cuenta, debe, haber, detalle, customer, project, department.",
  'ap.templateTitle': "Download the template",
  'ap.templateWhy': "It carries the accounts from YOUR chart, one per row and with no amounts. Fill in only the ones with a balance and delete the rest: a row with zero states that the account started at zero, which is not the same as having no balance.",
  'ap.templateGet': "Download CSV",
  'ap.paste': "Or paste the CSV",
  'ap.pasteHint': "The same as the file. Useful to fix one row without exporting again.",
  'ap.row': "Row",
  'ap.err.sin_filas': "The file has no rows below the header.",
  'ap.err.falta_cuenta': "The header needs at least the “cuenta” column. It brought",
  'ap.err.sin_importe': "That account brings neither a debit nor a credit.",
  'ap.err.sin_lineas': "There is no row with an account.",
  'ap.lineCount': "{lineas} lines",
  'ap.lineCountOne': "1 line",
  'ap.preview': "What will be sent",
  'ap.account': "Account",
  'ap.debit': "Debit",
  'ap.credit': "Credit",
  'ap.detail': "Detail",
  'ap.unreadable': "The balance cannot be anticipated: one amount cannot be read",
  'ap.difference': "Difference",
  'ap.previewNote': "Amounts are shown exactly as they arrived. The balance is an anticipation; the database decides.",
  'ap.load': "Load the opening balances",
  'ap.onlyOnce': "Only once, and before any other entry.",

  // -- E8.2 - The previous system's trial balance -----------------------------
  'mp.via': "Which file are you bringing?",
  'mp.viaPlantilla': "Our template",
  'mp.viaPlantillaWhy': "It already carries your chart's account codes. You download it in the next step.",
  'mp.viaBalance': "The trial balance from my previous system",
  'mp.viaBalanceWhy': "It carries ITS OWN account codes. Here you say which of our accounts each one corresponds to — once.",
  'mp.file': "Trial balance",
  'mp.columns': "It needs both columns: code, name, debit and credit.",
  'mp.paste': "Or paste the trial balance",
  'mp.pasteHint': "The same as the file. Useful to drop a totals row or fix a header without exporting again from the other system — whatever you already resolved below is kept.",
  'mp.err.sin_filas': "The file has no rows below the header.",
  'mp.err.falta_codigo': "The account code column was not found. The header brings",
  'mp.err.solo_saldo': "That file brings a single balance column, and there is no way to know whether a positive number is a debit or a credit: every system signs it differently, and guessing wrong flips accounts without anything failing. Export the trial balance with debit and credit columns. The header brings",
  'mp.err.sin_importe': "That account brings neither a debit nor a credit.",
  'mp.err.sin_lineas': "There is no row with an account code.",
  'mp.title': "Which account each one corresponds to",
  'mp.sub': "On the left, your accounts as they came. On the right, ours. It is saved: next time you upload a file, this will already be resolved.",
  'mp.theirs': "Your account",
  'mp.ours': "Our account",
  'mp.pick': "— choose —",
  'mp.suggested': "suggested",
  'mp.suggestedWhy': "Suggestions only appear when the name is a close match. Review them anyway: a suggestion saves a search, it does not make a decision.",
  'mp.mapped': "{cuentas} accounts mapped",
  'mp.mappedOne': "1 account mapped",
  'mp.pending': "unresolved",
  'mp.pendingWhy': "Some accounts are still unmatched. Those left without an account do NOT enter the opening, and their balance would be lost without the entry ceasing to balance.",
  'mp.save': "Save the mapping",
  'mp.saved': "Saved",
  'mp.resolved': "resolved",
  // -- The question about profit and loss -------------------------------------
  'mp.mode': "Your trial balance brings income and expense accounts",
  'mp.modeWhy': "That happens when the trial balance is from mid-year. There are two correct answers and which one applies depends on what you are going to do, so you choose.",
  'mp.modeClean': "Clean start",
  'mp.modeCleanWhy': "Income and expenses are netted against retained earnings and only the result comes in. The system starts with no history: this year's income statements begin at zero here and the full year is filed from the previous system.",
  'mp.modeMid': "Bring the current year",
  'mp.modeMidWhy': "Income and expenses come in as they are, with their accumulated balance for the year. This year's income statement is complete here from January, and the previous system is no longer needed.",
  'mp.modeNone': "Your trial balance brings no profit-and-loss accounts, so there is nothing to decide.",
  'mp.willLoad': "What will enter the opening",
  'mp.dropped': "without an account — they do not enter",
  'mp.provenance': "Each opening line records which of your accounts it came from, so it can be seen a year from now.",
  'sb.SB040': "This company already has its opening balances loaded.",
  'sb.SB041': "There are entries already: the opening goes before any other.",
  'sb.SB042': "The date must be the first day of a period in the financial year.",
  'sb.SB050': "There is no exchange rate for that currency on that date: that line cannot be converted.",
  'sb.SB060': "The period is closed. A correction is made with an entry in the following month.",
  'sb.SB090': "The statement is already in that currency: there is nothing to convert.",
  'sb.SB091': "Exchange rates are missing to convert the statement to that currency.",
  'sb.SB100': "That value is not in the analytical vocabulary for that dimension.",
  'idx.motivo.sin_modalidad': "The “modalidad” dimension is not declared in the chart of accounts: this index cannot be separated from its twin.",
  'idx.motivo.sin_ingreso_neto': "There is no net revenue in the period: a percentage of zero does not exist.",
  'idx.motivo.sin_gasto_de_area': "There is no departmental spend in the period: the index needs a denominator.",
  'idx.motivo.opex_sin_modalidad': "Some support spend in the period has no modality set. Computing it would leave that spend out of the denominator, and the index would look better than it is.",
  'idx.motivo.mantenimiento_sin_modalidad': "Some maintenance in the period has no modality set. It would be left out of the numerator, and the index would look worse than it is.",
  'cierre.title': "Month-end close",
  'cierre.sub': "The ten steps. The system checks the ones it can; the rest are signed off by a person, and it is recorded who.",
  'cp.title': "Customers and suppliers",
  'doc.title': "Accounts payable",
  'doc.sub': "Your suppliers' invoices. Recording one here writes the journal entry.",
  'doc.new': "Record an invoice",
  'doc.post': "Record and post",
  'doc.list': "Invoices payable",
  'doc.search': "Search",
  'doc.searchHint': "number, supplier or memo",
  'doc.onlyOpen': "Only with a balance",
  'doc.onlyOpenWhy': "Paid ones are not gone: untick this and they all come back.",
  'doc.hidden': "{n} with no balance, out of the list",
  'doc.count': "{n} listed",
  'doc.supplier': "Supplier",
  'doc.supplierWhy': "Their default account and currency fill in on their own, and can be changed before recording.",
  'doc.noSuppliers': "There are no active suppliers yet. The first one is created under Customers and suppliers.",
  'doc.allSuppliers': "All suppliers",
  'doc.pick': "Pick one",
  'doc.number': "Invoice number",
  'doc.numberHint': "the one on the paper",
  'doc.numberWhy': "It is the supplier's number, not one of ours: a number of our own would be a second name for the same invoice, and they do not know it.",
  'doc.date': "Date",
  'doc.totalN': "Total of {n} open documents",
  'doc.overdueN': "{n} overdue",
  'doc.due': "Due",
  'doc.dueWhy': "Empty means the paper does not say, not that it is due today.",
  'doc.dueProposed': "Proposed from the {n} credit days on their record. Correct it if the paper says otherwise.",
  'doc.subtotal': "Subtotal",
  'doc.tax': "Tax",
  'doc.taxWhy': "Empty is zero, and then the entry carries no tax line. Exempt sales and purchases exist and are ordinary.",
  'doc.currency': "Currency",
  'doc.currencyWhy': "The one on the counterparty's record, if they have one; otherwise the currency of the books.",
  'doc.account': "Expense account",
  'doc.accountAuto': "the supplier's default",
  'doc.accountWhy': "Only this one is asked. The tax account and the payables account are known to the system, and which of the two payables accounts is decided by the currency.",
  'doc.memo': "Memo",
  'doc.memoAuto': "built from the number and the name",
  'doc.total': "Total",
  'doc.entry': "Entry",
  'doc.overdue': "{n} days overdue",
  'doc.posted': "Invoice {numero} recorded. It went into entry #{asiento}.",
  'doc.none': "There are none yet. The first one is recorded with the button above.",
  'doc.noneFound': "None from that supplier.",
  'pay.new': "Record a payment",
  'pay.post': "Record the payment",
  'pay.amount': "Payment amount",
  'pay.otherCurrency': "The customer paid in another currency (colón deposit)",
  'pay.otherCurrencyWhy': "For a dollar invoice paid with a colón deposit. It cancels the invoice at its book value, leaves the bank with what was received, and the difference goes to FX or to a customer credit.",
  'pay.received': "Amount received in the bank",
  'pay.bankLocalWhy': "The colón bank account that received the deposit.",
  'pay.invoicesToCancel': "Invoices to cancel",
  'pay.invoicesToCancelWhy': "Cancelled in full: afterwards they are zero, in dollars and in colones",
  'pay.amountOwn': "Amount (own currency)",
  'pay.equivLocal': "In colones",
  'pay.remainsAfter': "Left after",
  'pay.diffTreatment': "The difference between what was received and the invoices",
  'pay.diffFx': "FX difference (gain or loss)",
  'pay.diffCredit': "Customer credit",
  'pay.diffTreatmentWhy': "A cents rounding is an FX difference; an overpayment the customer expects credited is a customer credit.",
  'bal.title': "Cash and bank balances",
  'bal.asOf': "As of",
  'bal.account': "Account",
  'bal.balance': "Balance",
  'bal.export': "Export",
  'bal.none': "No cash or bank accounts declared.",
  'bal.asOfNote': "Book balance as of {fecha}, each account in its own currency. A dollar account's is how many dollars there are, not its colón value.",
  'pay.from': "Account it comes from",
  'pay.fromHint': "bank, cash — or the payable of whoever paid",
  'pay.thirdParty': "Whose account is it?",
  'pay.thirdPartyWhy': "That account carries a counterparty's name, so naming the "
    + "account is not enough: it has to say who is owed. This is the case of someone "
    + "paying out of pocket — the supplier ends up paid and that person becomes a "
    + "creditor, with their debt ageing like any other.",
  'pay.fromWhy': "The account code from the chart. It is the one that goes down when you pay.",
  'pay.reference': "Reference",
  'pay.referenceHint': "cheque, transfer",
  'pay.referenceWhy': "It goes in the detail of the entry line. There is no separate record: neither a payment nor a receipt has any fact the entry does not already hold.",
  'pay.applyTo': "Which invoices it goes to",
  'pay.applyWhy': "Leaving it empty is valid: the report applies it to the oldest one and says so.",
  'pay.applyHere': "Apply here",
  'pay.open': "Left",
  'pay.settled': "settled",
  'pay.nothingOpen': "This supplier has nothing left open.",
  'pay.posted': "Payment recorded in entry #{asiento}, applied to {n} invoice(s).",
  'cob.title': "Accounts receivable",
  'cob.sub': "The invoices you issued. Recording one here writes the journal entry.",
  'cob.new': "Record an invoice",
  'cob.post': "Record and post",
  'cob.list': "Invoices receivable",
  'cob.none': "There are none yet. The first one is recorded with the button above.",
  'cob.noneFound': "None from that customer.",
  'cob.posted': "Invoice {numero} recorded. It went into entry #{asiento}.",
  'cob.customer': "Customer",
  'cob.customerWhy': "Their sales account and currency fill in on their own, and can be changed before recording.",
  'cob.noCustomers': "There are no active customers yet. The first one is created under Customers and suppliers.",
  'cob.allCustomers': "All customers",
  'cob.account': "Revenue account",
  'cob.accountAuto': "the customer's sales account",
  'cob.accountWhy': "Only this one is asked. The tax account and the receivables account are known to the system, and which of the two receivables accounts is decided by the currency. The customer is set by the system on both lines: every revenue account requires it.",
  'cob.numberWhy': "It is the number the tax authority's system issued. Here you record what was issued there; a number of our own would be a second name for the same invoice.",
  'cob.payNew': "Record a receipt",
  'cob.payPost': "Record the receipt",
  'cob.payPosted': "Receipt recorded in entry #{asiento}, applied to {n} invoice(s).",
  'cob.payFrom': "Account it goes into",
  'cob.payFromWhy': "The account code from the chart. It is the one that goes up when you collect.",
  'cob.applyTo': "Which invoices it goes to",
  'cob.applyWhy': "Leaving it empty is valid: the report applies it to the oldest one and says so.",
  'cob.nothingOpen': "This customer has nothing left open.",
  'cob.noneApplied': "Nobody has stated which receipt cancelled this invoice, and the ageing report has nothing to place against it either: there is no unallocated receipt from that customer.",
  'nc.new': "Record a credit note",
  'nc.post': "Record the note",
  'nc.number': "Note number",
  'nc.against': "Which invoice it goes against",
  'nc.againstWhy': "Required, and not only because electronic invoicing asks for it: a note without a reference is a credit that does not say what it cancels. That is also why it is applied to that invoice on its own.",
  'nc.reason': "Reason",
  'nc.reasonHint': "return, agreed discount",
  'nc.reasonWhy': "It goes on the document, and it is the only thing that explains months later why an invoice is worth less than it says.",
  'nc.accountAuto': "the same one as the invoice",
  'nc.accountWhy': "The account being undone. Empty uses the counterparty's default, which is the one the invoice went against.",
  'nc.chip': "note against {numero}",
  'nc.posted': "Note {numero} recorded. It went into entry #{asiento} and was applied to its invoice.",
  'nd.posted': "Note {numero} recorded. It went into entry #{asiento}, and adds to what is owed.",
  'nc.kind': "What the note does",
  'nc.credit': "Lowers what is owed",
  'nc.debit': "Raises what is owed",
  'nc.creditWhy': "A credit note undoes part of the invoice: a return, a discount. It is applied to the invoice it names on its own.",
  'nc.debitWhy': "A debit note charges more on that invoice: interest, a freight charge that was missing. It opens new debt, so it is applied to nothing and can carry a due date.",
  'ap.new': "Allocate a credit",
  'ap.why': "For what was hand-posted and nobody placed",
  'ap.post': "Apply",
  'ap.which': "Which credit",
  'ap.whichWhy': "These are the ones that still have something unallocated. It is what the report's “guessed” column counts: here you stop guessing.",
  'ap.none': "There is no unallocated credit. Everything paid already says which invoice it went to.",
  'ap.done': "Applied to {n} invoice(s). The report stops guessing that part.",
  'pay.loading': "Looking…",
  'pay.noneApplied': "Nobody has stated which payment cancelled this invoice, and the ageing report has nothing to place against it either: there is no unallocated payment to that supplier.",
  'pay.placed': "placed by the ageing report",
  'pay.undo': "Undo",
  'pay.undoWhy': "why?",
  'pay.undoConfirm': "Confirm",
  'pay.undone': "undone: {motivo}",
  'pay.undoneOk': "Allocation undone. Nothing was deleted: the opposite entry stands, with its reason.",
  'cp.sub': "Who buys from you and who you buy from. Each one's code is what travels inside their entries.",
  'cp.new': "New counterparty",
  'cp.list': "Counterparties",
  'cp.count': "{n} on the list",
  'cp.name': "Name",
  'cp.nameHint': "As it appears on their invoice",
  'cp.code': "Code",
  'cp.codeAuto': "derived from the name",
  'cp.codeWhy': "Leave it empty and the database builds it from the name. It is what travels written inside every movement.",
  'cp.codeLocked': "It does not change: it is written inside each of their entries, and changing it would leave them pointing at nobody.",
  'cp.role': "Role",
  'cp.role.all': "Customers and suppliers",
  'cp.role.cliente': "Customers only",
  'cp.role.proveedor': "Suppliers only",
  'cp.isCustomer': "Is a customer",
  'cp.isSupplier': "Is a supplier",
  'cp.roleWhy': "It can be both: you buy from them and sell to them. At least one is needed, or it shows up in no ageing report.",
  'cp.customer': "Customer",
  'cp.supplier': "Supplier",
  'cp.idType': "Tax ID type",
  'cp.idNone': "None, or not provided",
  'cp.id': "Tax ID",
  'cp.idWhy': "It is stored as you write it, and the canonical form is computed by the database. A supplier from another country goes in as Foreign, with their number and their country — \"US EIN 27-0805829\": the same number from two countries is not the same taxpayer, which is why the database never matches foreign IDs to each other. One that does not provide it — Meta, Google — is left without an ID: the field is optional on purpose."
  ,
  'cur.gone': "no longer published",
  'doc.currencyFromParty': "the party's, or the books'",
  'cfg.currencyNone': "not chosen",
  'op.currencyNone': "not chosen",
  'cp.email': "Email",
  'cp.emailNone': "not stated",
  'cp.emailWhy': "Where to write to them. This is where the e-invoice recipient will come from, so use the billing address rather than one person's. Stored in lowercase: \"Info@X.com\" and \"info@x.com\" are the same inbox.",
  'cp.currencyLocal': "the books' currency",
  'cp.currencyWhy': "Only the currencies this company can operate in are listed: the books' currency and those with a published exchange rate. A currency without a rate produces invoices that cannot be recorded.",
  'cp.credit': "Credit days",
  'cp.creditNone': "not declared",
  'cp.creditWhy': "Empty is not zero: “nobody declared it” and “pays cash” are two different things.",
  'cp.currency': "Usual currency",
  'cp.currencyNone': "not stated",
  'cp.account': "Default expense account",
  'cp.accountSale': "Default revenue account",
  'cp.accountSaleWhy': "The chart code that what you sell them usually goes against. There are two accounts and not one because you can both buy from and sell to the same party.",
  'cp.accountNone': "not stated",
  'cp.accountWhy': "The chart code that what you buy from them usually goes against. It fills in on its own when you record one of their invoices, and the invoice can override it. Without it, every invoice of theirs needs the account typed by hand.",
  'cp.note': "Note",
  'cp.balance': "Balance",
  'cp.inFavour': "in credit",
  'cp.edit': "Edit",
  'cp.editing': "Editing {codigo}",
  'cp.save': "Save",
  'cp.create': "Create",
  'cp.created': "{nombre} is created, with code {codigo}.",
  'cp.saved': "{nombre} is saved.",
  'cp.activate': "Activate",
  'cp.deactivate': "Deactivate",
  'cp.inactive': "inactive",
  'cp.unnamed': "needs naming",
  'cp.none': "There are none yet. The first one is created before posting an invoice to them.",
  'cp.noneFound': "None with that filter.",
  'cp.search': "Search",
  'cp.searchHint': "name, code or tax ID",
  'cierre.period': "Period",
  'cierre.exercises': "Financial years",
  'cierre.exercisesManage': "Manage financial years · open or close a year",
  'cierre.exercisesWhy': "Opening a year decides that it can be written to. It goes in the log.",
  'cierre.exerciseYear': "Year",
  'cierre.exerciseState': "State",
  'cierre.exerciseEntries': "Entries",
  'cierre.exerciseFuture': "not open",
  'cierre.exerciseOpenN': "{n} open",
  'cierre.exerciseClosedN': "{n} closed",
  'cierre.exerciseFutureN': "{n} not open",
  'cierre.closeYear': "Close {anio}",
  'cierre.yearClosed': "closed",
  'cierre.yearClosedOk': "Fiscal year {anio} closed. The year's result went to equity, in entry #{n}.",
  'cierre.openExercise': "Open {anio}",
  'cierre.exerciseOpened': "Financial year {anio} is open: {n} periods are ready to post to.",
  'cierre.paso.1': "Entries for the month balanced and posted",
  'cierre.paso.2': "BCCR exchange rates complete for the month",
  'cierre.paso.3': "Foreign exchange difference adjustment",
  'cierre.paso.4': "Deferred revenue recognition",
  'cierre.paso.5': "Professional services recognised by progress",
  'cierre.paso.6': "Depreciation and amortisation",
  'cierre.paso.7': "Income tax provision",
  'cierre.paso.8': "Allowance for doubtful accounts",
  'cierre.paso.9': "Freeze the month's indices",
  'cierre.paso.10': "Lock the period",
  'cierre.paso.11': "Deferred income tax · IAS 12",
  'cierre.estado.cumplido': "Checked",
  'cierre.estado.afirmado': "Signed off",
  'cierre.estado.pendiente': "Pending",
  'cierre.estado.no_aplica': "Not applicable",
  'cierre.motivo.por_construccion': "Guaranteed by the database: an unbalanced entry is not stored.",
  'cierre.motivo.sin_moneda_extranjera': "The company has no foreign-currency items.",
  'cierre.motivo.faltan_tipos': "Missing exchange rates",
  'cierre.motivo.falta_revaluar': "Revaluation has to run before closing.",
  'cierre.motivo.sin_activos': "The company has no assets to depreciate this month.",
  'cierre.motivo.sin_obligaciones': "This company has no revenue billed in advance in this period.",
  'cierre.motivo.falta_devengar': "There is deferred revenue due this month and it has not been recognised yet.",
  'cierre.motivo.sin_utilidad': "The year has no profit up to this month, so there is no tax to provide for.",
  'cierre.motivo.falta_declarar_tasa': "This company has not declared its income tax rate. It is set in Settings — the product ships without one because it depends on each company’s gross income bracket.",
  'cierre.motivo.falta_provisionar_renta': "There is profit and the income tax provision has not been posted yet.",
  'cierre.motivo.sin_cartera': "This company has no open receivables at this cut-off.",
  'cierre.motivo.matriz_sin_confirmar': "The impairment matrix is still the product’s proposal and nobody has confirmed it. Review it in Settings.",
  'cierre.motivo.falta_provisionar_deterioro': "The allowance is not at its target and the adjustment has not been posted yet.",
  'cierre.motivo.sin_proyectos': "This company has no projects recognised over time.",
  'cierre.motivo.falta_declarar_avance': "Some projects have no progress declared. Progress does not come from the books — it is declared by whoever knows the project, on the Projects screen.",
  'cierre.motivo.falta_reconocer_avance': "Progress is declared and the revenue has not been recognised yet.",
  'cierre.motivo.falta_depreciar': "Depreciation has to run in /activos before closing.",
  'cierre.motivo.al_cerrar': "Happens on closing, in the same transaction.",
  'cierre.motivo.sin_diferencias': "This company has no temporary differences declared for the year, so there is no deferred tax to recognise. They are declared in Provisions: tax bases do not come from the ledger — they depend on the law, and deriving them would be inventing them.",
  'cierre.motivo.falta_provisionar_diferido': "There are declared differences and their effect is not posted yet. The button posts the DIFFERENCE against what is already there, not the whole amount again.",
  'cierre.motivo.firmado_no_comprobado': "Signed off by a person, not checked by the system.",
  'cierre.motivo.necesita_modulo': "Needs the corresponding module; for now it is signed off.",
  'cierre.blocks': "blocks the close",
  'cierre.sign': "Sign off",
  'cierre.signWhy': "Signing off means “I did this”, with your name and the time. It is not a check, which is why it is only available on steps the system cannot check.",
  'cierre.note': "Note",
  'cierre.revalue': "Run the revaluation",
  'cierre.provisionTax': "Provide for income tax",
  'cierre.provisionDeferred': "Post deferred tax",
  'cierre.doneDeferred': "Deferred tax posted",
  'cierre.recogniseProgress': "Recognise progress",
  'cierre.doneProgress': "Progress recognised",
  'cierre.provisionBad': "Adjust the allowance",
  'cierre.provisionOk': "{importe} · entry {asiento}",
  'cierre.provisionNone': "already at its target, no entry needed",
  'cierre.revalueWhy': "Adjusts monetary items to the last day's rate. It can be run again: it redoes the entry instead of duplicating it.",
  'cierre.close': "Close the period",
  'cierre.closed': "Period closed",
  'cierre.reopen': "Reopen",
  'cierre.reopenWhy': "Reopening deletes the sign-offs: they were made on a month that is about to change.",
  'cierre.doneCount': "{lineas} lines adjusted",
  'cierre.doneNone': "there was nothing to adjust",
  'cierre.done': "Revaluation done",
  'cierre.doneTax': "Income tax provided for",
  'cierre.doneBad': "Allowance adjusted",
  'cierre.export': "What you take from the month",
  'cierre.exportWhy': "The same files as the reports, for the period you are closing. Exporting is READING: a suspended company still has them.",
  'cierre.exportOpen': "The month is not closed: these figures can still change",
  'cierre.groupAction': "Needs your action",
  'cierre.groupDone': "Done and not applicable",
  'cierre.progress': "{n} of {m} blocking steps left before closing",
  'cierre.ready': "All set: you can close the month now",
  'cierre.avanceReady': "The projects' progress is declared and ready to recognize. Recognize it below to close the month.",
  'cierre.avanceBacklog': "Backlog reported {n} project(s) in progress, {total} to recognize. Recognize it below to close the month.",
  'cierre.avanceProject': "Project",
  'cierre.avanceAmount': "To recognize",
  'cierre.fromBacklog': "from Backlog",
  'cierre.atClose': "at close",
  'sb.SB061': "That period does not exist in the financial year.",
  'sb.SB070': "The exchange rate for the last day of the month is missing: revaluation does not run halfway.",
  'sb.SB071': "There are accounts that get revalued with no IFRS 18 category. It has to be assigned first.",
  'sb.SB072': "There is no income account for that category's exchange difference.",
  'sb.SB080': "The period cannot be closed yet: blocking steps remain.",
  'sb.SB081': "That step is checked by the system: it cannot be signed off.",
  'sb.SB082': "The period is already closed.",
  // ── The company's people (E8.5) ──────────────────────────────────────────
  'us.title': "Company users",
  'us.sub': "Who gets into these books, and with what permission. The company manages this: no need to ask us.",
  'us.invite': "Invite someone",
  'us.email': "Email",
  'us.emailHint': "The same one they will sign in with. They get a link to set their password.",
  'us.name': "Name",
  'us.role': "Role",
  'us.pickRole': "— choose —",
  'us.roleHint': "Administrator adds and removes people. Accountant records and closes. Read-only views and exports.",
  'us.send': "Invite",
  'us.invited': "Invitation sent. They are already a member; the email carries the link to set their password.",
  'us.noMail.sin_llave': "They are a member, but the email did NOT go out: sending is not configured. They can get in using “Forgot my password”.",
  'us.noMail.ya_existe': "They are a member. They already had a Books account, so no new email was sent: they sign in with their usual password.",
  'us.noMail.rechazada': "They are a member, but the email did NOT go out. They can get in using “Forgot my password”.",
  'us.who': "Who gets in",
  'us.count': "{personas} people",
  'us.countOne': "1 person",
  'us.person': "Person",
  'us.since': "Since",
  'us.you': "you",
  'us.pending': "never signed in",
  'us.pendingWhy': "A member, but has not signed in even once. If the email was lost, they can get in with “Forgot my password”.",
  'us.remove': "Remove",
  'us.lastAdmin': "only administrator",
  'us.lastAdminWhy': "If they leave, the company is left with nobody who can invite — not even another administrator. Name another one first.",
  'us.foot': "Removing someone takes away their access; it does not erase what they recorded: their name stays in the log and on every entry of theirs.",
  'sb.sin_enlace': "You signed in, but Books does not recognise the account yet. Your administrator has to add it with this same address.",
  'sb.sin_membresia': "You do not have access to that company. Pick another one, or ask your administrator to add you.",
  'sb.usuario_retirado': "Your user is deactivated. Your administrator can turn it back on.",
  'sb.sin_token': "Your session expired. Please sign in again.",
  'sb.sin_empresa': "No company is selected. Go back to the picker and choose which one to work in.",
  'sb.faltan_datos': "The screen sent an incomplete request. That is a defect on our side, not something you did wrong: try again, and if it keeps happening tell us which screen you were on and what you were doing.",
  'sb.accion_desconocida': "The screen asked for an operation the server does not recognise. That is usually an old version cached in the browser: reload the page. If it keeps happening, it is a defect on our side.",
  'sb.fecha_invalida': "That date is not in the expected form: year, month and day, in that order and with dashes — yyyy-mm-dd.",
  'sb.SB113': "You must say which role that person gets.",
  'sb.SB115': "That address is not in Books yet.",
  'sb.SB116': "That is the address used to demo the product: it does not administer.",
  'sb.SB130': "You must be a company administrator to manage its people.",
  'sb.SB227': "You must be a company administrator to open a fiscal year.",
  'sb.SB228': "You must be a company administrator to close a fiscal year.",
  'sb.SB229': "You must be a company administrator to declare there are no prior balances.",
  'sb.SB131': "That role does not exist. There are three: administrator, accountant and read-only.",
  'sb.SB132': "The company would be left with no administrator, and then nobody could invite again. Name another one before removing or demoting this one.",
  'sb.SB133': "That person exists in Books, but is not a member of this company.",
  'sb.SB120': "The mapping must come as a list of correspondences.",
  'sb.SB121': "That way of treating profit-and-loss accounts does not exist. There are two: clean start, or bring the current year.",
  'sb.SB160': "That account takes no entries: it is either a parent account, deactivated, or missing from the chart.",
  'sb.SB161': "A statement row does not say whether money came in or went out. A row with both columns filled, or both empty, states no movement at all.",
  'sb.SB162': "That ledger entry line does not exist, or does not belong to the account being reconciled.",
  'sb.SB163': "The bank amount and the ledger amount are not the same. A deposit the bank charged a fee on is TWO facts: they are matched separately and the fee is recorded.",
  'sb.SB164': "That was already reconciled. Refresh the screen to see against what.",
  'sb.SB165': "The bank row and the ledger line go in opposite directions. What the bank calls a credit is money coming in, and in the ledger that is a debit.",
  'sb.SB166': "That statement row no longer exists.",
  'sb.SB167': "The statement must come as a list of rows.",
  'sb.SB168': "The day tolerance is out of range. It goes from 0 to 60.",
  'sb.SB169': "Aging is either of what you are owed or of what you owe. There is no third side.",
  'sb.SB170': "You must be a company administrator to change its settings.",
  'sb.SB171': "The currency of the books cannot be changed: there are entries already written. The amounts are stored *in* that currency without carrying it, so changing it would not convert them — it would reinterpret them, with nothing failing.",
  'sb.SB172': "That language does not exist. There are two: Spanish and English.",
  'sb.SB173': "The legal name cannot be left empty: it heads the financial statements.",
  'sb.SB174': "The currency goes as a three-letter ISO code — CRC, USD, EUR.",
  'sb.SB175': "The tax ID cannot be left empty.",
  'sb.SB176': "That ID type does not exist. There are five: individual, legal entity, DIMEX, NITE and foreign.",
  'sb.SB200': "That financial year has not been opened yet. The periods exist — the next ten years are there — and opening the year is a separate act: an open period is a place someone can write into, and a finger off by a decade would write into one nobody looks at.",
  'sb.SB201': "That financial year does not exist in this company.",
  'sb.SB202': "That financial year was already open.",
  'sb.SB210': "That financial year cannot be closed yet: the first eleven months must be closed and the last one open.",
  'sb.SB211': "That year already has its closing entry. Making another would add the year's profit to equity twice.",
  'sb.SB212': "There is nothing to close: that year has no result.",
  'sb.SB101': "That counterparty does not exist in this company. Create it under Customers and suppliers: its code is what travels written inside each of its entries.",
  'sb.SB180': "That code is not valid. Lowercase letters, digits and hyphens, up to sixty characters.",
  'sb.SB181': "There is already a counterparty with that code.",
  'sb.SB182': "A counterparty must be a customer, a supplier, or both. Without that it appears in neither ageing report, and a record that shows up nowhere is a lost record.",
  'sb.SB183': "That does not look like an email address. Something, an at sign, something, a dot and something — with no spaces.",
  'sb.SB190': "That counterparty is not marked as a supplier. A purchase invoice against someone who is only a customer leaves a balance the payables ageing does not show, and that looks exactly like owing them nothing.",
  'sb.SB191': "That invoice is already recorded for that counterparty. Entering it twice is the same liability counted twice, and the balance sheet would keep balancing while stating double.",
  'sb.SB192': "It was not stated which account the expense goes to. Put it on the invoice, or give the counterparty a default account so it need not be repeated on every one.",
  'sb.SB230': "The chart has no payable or receivable account that takes movement in that currency. This is not a missing field: check in Chart of accounts that the account is active and is the one that takes movement — if you hung an auxiliary under it, the auxiliary is the one.",
  'sb.SB231': "The chart has no VAT account that takes movement on that side. An invoice with tax needs somewhere to put it; without one the tax would blend into the expense and never be declared.",
  'sb.SB232': "That job is not in the task catalogue, so nobody watches whether it stopped running.",
  'sb.SB233': "Recognition cannot start before the month of the invoice. You bill in advance and the service starts later, not the other way round: if the contract was already running, the part already earned is not deferred revenue.",
  'sb.SB234': "The obligation is missing a classification its accounts require — for example the mode, SaaS or on-premise, which the maintenance ratio needs so it does not blend two businesses. It is asked for now and not at month-end, which is where it used to surface.",
  'sb.SB235': "That obligation cannot be ended this way: either it does not exist, or it is already ended, or the date is before recognition started. To change a contract you open another obligation; the end date is not rewritten.",
  'sb.SB236': "The two accounts of an obligation are not interchangeable: money billed in advance goes to a contract LIABILITY account, and what is earned each month to an INCOME one. The other way round gives an entry that balances and recognises income against income.",
  'sb.SB237': "This company has not declared its income tax rate, and without it there is nothing to provide for. It is set in Settings: the product ships without one because it depends on each company’s gross income bracket, and an invented rate produces a number that looks official.",
  'sb.SB238': "The impairment matrix has a band that does not exist or a percentage outside 0 to 100. The bands are the ones from the receivables ageing — current, 1 to 30, 31 to 60, 61 to 90, over 90 and no due date.",
  'sb.SB239': "The chart of accounts is missing one of the accounts the provision needs —the allowance, the impairment expense, the income tax or the tax payable. Check in Chart of accounts that they are active and take movement.",
  'sb.SB240': "That project does not exist in this company, or it is already closed. A closed project takes no new declarations: if the recognised revenue needs correcting, it is corrected with an entry — reopening the estimate of something declared finished is a different decision.",
  'sb.SB241': "Progress goes from 0 to 100. It is the percentage of the contract performed, and the revenue recognised is that times the price.",
  'sb.SB242': "The two accounts of a project are not interchangeable: work done and not yet billed goes to a contract ASSET account, and the period’s revenue to an INCOME one. The other way round gives an entry that balances and recognises income against income.",
  'sb.SB243': "That backlog category does not exist. They are the five from Module 5 — full throttle, slow burn, stalled, go-get and disengaged — and they come from the model, not from a list written into the screen.",
  'sb.SB244': "That ratio does not exist, or the target is not a number. Leaving it empty is allowed: it means somebody looked and decided not to set it yet, which is not the same as never having looked.",
  'sb.SB245': "That person is not on the list, or their details do not work. Add them before loading their hours: creating them on the fly would turn a typo in the file into a new person, and nobody would look at the list again.",
  'sb.SB246': "One row of hours was not well formed, and the message says which one and what is missing. None of them was loaded: half a load looks just like a whole one.",
  'sb.SB247': "That hours budget cannot be used to suggest anything. Leave it empty if there is no budget — then no progress is suggested: empty and zero are not the same, and you cannot divide by zero hours.",
  'sb.SB248': "Those working hours cannot measure anything. They are the hours of ONE full-time person in the month — from 1 to 400: team availability is that times the sum of the FTEs.",
  'sb.SB249': "That temporary difference cannot compute anything. It needs the concept, both bases — the carrying amount and the tax base; a missing base cannot be subtracted — and whether the item is an asset or a liability: the sign depends on it. On an asset a higher carrying amount produces a deferred LIABILITY; on a liability, an ASSET.",
  'sb.SB254': "That account is not a cash account, so it is not a bank account. A bank account is tied to a cash account from the chart — if the account is new, check that it accepts entries and hangs from cash.",
  'sb.SB255': "A transfer goes between two DIFFERENT cash accounts. If the money went to an expense or to a supplier it is a payment, not a transfer, and calling it one hides what it is.",
  'sb.SB256': "Between two accounts in the same currency, what leaves and what arrives are the same. If the bank charged a fee, it goes as a separate expense entry: it is not an FX difference, and putting it there would hide it in a P&L account where it does not belong.",
  'sb.SB062': "That entry does not exist in these books. If you copied the number, double-check it.",
  'sb.SB065': "That entry was generated by another module, not from the journal: that's why it can't be corrected here. The `hint` tells you from where: if it came from the bank, fix it from Banking; from an applied payment, from the document's ageing; from closing, re-run the close with the right numbers.",
  'sb.SB066': "The entry's period is closed — or the period you wanted to move it to. An entry in a closed month doesn't move: fix it with another entry (a new one that offsets it) so the closed month's balance stays as it was.",
  'sb.SB067': "That entry already has that date. Nothing to correct — writing the same value would generate an audit row with no real reason.",
  'sb.SB257': "A bank account needs a bank and a number. They are there so someone recognises it on a statement — without them the row says nothing the accounting account did not already say.",
  'sb.SB253': "That project is recognised from the amount Backlog reports, not from a percentage declared here — or the other way round. Both set the same target, so having them together means two writers on the same balance and the last one wins, with nothing going out of balance. If the project changed hands, the old form's entries have to be removed before starting with the new one.",
  'sb.SB252': "That account does not carry exactly one counterparty, so it cannot be given an owner. This field is for paying against a payable or a receivable — the one belonging to whoever paid on the company's behalf. If the money left a bank, there is no owner to name: a bank has no creditor.",
  'sb.SB251': "That account is a project's contract account, and its balance is released by PROGRESS: it is recognised by the percentage someone declares, not by a calendar. With both on top of it, revenue would be recognised twice and the balance sheet would still tie. The monthly fee for that same project IS an obligation — it belongs in the maintenance account, which the chart keeps separate.",
  'sb.SB250': "The chart of accounts is missing the three deferred tax accounts: the asset, the liability and the expense. The model ships them with “IAS 12” written beside them — check in Chart of accounts that they are active and accept entries.",
  'sb.SB258': "The treatment of the difference must be “FX difference” or “customer credit”. It is the decision of what to do with the part of the deposit that does not match the invoice, and the system does not guess it.",
  'sb.SB259': "This path is for collecting a foreign-currency invoice with a colón deposit. Either the bank account you chose is not in colones, or the document is already in the books' currency — in which case it is an ordinary collection.",
  'sb.SB260': "That invoice already has applied payments, and this path cancels invoices in full. Apply the rest with an ordinary payment, or remove the earlier payment first.",
  'sb.SB261': "Less was received than the invoices add up to, so there is no customer credit to leave. If you settle it anyway, the difference is an FX difference (a loss); otherwise apply a partial payment with an ordinary payment.",
  'sb.SB193': "The invoice amounts do not work. Subtotal plus tax must add up to something owed, and neither goes negative: a return is a credit note, not an invoice in reverse.",
  'sb.SB194': "That counterparty is deactivated. Reactivate it under Customers and suppliers if you still buy from them — deactivating does not erase its history, it stops new business.",
  'sb.SB195': "That counterparty is not marked as a customer. A sales invoice against someone who is only a supplier leaves a balance the receivables ageing does not show, and that looks exactly like having nothing to collect from them.",
  'sb.SB196': "Between this note and the earlier ones, more would be credited than the invoice states. It is measured against the invoice TOTAL and not against what is still open: a note on something already paid is legitimate and leaves a credit balance, but crediting more than was invoiced is not.",
  'sb.SB197': "That document does not exist, or is not an invoice. A credit note goes against an invoice; against another note it means nothing.",
  'sb.SB001': "That company code is not valid. It names a schema and a role in the database, so it goes in lowercase, starts with a letter and carries only letters, digits and underscores.",
  'sb.SB002': "That company does not exist.",
  'sb.SB052': "The exchange rates did not arrive as a list.",
  'sb.SB053': "That line's currency is not the account's. An account declared in one currency that takes amounts in another ends up with a balance in neither: revaluation would adjust something that does not exist, and reconciliation would compare against a statement that is in a single currency.",
  'sb.SB156': "A run with no rows must say which currency it is for. With no rows the currency comes from nowhere, and filing it under the wrong one would be worse than not filing it.",
  'sb.SB111': "There is no operation open.",
  'sb.SB112': "It was not stated whether the company is a demo and whether its tax ID is provisional. Neither is assumed: a real company treated as a demo, or the other way round, looks the same until it matters.",
  'sb.SB114': "Turning on the operator permission requires stating what authorizes it. A permission with no reason written down is one nobody can review later.",
  'sb.SB140': "This company has no issuer. It is created first, and it is created in the tax authority's portal: it cannot be done programmatically.",
  'sb.SB141': "That issuer cannot take a sequence number yet: either it has no credentials set, or its numbering is assigned by another system and here we only record the number that system gave.",
  'sb.SB142': "The issuer has no numbering range for that document type. One is opened when the type is set up.",
  'sb.SB143': "That document does not exist in this company.",
  'sb.SB144': "That document is already in a final state. A rejection is not edited: it is corrected by issuing another one.",
  'sb.SB145': "That document type does not exist.",
  'sb.SB146': "That document status does not exist.",
  'sb.SB147': "That entry does not exist in this company's books.",
  'sb.SB148': "That company does not exist.",
  'sb.SB149': "That company's tax ID is declared as provisional. A numbering range is tied to a tax ID, and opening one against a placeholder would leave documents issued under a number that is not theirs.",
  'sb.SB155': "It is not stated who keeps the numbering. There are three answers: the tax authority's system, this product, or off.",
  'sb.SB151': "This company already has opening balances loaded.",
  'sb.SB152': "It is already declared that this company has no prior balances. Changing when the books start for a company that is already trading would move the ground under everything recorded.",
  'sb.SB153': "Depreciation cannot start before the purchase.",
  'sb.SB154': "That asset's expense account requires a classification the asset does not carry. Without it, the depreciation would land on a line the reports by area do not know where to put.",
  'sb.SB220': "That line cannot be applied here. A credit on a receivable or payable account is applied AGAINST a document on the same account: not the bank, and not another line on the same side.",
  'sb.SB221': "That document has less than that left to apply. Applying more would leave it negative in the ageing while the balance sheet stays the same — the report and the books saying different things with nothing failing.",
  'sb.SB222': "That payment has less than that left to allocate. A payment cannot cancel more than it paid.",
  'sb.SB223': "The document and the payment belong to different counterparties. Paying one party's invoice with another's would throw both records off in opposite directions while the grand total still balances — which is what would make it invisible.",
  'sb.SB224': "The document and the payment are on different accounts. Cancelling a debt means bringing that account's own balance down; across accounts the ageing would say zero while the balance sheet still says it is owed. Each currency has its own account.",
  'sb.SB225': "That allocation is not well formed. Each one says which document it goes to and how much, the amount travels as text, and undoing one requires stating why.",
  'sb.SB226': "That document has no receivable or payable line to apply against. Either it does not exist, or its journal entry touches no account in that ageing report.",
  'sb.SB110': "Your user is not an operator.",
  // ── E8.4 · Onboarding as the CLIENT sees it ──────────────────────────────
  // ▶ The same steps, said as a TASK and not as an achievement. See the Spanish.
  'pmc.step.identificacion_real': "Put in the real tax ID",
  'pmc.step.primer_acceso': "Give someone else access",
  'pmc.step.entro_alguien': "Get the invited person to sign in",
  'pmc.step.apertura_cargada': "Load the opening balances",
  'pmc.title': "To finish setting up",
  'pmc.sub': "This disappears on its own once nothing is left. It does not have to be today: the company already works.",
  'pmc.ours': "We take care of this:",
  'pmc.oursBody': "there is nothing for you to do.",
  'pmc.goUsers': "Go to users",
  'pmc.goOpening': "Load the balances",
  'pmc.why.primer_acceso': "Nobody else gets into these books yet. If you will be working with others, add your people.",
  'pmc.why.entro_alguien': "There are unused invitations: someone is a member and has never signed in. The email may have been lost.",
  'pmc.why.apertura_cargada': "The balances the company arrives with from its previous system are missing. Without them, reports start at zero and will not match your real accounting.",
  'pm.title': "Getting started",
  'pm.empresa_creada': "The company is created",
  'pm.identificacion_real': "The tax ID is the real one",
  'pm.primer_acceso': "Someone has access",
  'pm.entro_alguien': "Someone has signed in",
  'pm.apertura_cargada': "Opening balances are loaded",
  'pm.byOperator': "on us",
  'pm.byAccountant': "on the client",
  'pm.ready': "Ready to work",
  'pm.pending': "Something is missing",
  'op.backToCompanies': "Back to my companies",
  'op.noBooksNote': "This console shows no balances, entries or ratios: creating a client does not grant access to its books.",
  'op.console': "Operator console",
};
